import {
  AgentStatus,
  CallStatus,
  CaseStatus,
  HotelCategories,
  SALE_ORDER_STATUS,
  WhatsappMessageEventStatusType,
} from '@bandido/shared/constants';
import {
  InvalidationRuleFieldOptionHotels,
  InvalidationRuleOperator,
  IVRActionType,
  MarkupFieldOptionHotels,
  MarkupRuleOperator,
  NotificationType,
  UserRoles,
  OriginatedByChannel,
} from '@bandido/shared/types';
import { CommunicationType, TimeoutAction } from '@bandido/shared/types';
import { WELCOME_MESSAGE } from '@frontend/features/calls-menu/constants';

export const english = {
  inbox: {
    title: 'Inbox',
    loading: 'Loading Inbox',
    list: {
      subtitle: 'Your work, ordered by activity',
      ariaLabel: 'Inbox cases',
      emptyTitle: 'The list will appear here',
      emptyDescription: 'Cases, filters and priorities are added in the next stage.',
    },
    conversation: {
      title: 'Conversation',
      subtitle: 'Case activity',
      caseTitle: 'Case #{{caseId}}',
      emptyTitle: 'Choose a case',
      emptyDescription: 'Select a case from the list to read its activity.',
      loading: 'Loading activity…',
      errorTitle: 'We could not load the activity',
      errorDescription: 'Try again without leaving the selected case.',
      noActivityTitle: 'This case has no activity yet',
      noActivityDescription: 'Case conversations and actions will appear here.',
      loadPrevious: 'Load previous activity',
      newActivity: 'New activity',
      readError: 'We could not save the read state.',
      attachment: 'Attachment or activity without text',
      audioWithoutTranscription: 'Audio without transcription',
    },
    composer: {
      email: 'Email',
      whatsapp: 'WhatsApp',
      channelLabel: 'Reply channel',
      discardEmail: 'Discard email',
      label: 'WhatsApp message',
      placeholder: 'Write a message…',
      send: 'Send message',
      error: 'We could not confirm the send. The message was kept while its state is reconciled.',
      unknown: 'The send outcome is still uncertain. The message was kept and will not be resent automatically.',
      composeAnother: 'Write another',
      windowExpired: 'The WhatsApp service window is closed. Use an available template to continue.',
      chooseTemplate: 'Choose template',
    },
    historyChannels: {
      WHATSAPP: 'WhatsApp', EMAIL: 'Email', PHONE: 'Call', NOTES: 'Note', CASE_LOG: 'Operation',
    },
    context: {
      title: 'Context for a better reply',
      emptyTitle: 'Case context',
      emptyDescription: 'The information you need will appear here without leaving the conversation.',
      errorTitle: 'We could not load the context',
      errorDescription: 'Try again or open the full case page.',
      sections: { case: 'Case', customer: 'Customer and contact', bookings: 'Bookings', related: 'Related cases' },
      fields: {
        name: 'Name', status: 'Status', agency: 'Agency', workflow: 'Workflow', step: 'Stage', advisor: 'Advisor',
        client: 'Customer', organization: 'Organization', email: 'Email', whatsapp: 'WhatsApp', phone: 'Phone',
      },
      openCase: 'Open full case',
      openClient: 'Open customer',
      openBookings: 'View all bookings ({{count}})',
      bookingsError: 'We could not load the bookings.',
      noBookings: 'There are no bookings available for this case.',
      noRelated: 'There are no related cases available.',
    },
    management: {
      title: 'Case management',
      optionsError: 'We could not load the management options.',
      updateError: 'We could not update the case. Check the option and try again.',
      noTransitions: 'There are no available transitions from this stage.',
      customFields: 'Workflow fields',
      noCustomFields: 'There are no workflow fields for this case.',
      customFieldsLoadError: 'We could not load the workflow fields.',
      customFieldsUpdateError: 'We could not save the field. Its authoritative value will be restored.',
    },
    actions: {
      backToList: 'Back to Inbox',
      openContext: 'Open case context',
      closeContext: 'Close case context',
    },
    search: {
      label: 'Search cases',
      placeholder: 'Case, customer or organization',
    },
    filters: {
      title: 'Filter Inbox',
      all: 'All',
      assignment: 'Assignment',
      agency: 'Agencies',
      workflow: 'Workflows',
      step: 'Stages',
      channel: 'Channel',
      unreadOnly: 'Unread cases only',
      clear: 'Clear',
      apply: 'Apply',
      assignments: {
        mine: 'Mine',
        all: 'All',
        team: 'My team',
        unassigned: 'Unassigned',
      },
    },
    savedViews: {
      selector: 'View',
      default: 'My cases',
      custom: 'Custom view',
      manage: 'Manage saved views',
      title: 'Saved views',
      description:
        'Save combinations of assignment, agencies, workflows, stages, channel and unread state. Search stays separate so you can search within any view.',
      name: 'View name',
      save: 'Save view',
      update: 'Replace with current view',
      delete: 'Delete view',
      duplicate: 'A view with that name already exists.',
      limit: 'You can save up to 20 views. Delete one to create another.',
      error: 'We could not save the views. Try again.',
      deleteTitle: 'Delete saved view',
      deleteDescription: 'Do you want to delete the “{{name}}” view?',
    },
    preferences: {
      title: 'Automatic activity',
      description: 'Choose whether automatic status changes appear in the conversation and create alerts.',
      showAutomaticStatusChanges: 'Show automatic status changes',
      notifyAutomaticStatusChanges: 'Notify automatic status changes',
      error: 'We could not save the settings. Try again.',
    },
    channels: {
      whatsapp: 'WhatsApp', email: 'Email', call: 'Call', note: 'Note', operation: 'Operation', unknown: 'Activity',
    },
    states: {
      loading: 'Loading cases…',
      errorTitle: 'We could not load the Inbox',
      errorDescription: 'Try again to recover the list without losing your filters.',
      emptyTitle: 'Your Inbox is up to date',
      emptyDescription: 'Cases with activity will appear here.',
      filteredEmptyTitle: 'No cases found',
      filteredEmptyDescription: 'Try changing your search or filters.',
      offline: 'Offline. Showing the latest available information.',
      reconnecting: 'Reconnecting updates…',
    },
  },
  hotelCostMarkup: {
    close: "Close",
    title: "Cost markups",
    description: "One percentage applies to every connection and fare in each search. Cost = reported amount / (1 − percentage / 100). Changes affect new searches.",
    default: "Default percentage (%)",
    salesAgency: "Sales agency",
    percent: "Percentage (%)",
    remove: "Remove override",
    add: "Add agency override",
    save: "Save",
    reload: "Discard and reload",
    error: "The operation could not be completed. Check permissions and values; reload before saving if settings changed.",
    saved: "Changes saved.",
    admins: "Super administrators",
    adminHint: "Can configure these costs and grant or revoke this role. The last super administrator cannot be removed.",
    revoke: "Revoke role",
    email: "Existing user email",
    grant: "Grant Super admin",
  },
  bookingSettings: { title: 'Booking settings' },
  penaltyReminders: {
    title: 'Cancellation cost reminders',
    inherit: 'Inherit agency settings',
    enabled: 'Enabled',
    disabled: 'Disabled',
    days: 'Days in advance',
    timezone: 'Time zone',
    hint: 'Adds a case note at 09:00 in this time zone and marks the case as needing an agent. Only for bookings not reconfirmed. Same-day deadlines before 09:00 are notified from midnight.',
    save: 'Save',
    close: 'Close',
    error: 'Could not complete the operation. Reload and try again.',
    reconfirmed: 'Reconfirmed',
    notReconfirmed: 'Not reconfirmed',
    reconfirmation: 'Reconfirmation',
    all: 'All',
    today: 'Today',
    nextWeek: 'Next week',
    datesUtc: 'Dates in UTC',
    noDeadline: 'No cancellation deadline available',
    inactiveBooking: 'Available once the booking is confirmed',
    loading: 'Checking reconfirmation…',
    reconfirmationHint: 'We have reviewed this booking. Do not notify for this cancellation deadline.',
    scope: 'Configuration',
    agency: 'Agency default',
    connection: 'This connection',
    savedAt: 'Reconfirmed on {{date}}',
  },
  hotelOnboarding: {
"reviewSources":"Review imported hotels","sourceNotice":"Review the source identity before linking or creating a hotel. Agency publication requires a separate action.","sourceSearch":"Find imported hotel","createExplicit":"I checked that no existing canonical hotel should be linked and want to create one from this source.","createFromSource":"Create draft hotel","linkSource":"Link selected hotel","linkError":"Link was not confirmed. Reload the source; existing links are never reassigned.","canonicalHotel":"Canonical hotel","agencyStatus":"Agency catalog status","projectionPending":"Projection pending","activeLinks":"Active supplier links","noAvailabilityClaim":"An active projection and link do not guarantee availability or price. Add global selection separately and verify through a search.","activateAgency":"Activate hotel for this agency","activationPending":"The projection may be pending or the operation was not confirmed. Check status before continuing.",
  "imports": "Import supplier content",
  "importNotice": "Import contacts the provider through this account. It does not publish global content or activate agency hotels.",
  "account": "Owned account",
  "access": "Configured access",
  "feed": "Content",
  "supplier_hotel_list": "Supplier hotels",
  "hotel_master": "FastX master",
  "destination_list": "Supplier destinations",
  "board_list": "Boards",
  "category_list": "Categories",
  "countries": "ISO countries (comma separated)",
  "destinationCodes": "Supplier destination codes (comma or line separated)",
  "hotelCodes": "Supplier hotel codes (comma or line separated)",
  "scope": "Import scope",
  "confirmScope": "I reviewed the account, access and scope and authorize this import.",
  "enqueue": "Request import",
  "enqueueError": "Import was not confirmed. Check existing jobs before submitting again.",
  "pages": "Processed pages",
  "attempts": "Attempts",
  "checkpoint": "Saved progress exists",
  "refreshStatus": "Check status",
  "existingJobs": "Existing jobs",
  "resumeNotice": "The worker preserves progress. An identical request returns the same job, including completed or failed jobs; it does not restart it. Automatic tracking stops after ten minutes.",
  "loadError": "Could not load information.",
  "addDestination": "Add destination",
  "addHotel": "Add hotel to destination",
  "geoCandidate": "Find hotelrs canonical place",
  "hotelCandidate": "Find canonical hotel",
  "find": "Find candidates",
  "selectionNotice": "This global selection does not guarantee availability. Hotels also require a supplier link and active agency publication. Existing records are never overwritten.",
  "destinationOnly": "Select a canonical destination, not an address or coordinate."
  },

  hotelEditorial: {
  "title": "Hotel content",
  "loading": "Loading…",
  "unavailable": "Editorial administration is unavailable for your account.",
  "noPublication": "This hotel has no publication available to edit.",
  "reload": "Reload",
  "version": "Version {{version}}",
  "global": "This publication is shared by all agencies. Changes are published only after reviewing the preview.",
  "withdrawn": "The publication is withdrawn. This editor does not republish it.",
  "edit": "Edit content",
  "name": "Name",
  "restoreName": "Use source name",
  "descriptions": "Descriptions",
  "customizeText": "Customize text while keeping every language",
  "language": "Language",
  "kind": "Text kind",
  "text": "Text",
  "remove": "Remove",
  "addText": "Add text",
  "restoreText": "Restore all source descriptions",
  "gallery": "Gallery (the first image is the main image)",
  "image": "Image {{index}}",
  "up": "Previous",
  "down": "Next",
  "upload": "Upload own image",
  "uploadUnavailable": "Image uploads require public storage configured by the server administrator. You can reorder or remove existing images.",
  "uploadError": "Could not upload image. Use a valid PNG or JPEG up to 3 MB.",
  "restoreImages": "Restore source gallery",
  "previewLanguage": "Preview language",
  "reason": "Reason for change",
  "conflict": "The publication changed. Reload and review its current version before publishing.",
  "saveError": "Could not complete the operation. Check the fields and preview again.",
  "preview": "Server preview",
  "publish": "Publish for all agencies",
  "cancel": "Discard changes",
  "history": "Editorial history",
  "historyError": "Could not load history.",
  "previous": "Previous",
  "next": "Next"
},
  hotelSettings: {
    "title": "Hotels",
    "description": "Providers, search rules and connections for this agency.",
    "search": "Where we search",
    "connections": "Connections and access",
    "catalog": "Hotel catalog",
    "connectionActive": "Active connection",
    "accesses": "View accesses ({{count}})",
    "dynamicAccess": "This connection uses a dynamic access selection."
},
  hotelAccessNames: {
    manage: 'Access display names',
    hint: 'Optional labels for this connection, shared with agencies using it. Existing reservations keep their saved name.',
    optional: 'Display name (optional)',
    saveError: 'Could not save. Reload before editing again; the names may have changed.',
  },
  hotelPolicy: {
    excluded: "Excludes",
    added: "Adds",
    networkError: "no response",
    errorStatus: "Diagnostic reference: HTTP {{status}}",
    retry: "Retry",
    action_off: "Not included",
    action_exclude: "Exclude",
    action_add: "Explicitly include",
    action_keep: "Keep inherited",
    providerAction: "Action",
    inheritedProvider: "Inherited from general rules",
    accessIdentity: 'Access {{code}}',
    findProvider: 'Search name, connection or access code',
    repair: "Repair inactive accesses",
    inactiveRules: "Rules reference inactive accesses. Review and remove all their references in one save.",
    repairHelp: "The listed accesses will be removed from selections and exclusions in these rules. Modes and active accesses are preserved. An empty exclusive list searches no providers. If an excluded access is reactivated later, you will need to exclude it again.",
    repairInventoryChanged: "The inventory changed or could not be verified. Close and reopen recovery to review the current accesses.",

    hierarchy: "General providers apply to all destinations. Country and destination rules adjust that selection.",
    exceptions: "Changes by country or destination",
    addRule: "Add rule",
    findRule: "Find country or destination",
    scopeType: "Applies to",
    all: "All",
    country: "Country",
    destination: "Destination",
    allDestinations: "All its destinations",
    noExceptions: "No rules match this selection. Inherited providers apply.",
    test: "Check a destination",
    removeRule: "Remove exception",
    removeHelp: "This location will inherit general rules again. Review the resulting providers before saving.",
    chooseDestination: "Choose a catalog destination. For a point or address, configure its country rule.",
    duplicate: "A rule already exists for this location. Edit that rule.",
    behavior: "Search behavior",
    addProviders: "Add providers",
    excludeProviders: "Exclude inherited providers",
    selectProviders: "Providers to search",
    inheritedFrom: "Inherited providers",
    previewError: "We could not validate the providers. Your selection is preserved and no changes were saved. Retry validation.",
    extend: "Adjust inherited providers",
    inherit: "Use inherited providers unchanged",
    pointDefault: "Points and addresses apply their verified country rule, without destination exceptions.",
    emptyAdditions: "No additions: inherited providers remain except for exclusions.",

    additional: "Additional",
    inherited: "Default",
    noneEffective: "No providers will be searched.",
    effective: "These providers will be searched",
    unsupportedAccounts: 'Some active connections use unrestricted access. Configure explicit accesses in Hotel connections before using All enabled: ',
  "title": "Search providers",
  "scope": "These rules choose which accounts and accesses this agency searches. They do not change shared content or enable bookings.",
  "default": "For all destinations",
  "loading": "Loading…",
  "loadError": "Could not load rules or you lack administration permission.",
  "reload": "Reload",
  "overrides": "Destination overrides: {{count}}",
  "edit": "Edit",
  "all_enabled": "All enabled accesses",
  "allowlist": "Use a custom selection",
  "disabled": "Do not search here",
  "includesFuture": "This option will include newly enabled accesses added in the future.",
  "routesError": "Could not verify available accesses.",
  "unavailableRoute": "Selected access unavailable:",
  "noRoutes": "No configured active accesses to select.",
  "emptyAllowlist": "No selected accesses: this rule will execute no searches.",
  "destinationRule": "Destination rule",
  "addDestination": "Add a destination override",
  "add": "Add",
  "remove": "Remove",
  "reason": "Reason for change",
  "conflict": "Rules changed. Reload and review the current version before saving.",
  "saveError": "Could not save rules.",
  "close": "Close",
  "save": "Save"
  },
  hotelDestinationMembership: {
    reference: 'Reference: {{id}}',
    identityHelp: 'Records can share the same name. The reference identifies the record you are adding; review it in the content tools if you are unsure.',
    hotelName: 'Hotel name',
    search: 'Search',
    add: "Add hotel",
    included: "Included",
    disabled: "Deactivated",
    disabledCount: "{{count}} deactivated hotels",
    disabledRange: "Deactivated hotels {{start}}–{{end}} · catalog page",
    deactivate: "Deactivate",
    reactivate: "Reactivate",
    deactivateHotel: "Deactivate {{hotel}} in this destination",
    reactivateHotel: "Reactivate {{hotel}} in this destination",
    choose: "Choose hotel",
    catalogHelp: "Search by name for a hotel already in the catalog. If it is missing, adding it to the catalog is managed through the content tools.",
    alreadyIncluded: "{{hotel}} is already included in {{destination}}. There is no need to add it again.",
    existingDisabled: "{{hotel}} is deactivated in {{destination}}. You can reactivate it with its existing priority.",
    newRelationship: "{{hotel}} is not yet included in {{destination}}. Check its location before continuing.",
    deactivateHelp: "It will no longer be included in new searches for {{destination}}. It keeps its priority and remains in the catalog and other destinations. Existing bookings do not change.",
    includeHelp: "It will be included in {{destination}} for all agencies. Inclusion does not guarantee availability or enable providers.",
    saved: "Change saved. It applies to new destination searches.",
    emptyDisabled: "This destination has no deactivated hotels.",
  },
  hotelDestinationOrder: {
    previousMatches: 'Previous destinations',
    nextMatches: 'More destinations',
    edit: 'Edit',
    hotel: 'Hotel',
    title: "Hotels by destination",
    subtitle: "Manage which hotels are included in each destination and their search order.",
    destination: "Destination",
    typeDestination: "Enter at least 2 characters to find a destination.",
    chooseDestination: "Select a destination to see its included hotels and edit their priorities.",
    priority: "Priority",
    priorityHelp: "Lower numbers appear first. Ties keep a stable order by hotel identifier.",
    editPriority: "Edit priority",
    editHotel: "Edit priority for {{hotel}}",
    currentOrder: "Current order",
    previewTitle: "Proposed order",
    preview: "Preview",
    position: "Position",
    pagePosition: "On this page",
    count: "{{count}} included hotels",
    range: "Included hotels {{start}}–{{end}} · catalog page",
    previewPosition: "Position {{position}} of {{count}}",
    previewPagePosition: "Position {{position}} among the {{count}} hotels on this page",
    partialPreview: "This preview compares only hotels on this page. The final position may be on another page; saving returns to the first page to load the updated order.",
    moves: "From position {{before}} to {{after}} within this view.",
    unchanged: "No changes to the order.",
    reasonHelp: "The reason is recorded with the change.",
    discard: "Discard",
    save: "Save priority",
    saved: "Priority saved. It applies to new destination searches.",
    saveUncertain: "We could not confirm the save. Reload to check the current status and priority before editing again.",
    empty: "This destination has no included hotels. Use Add hotel to include an existing catalog hotel.",
    searchHelp: "New destination searches request up to 200 eligible hotels in this order. Only available hotels appear in results. Sorting by price overrides priority; location searches keep distance order. Existing searches retain their original selection.",
    pageLimit: "You have reached the catalog navigation limit. Additional relationships cannot be queried from this screen.",
    discardTitle: "Discard unsaved changes?",
    discardBody: "The priority and reason you are editing will not be saved.",
    keepEditing: "Keep editing",
    backToAgency: "Back to agency Hotels",
    otherTools: "Other tools: destinations, content and providers",
    agencyDescription: "Manage hotel inclusion and order by destination. The catalog is shared: changes affect all agencies.",
  },
  hotelCatalogAdmin: {
    cancel: 'Cancel',
    "title": "Hotel catalog",
    "globalNotice": "Changes affect the shared catalog for all agencies. They do not change booking permissions or import hotels.",
    "unavailable": "Catalog administration is unavailable for this account.",
    "destinations": "Destinations",
    "hotels": "Destination hotels",
    "destinationFilter": "Canonical destination ID (optional)",
    "filter": "Filter",
    "loading": "Loading…",
    "loadError": "Could not load the catalog.",
    "reload": "Reload",
    "externalPlace": "External destination",
    "enabled": "Enabled",
    "rank": "Order",
    "yes": "Yes",
    "no": "No",
    "edit": "Edit selection",
    "empty": "No relationships match these filters.",
    "previous": "Previous",
    "next": "Next",
    "reason": "Reason for change",
    "conflict": "Another administrator changed this selection. Reload and review the values before saving again.",
    "saveError": "Could not save.",
    "close": "Close",
    "save": "Save"
},
  hotelReservation: {
    resumeQuote: "Resume",
    discardQuote: "Discard quotation",
    discardError: "Could not discard. Check the status: only quotations never submitted for booking can be discarded.",
    searchAgain: "Search again",
    restartNotice: "Available quotation details have been restored. Check location, dates, rooms and nationality before searching. Prices and conditions will be requested again.",
    restartUnavailable: "An expired, unsent quotation could not be recovered. You can start a new search.",
    unsentQuoteHelp: "No booking has been submitted. Discarded quotations remain in Closed.",
    quoteStatus: { expired: 'Quotation expired', discarded: 'Discarded' },
    reconciliationCheck: 'Check status',
    reconciliationHolderMissing: "Holder not returned by the supplier",
    reconciliationMissingDetails: "The supplier did not return these details; they could not be verified:",
    reconciliationMissing_holder: "holder",
    reconciliationMissing_occupancies: "occupancy",
    reconciliationAcceptMissing: "I verified this reservation with the supplier and accept reconciliation without returned holder or occupancy details. References, connection, hotel and dates match.",
    reconciliationOutcome_identity_mismatch: "The supplier returned details that contradict the original booking identity. No changes were applied.",
    reconciliationOutcome_provider_error: "The supplier returned an error. The reservation state was preserved; you can check again.",
    reconciliationOutcome_inconclusive_result: "The response does not allow verification. Check the reference and status with the supplier.",
    reconciliationOutcome_malformed_response: "The supplier returned an incomplete or invalid response.",
    reconciliationOutcome_stale_result: "The reservation changed during the query. Please query again.",
    externalCancellationTitle: "Record cancellation performed with the supplier",
    externalCancellationHelp: "Use only after cancelling the reservation with the supplier. This records cancellation in the CRM; it does not send a cancellation or calculate fees or refunds.",
    externalCancellationDate: "Cancellation date (local time)",
    externalCancellationAccept: "I confirm this reservation has already been cancelled with the supplier and I verified its reference.",
    externalCancellationSave: "Record external cancellation",
    externalCancellationError: "The record could not be completed. Wait for any running query or cancellation to finish. After a connection error, retry with the same details or reopen the reservation to recover the result.",
    reconciliationTitle: 'Reconcile supplier reservation',
    reconciliationHelp:
      'Look up an existing reservation by supplier reference. The original client reference, connection, hotel and dates must match. Holder and occupancy are checked when returned; missing details require explicit acceptance. This does not create or cancel bookings.',
    reconciliationReference: 'Supplier reference',
    reconciliationReason: 'Reason for reconciliation',
    reconciliationPreview: 'Look up and verify',
    reconciliationPending: 'Querying the supplier. You can close and reopen this reservation.',
    reconciliationError:
      'The operation could not be completed. Check the status before retrying; request a new preview if it changed or expired.',
    reconciliationPrice: 'Supplier price',
    reconciliationExpired: 'The preview expired. Query again.',
    reconciliationAccept: 'I reviewed the details and want to associate this confirmed reservation with the case.',
    reconciliationConfirm: 'Confirm reconciliation',

    uncertainLabel: 'Uncertain outcome',
    refreshPending: 'Checking status with the provider…',
    refreshWaiting: 'The query is still pending. You can check its status again.',
    refreshComplete: 'Query completed. The updated status is shown in this reservation.',
    refreshManual: 'The outcome could not be determined. Manual reconciliation is required; do not book again.',
    refreshError: 'We could not check the status. You can retry the query; do not book again.',
    packageCondition: 'PACKAGE rate: the provider requires this hotel to be sold with another product, such as a flight.',
    quoteUnknown: 'The fare validation outcome could not be confirmed. No booking was sent. Check this request’s status.',
    bookingsDisabledShort: 'Bookings disabled for this connection',
    fareIncompleteShort: 'Missing provider details',
    quoteFailed: 'The fare could not be validated with the provider. No booking was sent.',
    caseTravelers: 'Case travelers',
    acceptSearch: "I accept the displayed price and conditions for these travelers. If they change, I want to review them before booking.",
    selectedPrice: "Selected price",
    reviewBeforeBooking: "Review the conditions confirmed by the provider before booking. The booking has not been sent yet.",
    fareUnavailable: "The selected fare is no longer available in this search. Search again. No booking was sent.",
    fareIncomplete: "This fare is missing the provider details required to book. No booking was sent.",
    quoteInvalid: "The fare validation request was rejected. Review the holder and case details. No booking was sent.",
    quoteForbidden: "You do not have permission to start this booking. Check agency and connection permissions. No booking was sent.",
    quoteConflict: "The fare could not be validated because the request state changed. No booking was sent.",
    quoteInvalidResponse: "Bandido could not interpret the hotel service response while validating the fare. No booking was sent.",
    quoteUnavailable: "The hotel service did not respond correctly while validating the fare. No booking was sent.",
    quoteConnectionError: "No response was received while validating the fare. No provider booking was sent from this form.",

    permissionLoading: 'Checking booking permission…',
    permissionError: 'Booking permission could not be verified. Try checking again.',
    bookingsDisabled: 'Bookings are disabled for this account. An agency administrator can enable them in Hotel connections.',

    cancelBooking: 'Cancel booking',
    cancelAccept: 'I reviewed the terms and want to cancel this booking.',
    cancelUncertain: 'We are checking your cancellation because the response took longer than expected. Do not submit it again; the status will update here.',
    cancelState: {
      requested: 'Cancellation requested. Waiting for the provider.',
      processing: 'The provider is processing the cancellation.',
      completed: 'The provider confirmed cancellation. This does not certify a refund amount.',
      failed: 'Cancellation failed. Do not consider this booking cancelled.',
      unknown: 'Cancellation outcome uncertain. Check status or request manual reconciliation.',
    },

    cancellationUpdated: 'Conditions changed since your search. Review the current conditions before confirming.',
    providerObservations: 'Supplier remarks',
    quotationTerms: 'Quotation terms',
    confirmedCancellation: 'Confirmed booking cancellation terms',
    confirmedPrice: 'Confirmed booking price',
    searchRoomCharge: 'Charges reported in Search — room {{room}} (not refreshed by the quotation)',
    nonRefundable: 'Non-refundable',
    title: 'Hotel reservation',
    quote: 'Book',
    book: 'Confirm reservation',
    close: 'Close',
    refresh: 'Check status',
    searchOnly: 'Booking unavailable',
    commercialNotice: 'Bookings can create real supplier charges. Review the current quotation and cancellation deadline before confirming.',
    case: 'Case #{{id}}',
    caseSelect: 'Case',
    reference: 'Reservation reference: {{id}}',
    holderSelect: 'Reservation holder',
    holderDefault: 'Case client or manual details',
    holderHint: 'Choose a case traveler or enter the details. The email can be edited for this reservation.',
    holder: { given_name: 'Holder first name', family_name: 'Holder last name', email: 'Holder email' },
    quoteError:
      'The quotation request could not be confirmed. Check the reservation list before starting another request.',
    bookRejected:
      'The booking was not submitted. Correct the traveler details and review and accept the quotation again.',
    boardCode: 'Meal plan code',
    processing: 'We are processing your reservation. The status will update automatically.',
    delayed: 'The provider is taking longer to respond. We are checking your reservation; do not book again.',
    onRequest: 'Reservation awaiting confirmation from the provider.',
    bookUncertain:
      'The booking response could not be received. Check the status before attempting another reservation.',
    readError:
      'Status could not be retrieved. This does not mean the reservation failed. Check again without submitting another booking.',
    pollPaused: 'Automatic checks have paused. You can check status here or return from the reservation list.',
    priceChanged: 'The price changed since availability. Review and accept the current quotation before confirming.',
    expires: 'Quotation expires: {{date}}',
    priceComponents: 'Net: {{net}} · Gross: {{gross}} · Minimum selling price: {{msp}}',
    binding: 'Supplier selling-price restriction applies',
    cancellation: 'Cancellation conditions',
    policyUnknown: 'No detailed cancellation penalties were supplied. Do not assume cancellation is free.',
    penaltyWindow: 'From {{startsAt}} to {{endsAt}}: {{value}} {{currency}} ({{type}})',
    penalty: 'From {{date}}: {{value}} {{currency}} ({{type}})',
    manageTravelers: 'Manage case travelers',
    travelersError: 'Case travelers could not be loaded.',
    travelerSlot: 'Room {{room}} · {{type}}',
    room: 'Room {{room}}',
    adult: 'Adult',
    childAge: 'Child aged {{age}} at check-in',
    travelerHint:
      'Select a different case traveler for every slot. Children need a birth date matching their quoted age at check-in.',
    accept: 'I accept this current price, cancellation conditions and surcharges for the selected travelers.',
    providerReference: 'Provider confirmation',
    phase: {
      discarded: 'Quotation discarded. No booking was sent to the provider.',
      quoting: 'Checking the current price…',
      quoted: 'Quotation ready for review',
      booking: 'Reservation is being processed. Do not submit again.',
      confirmed: 'Reservation confirmed',
      failed: 'The request failed. No confirmation is available.',
      unknown: 'The outcome is uncertain. Check status or request manual reconciliation; do not book again.',
      expired: 'The quotation expired. Start a new availability search.',
      cancelled: 'Reservation cancelled',
    },
  },
  hotelSearch: {
    countryUnknown: "We could not verify the country for provider rules. Select the place again or choose a catalog destination.",
    hotelName: 'Hotel name',
    applyName: 'Search',
    sortHotels: 'Sort hotels',
    sortSelection: 'Suggested order',
    sortCheapest: 'Lowest price (USD)',
    sortHighest: 'Highest price (USD)',
    nameScope: 'Name searches only cover the hotels included in this search.',
    sortPriceScope:
      'Compares each hotel’s lowest reported price matching the current filters, in USD and the selected basis. Hotels without a comparable price appear last. Additional charges may apply.',
    sortPriceFrom: 'From {{currency}} {{amount}} · {{basis}}',
    noSortPrice: 'No comparable price for this order',
    catalogLimit:
      'We searched the first {{count}} selected hotels. More hotels exist in the catalogue; filters and sorting apply to this selection.',

    routeConfiguration: 'An administrator must configure explicit hotel connection accesses before this search can run.',
    contextChanged: 'The selection or search rules changed or expired. Search again to resolve hotels with the current rules.',
    contextUnresolved: 'Search dispatch could not be confirmed. It was not retried automatically; verify its status before starting again.',
    interruptedSearch: 'This page stopped loading before a search ID was received. It was not retried automatically. You can return to the previous page or start a new search from the form.',

    allReceivedOffers: 'All received offers are displayed',
    enlargePhoto: "Enlarge photo",
    closePhoto: "Close photo",
    verifiedStars: "{{count}} verified stars",
    showDescription: "About this hotel",
    hideDescription: "Hide description",
    includePackages: "Include package rates",
    packagesNeedSearch: "This search did not request package rates. A new search is needed to include them.",
    searchWithPackages: "Search including packages",
    keepSearch: "Keep current search",
    cancellationState: {"free_now": "Free cancellation", "penalty_applies": "Cancellation with a charge", "non_refundable": "Non-refundable", "unknown": "Cancellation to be confirmed"},
    noDeadline: "Unspecified boundary",
    policyExpired: "The reported conditions need refreshing. Check the current policy before confirming.",
    allDetailFilters: 'All',
    providerLabel: 'Provider',
    cancellationLabel: 'Cancellation',
    roomsLabel: 'Rooms',
    roomNamesFilter: 'Room names',
    noMatchingRoomNames: 'No matching room names',
    roomLabel: 'Room {{index}}',
    priceLabel: 'Total',
    priceFromLabel: 'From',
    completeOffers: 'Complete room offers',
    rateMinimums: 'Reported minimum rates',
    loadedOfferFilters: 'Filters apply to loaded offers. Load more to explore further alternatives.',
    noLoadedOffers: 'No loaded offers match. Change filters or load more offers.',
    policyWhen: 'Cancellation time · UTC',
    policyPenalty: 'Penalty',
    policyFrom: 'From {{time}}',
    policyBefore: 'Until before {{time}}',
    occupancyAdult: '{{count}} adult',
    occupancyAdults: '{{count}} adults',
    occupancyChildren: 'Children (ages): {{ages}}',
    freeUntil: "Free until {{time}}",
    cancellationDetails: "Cancellation conditions",
    policyFromQuote: "Current quotation conditions",
    policyFromSearch: "Search conditions; confirm the selected rate before booking",
    penaltyNights: "night(s)",
    penaltyUnknown: "Charge not specified",
    policyLimitations: "Reported policy only. Periods are not added together. Percentage and night charges are not converted to money. Interpretation ends at the start of the check-in date in UTC; this is not the hotel’s check-in time.",
    wholeStay: "Reported price for the whole stay and all rooms. Additional charges may apply.",
    netPrice: "Net rate",
    additionalCharges: "Check additional charges in the offer",
    moreRatesAvailable: "More rate conditions available in the hotel details",
    loadingOffers: "Updating available offers…",
    restrictions: "Rate restrictions",
    policyChanged: "The provider updated the cancellation conditions. Compare the search conditions with the current ones before confirming.",

    summaryFallback: 'This search preserves the full received room combinations; upstream summary reduction was not applied.',
    summaryLimited: 'Limited summary of received offers. Open a hotel to request more options.',
    executionProgress: '{{done}} of {{total}} provider routes finished',
    expansion_failed: 'Additional search failed. Previously received offers remain available.',
    expansion_completed: 'Additional search finished. Results are not exhaustive inventory.',
    expansion_running: 'Receiving additional hotel offers…',
    expansion_accepted: 'Additional hotel search queued…',
    expansion_not_requested: 'Preparing the additional hotel search…',
    expansionError: 'The additional search could not be checked. Known offers remain available. No automatic retry was made.',
    expandedOffers: 'Additional hotel offers',
    knownOffers: 'Previously received offers',
    hotelPhoto: '{{name}} — photo {{index}}', imageUnavailable: 'Image unavailable',
    contentLoading: 'Loading published hotel information…', contentMissing: 'Published hotel information is not available.',
    contentError: 'Hotel information could not be loaded. Availability remains available.', reloadContent: 'Reload hotel information',
    locationUnknown: 'Location not provided', coordinates: 'Coordinates', categoryCode: 'Category code: {{code}}', categoryUnknown: 'Category not provided',
    locationHeading: 'Location',
    resultsView: 'Results view',
    listView: 'List',
    mapView: 'Map',
    mapResultCount: '{{count}} hotels found with these filters.',
    mapCoverage: '{{located}} of {{total}} hotels located on the map',
    mapResultsProgress: 'Loading results: {{loaded}} of {{total}} hotels',
    mapContentProgress: 'Reading locations: {{loaded}} of {{total}} hotels',
    mapPendingLocations: '{{count}} hotel locations still need to be loaded.',
    mapMissingLocations: '{{count}} hotels have no published coordinates. They remain available in the list.',
    mapIncomplete: 'Only part of the results has been loaded. Reload to see all results for this search.',
    mapNoLocations: 'No hotels with published coordinates to show on the map.',
    mapPriceHint: 'Choose a price sort to display comparable prices on the markers.',
    mapNavigationHint: 'Moving the map does not change the search or its filters.',
    mapConsultRates: 'Check rates in the hotel details',
    mapSelectHotel: 'Select a hotel on the map',
    mapCluster: '{{count}} hotels grouped',
    mapGroupHotels: 'Hotels in the selected group',
    resultsMapLabel: 'Hotel search results map',
    resultsMapUnavailable: 'The map could not be loaded. You can still select a hotel, view its rooms or open its external map.',
    viewLocation: 'View location',
    showMap: 'Show map',
    hideMap: 'Hide map',
    closeMap: 'Close',
    hotelMap: '{{name}} — location',
    exactLocationUnknown: 'Exact location unavailable',
    mapLoading: 'Loading map…',
    mapUnavailable: 'The map could not be loaded. You can open this location in Google Maps.',
    openExternalMap: 'Open in Google Maps',
    openExternalMapLabel: 'Open location in Google Maps (new tab)',
    descriptionLanguage: 'Description language: {{language}}', descriptionUnknown: 'Description not provided',
    partialFriendly: 'Some hotels could not be searched. You can compare the options received.',
    resultsNotice: 'There are notices about the available results.',
    noticeDetails: 'View search details',
    receivedPrices: 'Prices from received offers. Opening a hotel may reveal more options; prices and conditions are checked before booking.',
    compareCancellation: 'Compare cancellation',
    hideComparison: 'Hide comparison',
    visibleHotelCount: 'Showing {{from}}–{{to}} of {{total}} hotels found with these filters.',
    groupHotelCount: 'Showing {{from}}–{{to}} of {{total}} hotels in group {{group}} with these filters.',
    groupCountScope: 'The destination is searched in groups. This total covers the current group, not the entire destination.',
    resultPages: 'Result pages',
    previousResults: 'Previous',
    nextResults: 'Next',
    resultPage: 'Page {{page}}',
    hotelCount: '{{count}} of {{total}} hotels in this filtered view',
    moreHotels: 'Load more hotels', moreGroups: 'Load more room groups', moreOffers: 'Load more offers',
    viewRooms: 'View rooms', viewOffers: 'View broker offers', backResults: 'Back to results',
    reportedFrom: 'Reported from {{currency}} {{amount}}', minimumUnknown: 'Comparable minimum unavailable',
    wholeOffer: 'Reported price for the full stay and all requested rooms; not a final payable total.',
    priceBasis: 'Price basis', basis: { gross: 'Gross', net: 'Net', unknown: 'Unknown' },
    optionStatus: 'Provider status', packageCondition: 'Package condition', notPackage: 'Not package-only', conditionUnknown: 'Unknown',
    reportedCharges: 'Reported charges: {{included}} included · {{excluded}} excluded · {{unknown}} unknown',
    chargeDetailsMissing: 'Charge details are incomplete. Additional charges may apply.',
    chargeOtherCurrency: 'Some charges use another currency and are not added to this amount.',
    navigationGroup: 'Navigation group only: rooms and conditions have not been verified as equivalent. Every broker offer is retained.',
    moreGroupsAvailable: 'More room groups available in the detail.',
    observedAt: 'Observed: {{time}}',
    unmappedOffers: '{{count}} offers lack a verified property mapping and are not included in these cards.',
    quoteValidityUnknown: 'Search has no guaranteed validity period. A new quotation checks current price and availability.',
    resultsRefreshStopped: 'Automatic refresh stopped because results kept changing. Previously loaded results remain visible. Reload this view to continue.',
    resultsChanged: 'New offers arrived. Refreshing this view automatically without mixing result versions.',
    reloadResults: 'Reload results',
    emptyFiltered: 'No offers match these filters. Change them to explore the stored alternatives.',
    boardFilter: 'Meal plan',
    anyBoard: 'Any meal plan',
    refundabilityFilter: 'Refundability',
    anyRefundability: 'Any refundability',
    filtersHelp:
      'Every room must match a selected meal plan. Refundable does not mean free cancellation; check the policy. Unknown conditions remain unknown.',
    modifySearch: 'Modify search',
    updateSearch: 'Update search',
    cancelEdit: 'Cancel',
    preferences: 'Preferences',
    searchOptions: 'Search options',
    optionsApplyOnSearch: 'These options apply when you run a new search.',
    retainedFilters: 'Your filters are kept:',
    occupancySummary: '{{guests}} guests · {{rooms}} room(s)',
    filtersButton: 'Filters',
    clearFilters: 'Clear filters',
    applyFilters: 'Apply filters',
    closeFilters: 'Close',
    resultFilters: 'Filter these results',
    filteredCount: '{{count}} of {{total}} offers for all rooms',
    mixedBoards: 'Different meal plans across rooms',
    boards: {
      room_only: 'Room only',
      breakfast: 'Breakfast',
      half_board: 'Half board',
      full_board: 'Full board',
      all_inclusive: 'All inclusive',
      other: 'Other',
      unknown: 'Unknown meal plan',
    },
    refundability: { refundable: 'Refundable', non_refundable: 'Non-refundable', unknown: 'Unknown refundability' },
    title: 'Hotels',
    intro: 'Choose a destination or nearby point, dates and rooms.',
    location: 'Destination, airport or address',
    destinations: 'Places',
    google: 'Google addresses and places',
    keepTyping: 'Type at least two letters',
    noPlaces: 'No places found',
    destinationMode: 'Selected hotels in this destination',
    pointMode: 'Hotels near this point',
    partialPlaces: 'A places source is unavailable. Results may be incomplete.',
    coordsError: 'This point could not be located. Choose another place or try again.',
    radius: 'Radius (km)',
    nationality: 'Traveler nationality',
    search: 'Search hotels',
    invalid: 'Check the location, dates and occupancy. Every room needs at least one adult.',
    priceWarning: 'Amounts reported by providers. Check the current quotation before confirming a reservation.',
    packages: 'Show package-only rates',
    onlyPackages: 'The returned options are package-only. Enable the filter to see them.',
    offerCount_one: '{{count}} option for all rooms',
    offerCount_other: '{{count}} options for all rooms',
    unknownBroker: 'Unidentified provider',
    roomDetail: 'Room {{index}} · {{adults}} adults · child ages: {{ages}}',
    cancellationUnknown: 'Cancellation conditions not provided',
    payment: 'Payment',
    surcharge: 'Additional charge',
    roomCharge: 'Room {{index}} charge',
    chargeIncluded: 'Included in the fare',
    chargeExcluded: 'Not included in the fare',
    chargeUnknown: 'Inclusion in the fare unknown',
    loading: 'Resolving hotels and checking availability…',
    running: 'Receiving provider options…',
    partial: 'This search is incomplete: some providers or options could not be processed.',
    failed: 'The search could not be completed. Received options may be incomplete.',
    expired: 'Updates stopped after the time limit. Options may be incomplete; start a new search to refresh.',
    empty: 'No availability for this selection.',
    noCatalog: 'No eligible hotels for this selection.',
    error: 'The search could not be completed. It was not retried automatically.',
    unmapped: 'This destination does not yet have a configured hotel selection.',
    nextPage: 'Search the next group of hotels',
    pageNotice: 'Showing one catalog group. More hotels can be searched.',
    stop: 'Stop updates',
    stopped: 'Updates stopped. Options may be incomplete.',
    searched: 'Results for {{location}} · {{from}} — {{to}}',
  },
  hotelSharing: {
    salesAgency: 'Selling agency',
    searchWith: 'Connection from',
    contextHint: 'Choose the agency that owns the connection, including connections shared with your agency. The case determines the selling agency. Changing this selection starts a new search.',
    noContexts: 'No hotel search contexts are available.',
    manage: 'Share connection',
    hint: 'Choose which agencies may book using this connection. Cases and bookings still belong to the selling agency.',
    agencies: 'Shared with',
    shared: 'Available to the owning agency and the selected agencies.',
    exclusive: 'Only the owning agency may use this connection.',
    existingBookings: 'Removing access prevents new bookings; existing bookings can still be read and cancelled.',
    saveError: 'Could not save access settings. Reload the configuration and review your selection.',
  },
  hotelConnections: {
    bookings: 'Allow bookings',
    commercialWarning: 'Enable real operations with the provider for this account? Quotes and bookings may create charges. Test mode does not guarantee a sandbox. Turning this off blocks new quotes and bookings; existing bookings can still be cancelled.',
    confirmBookings: 'Enable provider operations',

    retry: 'Retry',
    empty: 'No hotel connections configured for this agency.',
    accessHint: 'Enter the Travelgate access IDs authorized for this account, separated by commas. These are not supplier names. They are not discovered automatically.',
    invalidAccess: 'Enter between 1 and 50 access IDs, without spaces inside each ID.',
    title: 'Hotel connections',
    hint: 'Configuration is stored encrypted in the hotel service. Enabling a connection does not verify availability.',
    loading: 'Loading connections…',
    error: 'The operation could not be completed.',
    test: 'Test',
    live: 'Live',
    add: 'Add Travelgate connection',
    name: 'Name',
    client: 'Travelgate client',
    access: 'Access codes (comma separated)',
    key: 'API key (write only)',
    safety: 'New accounts cannot quote or book until an administrator enables bookings. Test mode does not guarantee a sandbox.',
    close: 'Close',
    save: 'Save',
  },
  travelServices: {
    showFilteredResults_one: 'Show {{count}} result',
    showFilteredResults_other: 'Show {{count}} results',
    title: 'Ground services',
    subtitle:
      'Tours, transfers, circuits, rail, ferries, cars and eSIMs. Prices already include the agency markup.',
    caseContext: {
      active: 'Case #{{number}} · {{subject}} · {{client}}',
      invalid: 'This case is unavailable for booking in the operating agency or has no client.',
      remove: 'Remove context',
      travelers_one: '{{count}} traveler',
      travelers_other: '{{count}} travelers',
      useComposition: 'Use this party',
      compositionTooLarge: 'The roster has {{count}} travelers; this search supports up to 9 at a time.',
      searchServices: 'Search services',
      caseNeedsClient: 'Assign a client before searching services for this case.',
      wrongAgency: 'The case does not belong to the Rutero operating agency.',
    },
    transfersIntro:
      'Private and shared transfers. Prices already include the agency markup.',
    busesIntro:
      'Medium and long distance buses. The search price is the seat fare: sales taxes appear at booking time.',
    buses: {
      from: 'From',
      to: 'To',
      returnDate: 'Return date',
      withReturn: 'Round trip',
      seats: 'Seats',
      stationPlaceholder: 'City or terminal',
      typeToSearch: 'Type at least 2 letters',
      noStations: 'No stations for that search',
      emptyState: 'Fill in origin, destination and date to search buses.',
      found_one: '{{count}} service',
      found_other: '{{count}} services',
      returnsFound_one: '{{count}} return for your outbound',
      returnsFound_other: '{{count}} returns for your outbound',
      noResults: 'No services for that date.',
      direct: 'Direct',
      connection: 'With transfer',
      attendant: 'Attendant on board',
      fewSeats_one: '{{count}} seat left',
      fewSeats_other: '{{count}} seats left',
      otherClasses: 'Also: {{classes}}',
      plusTaxes: '+ taxes at booking',
      roundTripTotal: 'Round trip total',
      stepOutbound: '1 · Pick the outbound',
      pickOutbound: 'Select outbound',
      stepReturn: '2 · Pick the return',
      outboundChosen: 'Outbound chosen: {{time}} ({{seatClass}})',
      changeOutbound: 'Change outbound',
      outboundExpired:
        'The chosen outbound expired (the provider session lasts 15 minutes). Pick it again.',
      sortDeparture: 'Departure time',
      sortDuration: 'Duration',
      classFilter: 'Class',
      operatorFilter: 'Operator',
      serviceFilter: 'Service',
      seatMap: 'Pick seats',
      seatMapOptional:
        'Picking seats is optional: without a pick, the first free seats of the class are assigned.',
      seatsChosen: '{{chosen}} of {{total}}',
      seatPremium: 'Extra {{price}}',
      deck: 'Deck {{deck}}',
    },
    places: {
      placeholder: 'City, airport, port…',
      typeToSearch: 'Type at least {{count}} letters',
      noResults: 'No results for that search',
      noProviders: 'No providers configured for this destination',
      searchError: 'Could not search destinations. Try again.',
      groups: {
        places: 'Cities and areas',
        airports: 'Airports',
        stations: 'Stations',
        ports: 'Ports and terminals',
        points: 'Points of interest',
        addresses: 'Hotels and addresses',
      },
      kinds: {
        country: 'Country',
        region: 'Region',
        district: 'District',
        city: 'City',
        neighborhood: 'Neighborhood',
        airport: 'Airport',
        station: 'Station',
        port: 'Port',
        terminal: 'Terminal',
        beach: 'Beach',
        poi: 'POI',
      },
    },
    passengerPicker: {
      label: 'Passengers',
      title: 'Passengers',
      empty: 'Choose passengers',
      max: 'Up to {{count}} passengers total',
      incomplete: 'Complete the required ages',
      done: 'Done',
      increase: 'Add {{type}}',
      decrease: 'Remove {{type}}',
      ageLabel: 'Age · {{type}} {{number}}',
      typeNames: {
        passenger: 'Passenger',
        adult: 'Adult',
        child: 'Child',
        youth: 'Youth',
        senior: 'Senior',
      },
      types: {
        passenger_one: '{{count}} passenger',
        passenger_other: '{{count}} passengers',
        adult_one: '{{count}} adult',
        adult_other: '{{count}} adults',
        child_one: '{{count}} child',
        child_other: '{{count}} children',
        youth_one: '{{count}} youth',
        youth_other: '{{count}} youths',
        senior_one: '{{count}} senior',
        senior_other: '{{count}} seniors',
      },
      age: {
        hidden: 'Age is not required',
        optional: 'Optional age · improves fare accuracy',
        required: 'Age required',
      },
      errors: {
        required: 'Enter the age to continue.',
        invalid: 'Enter a whole-number age within the indicated range.',
        unsupported: 'That passenger type is no longer available.',
      },
    },
    railIntro:
      'Trains across Europe. The provider quotes live, so the search can take up to a minute.',
    rail: {
      from: 'From',
      to: 'To',
      stationPlaceholder: 'City or station',
      typeToSearch: 'Type at least 2 letters',
      noStations: 'No stations for that search',
      searchingLong:
        'Searching… the provider is quoting live with the operators, this can take up to a minute.',
      ageErrors: {
        required: 'Real age is required for youth and senior passengers.',
        invalid: 'Enter a whole-number age from 0 to 120.',
        unsupported: 'This type is no longer available. Select it again.',
      },
      ageNeededForPasses:
        'Close this dialog, return to the Rail search, enter every passenger’s real age, and search again to quote passes.',
      journeysFound_one: '{{count}} journey',
      journeysFound_other: '{{count}} journeys',
      direct: 'Direct',
      connection: 'Connection · {{duration}}',
      stops_one: '{{count}} change',
      stops_other: '{{count}} changes',
      select: 'Select',
      extraVariants_one: 'The provider lists {{count}} more variant with the same conditions at another price: the cheapest is shown.',
      extraVariants_other: 'The provider lists {{count}} more variants with the same conditions at other prices: the cheapest are shown.',
      emptyState: 'Fill in origin, destination and date to search trains.',
      conditions: 'Fare conditions',
      conditionTypes: {
        REFUND: 'Refund',
        EXCHANGE: 'Exchange',
        LUGGAGE: 'Luggage',
        SEAT: 'Seat',
        CANCELLATION: 'Cancellation',
      },
    },
    ferryIntro:
      'Ferries. Times are local to each port and the price includes the vehicle when it travels on board.',
    ferry: {
      fromPort: 'Departure port',
      toPort: 'Arrival port',
      date: 'Outbound date',
      returnDate: 'Return date',
      addReturn: 'Add return',
      withVehicle: 'With vehicle',
      vehicle: 'Vehicle',
      vehicleTooltip:
        'Without a vehicle the passengers travel on foot. The fare depends on the vehicle’s length and height, so the model has to be picked from the provider’s catalogue.',
      currency: 'Currency',
      portPlaceholder: 'e.g. Dover',
      vehiclePlaceholder: 'e.g. Volkswagen Golf',
      typeToSearch: 'Type at least 2 letters',
      vehicleOnly: 'vehicle only',
      noRoutes: 'The provider does not connect this departure port with any other. Try a different origin.',
      noPorts: 'No ports for that search',
      noVehicles: 'No vehicles for that search',
      emptyState: 'Pick the departure and arrival ports and a date to see the available crossings.',
      accommodation: 'Accommodation',
      ticketType: 'Fare type',
      berths_one: '{{count}} berth',
      berths_other: '{{count}} berths',
      shared: 'Shared',
      compulsory: 'Required on this crossing',
      compulsoryNotice:
        'This crossing requires an assigned place for every passenger. If you pick nothing, the place included in the fare stays; if you pick, your selection replaces it and the operator validates it before booking.',
      compulsoryNoOptions:
        'This crossing requires an assigned place for every passenger and the operator publishes no places to choose from: it books with the one included in the fare.',
      includedInPrice: 'Included in the price',
      defaultAccommodation: 'Kept if you pick nothing',
      fareIncluded: 'Included',
      fareOptional: 'Optional: the base fare applies if you pick nothing.',
      fareSamePrice: 'All cost the same: what changes are the conditions below.',
      farePriceUnknown:
        'Optional: the operator does not publish a price for every fare; the firm total is computed when booking.',
      fareFinalHint: 'Supplements already include the agency markup; the firm price is computed when booking.',
      pricingUnavailable:
        'The supplements for this offer could not be priced (it may have expired): the firm price is computed when booking.',
      extraPrice: '+ {{price}}',
      seatsOptional: 'Optional on this crossing',
      seatsRequired: 'Pick one place per passenger: this crossing requires it',
      seatsPartial_one: '{{count}} passenger still to cover',
      seatsPartial_other: '{{count}} passengers still to cover',
      seatsCovered_one: '{{count}} passenger covered',
      seatsCovered_other: '{{count}} passengers covered',
      vessel: 'Vessel',
      noRoute:
        'No departures on that date between {{fromPort}} and {{toPort}}. Note: both ports existing does not mean there is a direct route between them.',
      noKnownRoute:
        "We couldn't find any route between {{fromPort}} and {{toPort}}. You can still search, but there's likely no direct crossing.",
      found_one: '{{count}} crossing',
      found_other: '{{count}} crossings',
      busIncluded: 'Includes a bus leg',
      checkIn_one: 'Check-in {{count}} min before',
      checkIn_other: 'Check-in {{count}} min before',
      crossing: 'Crossing',
      assignedSeatRequired: 'Assigned seat required',
      roundTrip: 'Round trip',
      onFoot: 'On foot (no vehicle)',
      totalOneWay: 'leg total',
      totalRoundTrip: 'round-trip total',
      sortDeparture: 'Departure',
      sortDuration: 'Duration',
    },
    circuitsIntro:
      'Multi-day guided circuits. Pick the circuit first and then you will see its departures: the provider price is per person and the total is shown here.',
    circuits: {
      itineraryByDay: 'Day-by-day itinerary',
      optionalFull: 'See the full excursion',
      dayFull: 'See the full day',
      dayLess: 'See less',
      dayVideo: 'Video of the day',
      seeAllDays_one: 'See the itinerary day',
      seeAllDays_other: 'See all {{count}} itinerary days',
      collapseDays: 'Show fewer days',
      optionalsAside: 'Optional excursions — bought separately',
      providerPrice: 'Provider price',
      optionalNoPrice: 'Price to be confirmed',
      operatorBrochure: 'Operator brochure',
      seeDepartures: 'See departures',
      toConfirmByOperator: 'Operator confirms',
      passportFlag: 'Passport required',
      fromPerPersonIndicative: 'from {{price}} pp in a double · indicative',
      productSheet: 'Circuit details',
      clientPdf: 'PDF for your client',
      selectedCount_one: '{{count}} selected — it goes marked in the PDF',
      selectedCount_other: '{{count}} selected — they go marked in the PDF',
      print: {
        optionalPriceNote: 'Your advisor confirms the price of each excursion.',
        loading: 'Loading the agency details…',
        agencyUnavailable:
          'The agency details could not be loaded: reload the screen to try again.',
        action: 'Print / Save as PDF',
        quotedOn: 'Circuit quote · {{date}}',
        yourAdvisor: 'Your advisor: {{name}}',
        operatedBy: 'Operated by {{provider}}',
        perPersonDoubleLong: 'per person in a double room',
        indicativeOn:
          'Prices indicative as of {{date}}, subject to availability and operator confirmation.',
        selectedBadge: 'Selected',
        alsoAvailable: 'also available',
        generatedOn: 'Quote generated on {{date}}',
      },
      changeCircuit: 'Change circuit',
      returns: 'returns {{date}}',
      series: '{{name}} series',
      singleSupplementIndicative: 'Single suppl. {{price}} (indicative)',
      indicativeFrom: 'From — indicative',
      sheetError: 'Could not load the circuit details',
      viewSheet: 'View details',
      includes: 'Included',
      video: 'Watch video',
      brochure: 'Brochure {{number}}',
      guaranteedDeparture: 'Guaranteed departure',
      passportRequired: 'This circuit requires each passenger\'s passport number.',
      product: 'Circuit',
      productPlaceholder: 'e.g. Bellezas de Europa',
      typeToSearch: 'Type at least 3 letters',
      noProducts: 'No circuits for that search',
      onlyPastSeasons:
        'Only circuits from past seasons match that name. Turn on "include past seasons" to see them.',
      includePastSeasons: 'Include past seasons',
      destinations: 'Destinations',
      destinationsPlaceholder: 'City on the route',
      typeToSearchCity: 'Type at least 2 letters',
      noCities: 'No cities for that search',
      reselectCanonicalDestinations:
        'This saved search uses previous destinations. Select them again from the places directory.',
      circuitsCount_one: '{{count}} circuit',
      circuitsCount_other: '{{count}} circuits',
      allCitiesNotice: 'The circuit must pass through ALL selected cities.',
      duration: 'Duration (days)',
      minDays: 'Min.',
      maxDays: 'Max.',
      catalogFound_one: '{{count}} circuit in the catalog',
      catalogFound_other: '{{count}} circuits in the catalog',
      catalogTracked: 'Tracked catalog',
      catalogProviderFreshness_one: '{{provider}} {{total}} circuit ({{date}})',
      catalogProviderFreshness_other: '{{provider}} {{total}} circuits ({{date}})',
      catalogCoverage:
        'G Adventures is not listable: it is searched product by product.',
      noCatalogResults:
        'No circuit passes through all those cities. Try fewer cities or a wider duration.',
      pickDate: 'Pick the start date and hit Search to see departures.',
      categoryPrice: '{{category}} category price',
      seeCircuit: 'See circuit',
      sortDate: 'Departure date',
      sortDuration: 'Duration',
      sortName: 'Name',
      categoryFilter: 'Category',
      monthFilter: 'Departure month',
      band: {
        '1-7': 'Up to 7 days',
        '8-14': '8 to 14 days',
        '15+': '15 days or more',
      },
      mixedCurrencies:
        'These circuits are in different currencies: sorting by price compares the amounts without converting them.',
      priceOnDepartures: 'Price shown inside the circuit',
      perPersonDouble: 'pp in a double',
      indicativeByDeparture: 'Indicative — by departure',
      pastSeasonWarning:
        'That circuit belongs to season {{season}}: it most likely has no available departures. Pick one from the current season.',
      from: 'Departures from',
      to: 'Departures until',
      emptyState: 'Pick a circuit and the date window to see its departures.',
      departuresFound_one: '{{count}} departure',
      departuresFound_other: '{{count}} departures',
      noDepartures:
        'That circuit has no departures in those dates. Try another window or check that it belongs to the current season.',
      providerNotSearchable:
        'This circuit\'s departures come from {{provider}}, which is not available for search in this agency: they cannot be quoted until its connector is configured.',
      providerNotSearchableShort: '{{provider}} does not quote departures in this agency',
      until: 'until {{date}}',
      departureOn: 'Departing on {{date}}',
      fromPerPersonDouble: 'from, per person in a double',
      singleSupplement: 'Single: + {{price}}',
      indicativeNotice: 'The exact total is confirmed when booking.',
      perPerson: '{{price}} per person',
      totalForPax_one: 'total for {{count}} passenger',
      totalForPax_other: 'total for {{count}} passengers',
      occupancy: {
        label: 'Rooms and ages',
        title: 'Passenger rooms and ages',
        hint: 'Ages are at the departure date. A new passenger uses the adult age published by the operator.',
        summary: '{{rooms}} rooms · {{passengers}} pax',
        room: 'Room {{number}}',
        age: 'Passenger {{number}} age',
        addAdult: 'Add adult',
        addRoom: 'Add room',
        removeRoom: 'Remove room',
        removePassenger: 'Remove passenger',
        unavailable: 'This circuit connector did not publish occupancy limits. Rooms cannot be quoted safely.',
      },
      quote: {
        rooms: 'Rooms and category',
        roomsHint: 'The composition comes from the search. Pick a category only when the operator publishes it.',
        ages: 'Ages: {{ages}}',
        supplements: 'Supplements',
        passengerScope: 'Passenger {{passenger}} · room {{room}}',
        bookingScope: 'Whole booking',
        addSupplement: 'Add',
        nights: 'Nights',
        breakdown: 'Operator breakdown',
        conditions: 'View operator conditions',
        passengerRoom: '{{passenger}} · room {{room}}',
        unavailable: 'An authoritative quote cannot be obtained for this departure. Booking remains blocked until the connector publishes that capability.',
        refreshing: 'The operator rejected the previous quote token. A fresh quote is being requested with the same rooms and passengers.',
        ageBandsUnavailable: 'The operator did not publish an unambiguous age band for every passenger. Booking is blocked without inventing passenger types.',
        unavailableReason: {
          external_approval_required: 'Quoting is blocked until external commercial certification is complete.',
          provider_environment_restriction: 'The current operator environment cannot quote this departure.',
          live_validation_pending: 'Quoting is pending live operational validation.',
        },
        transfer: {
          airport: 'Airport',
          airline: 'Airline',
          flightNumber: 'Flight number',
          dateTime: 'Date and time',
          city: 'City',
        },
      },
    },
    extras: {
      title: 'Provider extras',
      loading: 'Loading the provider extras…',
      unavailable: 'Could not fetch the provider extras for this booking.',
      none: 'This booking has no provider extras available.',
      purchased: 'Purchased',
      add: 'Add',
      remove: 'Remove',
      added: 'Service added to the booking',
      removed: 'Service removed from the booking',
      unavailableCount_one: 'Unavailable ({{count}})',
      unavailableCount_other: 'Unavailable ({{count}})',
      addError: 'Could not add the service',
      removeError: 'Could not remove the service',
      serviceDate: 'Service date',
      validRange: 'Valid {{from}} – {{to}}',
      passengers: 'Passengers',
      passengerFallback: 'Passenger {{number}}',
      providerPassenger: 'Provider passenger: {{reference}}',
      passengerUnknown: 'The provider does not say which passenger it applies to',
      // The published amount describes ONE unit (the provider buys one per
      // passenger): that is what it says, plus how many go. Nothing is added up.
      perPassenger_one: 'per passenger · {{count}} passenger',
      perPassenger_other: 'per passenger · {{count}} passengers',
      blocked: {
        noPassenger: 'Choose at least one passenger',
        dateOutOfRange: 'The date is outside the service validity',
        dateRequired: 'Choose the service date',
        limitReached: 'No provider allowance for this service on this booking',
        overRemaining:
          'The provider accepts {{max}} on this booking and {{remaining}} remain(s): choose fewer passengers',
      },
    },
    passesIntro:
      'Rail passes for Europe. There is no origin or destination: a pass allows travel across a network during its validity. The product (Interrail or Eurail) is set by the passenger\'s country of residence.',
    passes: {
      residence: 'Country of residence',
      bookingBlocked: 'Passes can be quoted but not booked yet: the provider is fixing an issue on their end. The price shown is real.',
      quoteOnly: 'Quote only',
      residenceHint:
        'It sets the product: European residents buy Interrail and everyone else Eurail. That is why it is not picked by hand.',
      addPassenger: 'Add passenger',
      age: 'Age',
      youth: 'Youth',
      youthHint:
        'There is no child fare: minors travel as "youth" with their real age, and the provider applies the matching discount.',
      emptyState: 'Set the country of residence and the passengers to see the available passes.',
      found_one: '{{count}} product ({{total}} variants)',
      found_other: '{{count}} products ({{total}} variants)',
      variants_one: '{{count}} variant',
      variants_other: '{{count}} variants',
      fromPrice: 'from',
      showVariants: 'Show variants',
      hideVariants: 'Hide',
      travelClass: 'Class',
      firstClass: '1st class',
      secondClass: '2nd class',
      travelDays: 'Travel days',
      travelDaysIn: '{{days}} travel days within {{validity}} days',
      continuous: 'Continuous travel (every day of the validity)',
      globalOnly: 'Global passes only',
      filterByCountry: 'Search country',
      ticketNotice:
        'The travel document is a CODE (not a PDF) and the provider issues it 3 to 15 minutes after confirming.',
      residenceNeeded:
        'Set the passenger\'s country of residence to see the passes available to them.',
      crossSell: 'Passes covering this journey',
      crossSellHint:
        'If the passenger makes several trips, a pass may be better value than individual tickets.',
      crossSellSearch: 'See passes for this journey',
      crossSellEmpty: 'No passes cover this journey.',
      crossSellFrom: '{{count}} passes from {{price}}',
    },
    carsIntro:
      'Car rental. The price is the TOTAL for the period and the provider prices it by the customer’s country of residence.',
    cars: {
      pickUp: 'Pick-up',
      dropOff: 'Drop-off',
      pickUpDate: 'Pick-up date',
      pickUpTime: 'Time',
      dropOffDate: 'Drop-off date',
      dropOffTime: 'Time',
      residence: 'Country of residence',
      residenceHint: 'Of the customer: it changes the fare',
      differentDropOff: 'Return at a different location',
      paymentType: 'Payment',
      paymentAny: 'Any',
      paymentPpd: 'Prepaid',
      paymentPod: 'Pay at destination',
      locationPlaceholder: 'Airport or city',
      typeToSearch: 'Type at least 2 letters',
      noLocations: 'No locations for that search',
      nearbyOffices_one: '{{count}} nearby office',
      nearbyOffices_other: '{{count}} nearby offices',
      unsearchableLocation:
        'The provider publishes neither a code nor coordinates for that location: pick the airport or a nearby city.',
      unsearchableLocationNoCountry:
        "We couldn't get that location's country: try picking another one.",
      reselectCanonicalDropOff:
        'This saved search uses a previous drop-off. Select it again from the places directory.',
      reselectCanonicalPickUp:
        'This saved search uses a previous pick-up. Select it again from the places directory.',
      emptyState: 'Choose where and when to pick up the car to see the available vehicles.',
      found_one: '{{count}} vehicle · {{rates}} rates',
      found_other: '{{count}} vehicles · {{rates}} rates',
      ratePlan: 'Rate',
      included: 'What’s included',
      onTerminal: 'In terminal',
      shuttle: 'Shuttle',
      onTerminalOnly: 'In terminal only',
      onTerminalTooltip:
        'The office is IN the airport terminal: the car is picked up without transfers.',
      shuttleTooltip:
        'The office is off-airport: a rental company shuttle is needed to pick up the car.',
      conditionTypes: {
        Mileage: 'Mileage',
        Tax: 'Taxes and fees',
        'Collision Protection': 'Collision protection',
        'Liability Insurance': 'Liability insurance',
        'Addt’l. Driver': 'Additional driver',
        'Additional Driver': 'Additional driver',
        Fuel: 'Fuel',
        Deductible: 'Deductible',
      },
      filters: 'Filters',
      clearFilters: 'Clear',
      showResults_one: 'Show {{count}} vehicle',
      showResults_other: 'Show {{count}} vehicles',
      commissionableTooltip: 'This price is already the retail price to the customer: no agency markup is applied. The provider settles its commission separately, and the exact amount is known when the booking is confirmed.',
      onTerminalDetail: 'The office is IN the airport terminal: the car is picked up without transfers.',
      offTerminalDetail: 'The office is outside the airport: the rental company shuttles the passenger.',
      typeFilter: 'Type',
      paxFilter: 'Passengers',
      companyFilter: 'Company',
      protectionFilter: 'Protection',
      compareProtections: 'Compare protections',
      comparingProtections: 'Loading protections…',
      refreshProtections: 'Refresh protections',
      retryProtections: 'Retry comparison',
      protectionComparisonFailed:
        'We could not load the comparison. The originally available rate remains visible.',
      protectionComparisonExpired:
        'The protection comparison expired. Refresh it before selecting one of those rates.',
      protectionComparisonReady_one:
        '{{count}} protection available · valid for {{time}}',
      protectionComparisonReady_other:
        '{{count}} protections available · valid for {{time}}',
      protectionComparisonEmpty:
        'The comparison completed successfully, but no alternative protections are available.',
      automaticOnly: 'Automatic only',
      sort: 'Sort by',
      sortPrice: 'Price',
      sortPassengers: 'Capacity',
      sortCompany: 'Company',
      bodies: {
        car: 'Car',
        suv: 'SUV',
        van: 'Van',
        pickup: 'Pick-up truck',
        convertible: 'Convertible',
        wagon: 'Wagon',
      },
      passengers_one: '{{count}} passenger',
      passengers_other: '{{count}} passengers',
      bags_one: '{{count}} bag',
      bags_other: '{{count}} bags',
      doors_one: '{{count}} door',
      doors_other: '{{count}} doors',
      airConditioning: 'Air conditioning',
      sameOffice: 'Pick-up and drop-off at {{office}}',
      twoOffices: 'Pick-up at {{pickUp}} · drop-off at {{dropOff}}',
      totalForDays_one: 'total for {{count}} day',
      totalForDays_other: 'total for {{count}} days',
      totalTooltip_one:
        'Total rental price for {{count}} day, including the taxes the provider reports.',
      totalTooltip_other:
        'Total rental price for {{count}} days, including the taxes the provider reports.',
      commissionable: 'The provider settles commission separately',
      approxBreakdown: 'Provider estimates — base: {{base}} · taxes: {{tax}}',
      bookingDates_one: 'Pick-up {{pickUp}} → drop-off {{dropOff}} ({{count}} day)',
      bookingDates_other: 'Pick-up {{pickUp}} → drop-off {{dropOff}} ({{count}} days)',
      bookingOffices: '{{location}} · offices {{pickUp}} → {{dropOff}}',
    },
    esimsIntro:
      'Travel data eSIMs. Check each package’s activation policy: installing the eSIM does not necessarily start its validity.',
    esims: {
      local: 'Single country',
      global: 'Regional / worldwide',
      destination: 'Destination',
      countriesUnavailable:
        "We couldn't search countries right now. You can pick a region, or try again shortly.",
      noDestinations: 'No destinations for that search',
      countries: 'Countries',
      regions: 'Regions',
      available_one: '{{count}} package',
      available_other: '{{count}} packages',
      findPlan: 'Find a plan',
      destinationPlaceholder: 'e.g. Spain, Europe',
      coverageIs: 'Coverage: {{type}}',
      dataFilter: 'Data (GB)',
      validityFilter: 'Validity (days)',
      emptyState: 'Pick a destination and search the available packages.',
      days: 'days',
      coverage: 'Coverage',
      network: 'Network',
      package: 'Package',
      validityCol: 'Validity',
      retailCol: 'Suggested retail',
      unlimited: 'Unlimited data',
      validity_one: '{{count}} day validity',
      validity_other: '{{count}} days validity',
      suggestedPrice: 'Suggested retail: {{price}}',
      planType: 'Type: {{value}}',
      voiceMinutes: '{{count}} voice min',
      textMessages: '{{count}} SMS',
      activationPolicy: 'Activation: {{value}}',
      requiresRoaming: 'Enable data roaming on this eSIM. Keep it disabled on your usual line to avoid possible carrier charges.',
      apn: 'APN: {{value}}',
    },
    experiencesIntro:
      'Tours and activities. Prices already include the agency markup.',
    search: 'Search',
    searching: 'Searching providers…',
    refreshingPrices: 'Refreshing current prices…',
    resultsExpired: 'These results expired. Select Search to get current prices before booking.',
    // Short version to sit next to a blocked button, where the full
    // explanation does not fit (the long notice is already above the list).
    resultsExpiredShort: 'Quote expired',
    // The form no longer describes the search that produced the list: these
    // results belong to the previous one and cannot be booked from.
    formDiverged:
      'You changed the search and have not run it yet — these results are from the previous one. Select Search to update them.',
    formDivergedShort: 'Search not run',
    pricesValidFor: 'Prices valid for {{time}} more',
    editSearch: 'Edit search',
    refreshPrices: 'Refresh prices',
    sortDirection: 'Reverse the order',
    priceFrom: 'from',
    viaProvider: 'via {{provider}}',
    incompleteLink:
      'This link is missing part of the search: fill in what is left in the form and hit Search.',
    activeSearchSummary: 'Results for',
    searchError: 'Search failed. Try again.',
    noResults: 'No results for this search.',
    providerFailed: '{{provider}} did not respond',
    providerNoResults: 'no results',
    providerTimedOut: '{{provider}} timed out',
    providerSkipped: '{{provider}} skipped because of location',
    providerSkippedReasons: {
      place_not_mapped: 'The provider does not serve this place.',
      system_not_known: 'The provider does not recognize this place type.',
      resolver_unavailable: 'The place could not be resolved on this attempt.',
    },
    providerTruncated: '{{provider}}: {{returned}} of {{total}} results',
    providerDegraded: '{{provider}} quoted in its native currency',
    providersFailed: 'Providers without response: {{providers}}',
    offer: 'Offer',
    pax: 'Passengers',
    durationDays_one: '{{count}} day',
    durationDays_other: '{{count}} days',
    clearSearch: 'Clear',
    recentSearches: 'Recent searches',
    paxShort_one: '{{count}} pax',
    paxShort_other: '{{count}} pax',
    netPrice: 'Net: {{price}}',
    grossPrice: 'Provider retail price',
    book: 'Book',
    all: 'All',
    finalPrice: 'Price',
    createdAt: 'Created',
    bookingsTitle: 'Bookings',
    bookingsSubtitle:
      'All hotel, tour, transfer and other ground service bookings for the agency.',
    bookingList: {
      penalty: {
        label: 'Cancellation charges start',
        situation: 'Timing',
        scheduled: 'Known date',
        from_booking: 'From booking',
        none: 'No cancellation charges',
        unknown: 'Unknown',
        cancellationPenaltyFrom: 'From (UTC)',
        cancellationPenaltyTo: 'To (UTC)',
      },
      tabs: {
        attention: 'Need attention',
        upcoming: 'Travelling soon',
        all: 'All',
        closed: 'Closed',
      },
      columns: {
        reconfirmation: 'Reconfirmation',
        cancellationPenalty: 'Cancellation charges start',
        state: 'Status',
        service: 'Service',
        holder: 'Holder · pax',
        provider: 'Provider · ref',
        milestone: 'Next milestone',
        total: 'Total',
        case: 'Case',
        created: 'Created',
        action: 'Action',
      },
      scope: {
        label: 'Whose',
        mine: 'Mine',
        all: 'All',
        advisor: 'Advisor',
        advisorPlaceholder: 'Search…',
      },
      filters: {
        search: 'Search',
        searchPlaceholder: 'Booking, holder, case, client…',
        case: 'Case #{{number}}',
        serviceDate: 'Service date',
        serviceDateFrom: 'Service from',
        serviceDateTo: 'Service to',
        saleOrderless: 'No sale order',
        saleOrderlessHint: 'Shows bookings whose case has no Sales Order (Legacy). Only that case field is checked; this does not verify whether this booking is included as a service in the sales order.',
      },
      sort: {
        cancellationPenaltyStartsAt: { asc: 'Cancellation charges ↑', desc: 'Cancellation charges ↓' },
        createdAt: { desc: 'Created ↓', asc: 'Created ↑' },
        serviceDate: { asc: 'Service ↑', desc: 'Service ↓' },
        finalAmount: { desc: 'Total ↓', asc: 'Total ↑' },
      },
      day: {
        today: 'Today',
        tomorrow: 'Tomorrow',
        undated: 'No service date',
      },
      nextStep: {
        transportAmbiguous: 'The attempt got no answer — retake it from the booking',
        unknownStateSync: 'Pending reconciliation',
        manual: 'Check with the provider and reconcile by hand',
        adoptionPending: 'Booked — the numbers are still open',
        awaitingProvider: 'Waiting for the provider',
        ticketsReady: 'Tickets ready',
        ticketsPending: 'No tickets yet',
      },
      saleOrder: {
        attached: 'Attached',
        detached: 'Not attached',
        detachedHint: 'This booking is not on a sale order yet.',
      },
      case: {
        number: 'Case #{{number}}',
        none: 'No case',
      },
      actions: {
        view: 'View',
        resolve: 'Resolve',
      },
      by: 'by {{who}}',
      rowAria: '{{service}} — {{state}}. Opens the booking detail.',
      actionAria: '{{action}} — {{service}}',
      empty: {
        attention: {
          title: '✅ Nothing needs attention',
          body: 'Every booking is up to date. The badge disappears when this happens.',
        },
        upcoming: {
          title: 'No upcoming services',
          body: 'No live bookings with a service date from today on. Try widening the date range or look at "All".',
        },
        filtered: {
          title: 'No bookings with these filters',
          body: 'Nothing matches what is filtered. Clear the filters to see the whole tab.',
        },
        default: {
          title: 'No bookings yet',
          body: 'Ground service bookings show up here as soon as they are created.',
        },
      },
      truncated: 'The queue has {{count}} bookings and the {{shown}} most urgent are shown. Narrow it with the filters to see them all.',
    },
    selectedOptions: {
      section: 'Selected booking options',
      ferryAccommodations: 'Selected accommodations',
      ferryTicketType: 'Selected fare type',
      noFerryAccommodations: 'No explicit accommodations were submitted.',
      busSeats: 'Explicitly selected seats',
      busClassOverride: 'Selected class override',
      noExplicitBusSeats:
        'No explicit seats selected — the provider assigns the seats.',
      // Confirmed by the PROVIDER at hold time — distinct from what was requested.
      sectionReserved: 'What was reserved',
      reservedAccommodations: 'Accommodation',
      noReservedAccommodations: 'The provider did not assign any place.',
      reservedTicketType: 'Fare',
      // The provider did not confirm (at least) one of the requested facets
      // — accommodation and/or fare — so both sources are shown so the
      // discrepancy is visible. The note must name the facet that is
      // actually missing: it can never say "no place" when the panel above
      // it does list a confirmed accommodation (or vice versa with the fare).
      requestedDiscrepancyNoteAccommodations:
        'The provider did not confirm any place, but this was requested:',
      requestedDiscrepancyNoteTicketType:
        'The provider did not confirm the fare, but this was requested:',
      requestedDiscrepancyNoteBoth:
        'The provider did not confirm the place or the fare, but this was requested:',
    },
    // The transfer LEGS (`booking.legs` from the hub, transfers only):
    // pickup with its status, meeting point and vehicle, per leg.
    legs: {
      section: 'Transfer legs',
      pickup: 'Pickup',
      associatedFlight: 'Associated flight',
      associatedFlightTime: '{{time}} flight local time',
      // The provider does not give the time yet (declared closer to travel).
      // The pre-confirm NOTICE with the lead time belongs elsewhere (#2357).
      pickupReconfirm: 'Pickup time pending',
      pickupReconfirmWithin: 'Check the pickup time {{hours}} h before the service.',
      verifyPickup: 'The provider requires pickup time verification, even when a time is already published.',
      verificationAvailable: 'The time can be checked starting {{hours}} h before the service.',
      instructionsUnavailable: 'The full instructions exceed the available limit. Contact the operator before travelling; this document does not replace them.',
      pickupReconfirmGeneric: 'Check the pickup time before the service.',
      pickupReconfirmConfirmed: 'The booking remains confirmed.',
      pickupReconfirmLink: 'Check pickup time',
      meetingPoint: 'Meeting point',
      driverInstructions: 'How to find your driver',
      vehicle: 'Vehicle',
      freeCancellationUntil: 'Free cancellation for this leg until',
      // Wall-clock time of the pickup point, never converted: it is the time
      // the passenger reads on their watch on arrival.
      localTime: '{{time}} local time',
      localTimeWithZone: '{{time}} local time ({{timeZone}})',
    },
    detail: {
      cancelConfirmTitle: 'Cancel this booking?',
      cancelConfirmQuestion: 'The cancellation cannot be undone.',
      cancelConfirm: 'Cancel and accept the fee',
      cancelConfirmFree: 'Cancel the booking',
      cancelKeep: 'No, go back',
      cancelRefused: 'The operator did not allow the cancellation',
      cancelToday: {
        costWithRefund: 'Cancelling today costs {{fee}} — {{refund}} is refunded',
        cost: 'Cancelling today costs {{fee}}',
        freeWithRefund: 'Cancelling today is free — {{refund}} is refunded',
        free: 'Cancelling today is free',
        refund: 'Cancelling today refunds {{refund}}',
      },
      cancelFullVoucherValue:
        'Cancelling today retains the full voucher value. This is not a monetary amount.',
      noShow: {
        cost: 'No-show fee: {{fee}}',
        fullVoucherValue:
          'A no-show retains the full voucher value. This is not a monetary amount.',
      },
      cancelRefundByProvider: 'The refund is processed by the provider.',
      cancelPolicyUnpublished:
        'The provider does not publish cancellation costs — there may be fees.',
      policyLoading: 'Checking the cancellation policy…',
      policyUnknown: 'We could not get this provider cancellation policy. Cancelling may still have a cost.',
      policyScheduleTitle: 'Provider schedule',
      serviceRulesTitle: 'Cancellation rules by service',
      serviceRuleFrom: 'From {{from}} (inclusive)',
      serviceRuleUntil: 'Until {{to}} (exclusive)',
      serviceRulesNotice: 'Provider local times. Each cutoff replaces the previous one for the same service; these amounts are not refunds.',
      serviceRulesMissing: 'No published rules; this does not mean free cancellation.',
      policyService: 'Service {{number}}',
      policyBeforeFirst: 'Before the first cutoff',
      policySummaryBasis: 'Summary for cancelling all services assuming none have been consumed. It does not establish consumption or a refund.',
      policyPenaltyUnknown: 'The current cancellation charge is undetermined. This does not mean zero.',
      conditionalNotice: 'Independent conditional charges: not the current cancellation cost and not additive.',
      conditionalEvent: { cancellation: 'In case of cancellation', no_show: 'In case of no-show' },
      conditionalFull: 'Charge: full voucher value',
      conditionalWindow:
        'Published notice before pickup: {{hours}} h. Boundary and time zone unspecified; check the supplier conditions.',
      policyRefundUnknown: 'The provider does not determine consumed value or refund.',
      policySource: { availability: 'Source: quoted availability', booking: 'Source: provider booking' },
      policyScope: { incomplete: 'Missing or conflicting rules: no determined total.', unattributed: 'Rules cannot be linked to services; amounts are not added together.' },
      policyScheduleWhen: 'When',
      policyScheduleCost: 'Cancellation cost',
      policyScheduleLocalTime: 'Cutoffs use the service’s local time, without conversion to the advisor’s time zone. The start is exclusive and the end inclusive. The current cost is reported by the provider.',
      policyRuleCost: '{{percent}} + {{fee}} in fixed fees',
      policyRuleManagement: '{{fee}} in fixed fees',
      policyRangeBetween: 'After {{from}} through {{to}} inclusive',
      policyRangeUntil: 'Through {{to}} inclusive',
      policyRangeFrom: 'After {{from}}',
      policyRangeAny: 'No date published',
      title: 'Booking detail',
      expiresCol: 'Expires',
      onRequestNotice: 'The operator has not accepted this booking yet: it can still be confirmed or rejected. The state updates on sync.',
      holdExpires: 'Confirmation deadline: {{date}}.',
      createdBy: 'Booked by',
      updatedAt: 'Updated',
      case: 'Case',
      openCase: 'Open case',
      pricingSection: 'Price',
      net: 'Net',
      markup: 'Markup',
      commission: 'Provider commission',
      carPaymentBreakdown: 'Rental payment breakdown',
      providerTotal: 'Provider total frozen at booking',
      serviceBase: 'Service base, excluding counter charges',
      payment: {
        ppd: 'The service base is collected at booking; listed charges are paid at the counter.',
        pod: 'The full fare is paid at the counter.',
      },
      pickupCharge: 'At the counter — {{name}}: {{amount}}',
      unnamedCharge: 'Provider charge',
      cancelBooking: 'Cancel booking',
      viewPolicy: 'View cancellation policy',
      operate: 'Manage booking',
      itemNotLinked: 'This item has no linked service booking.',
    },
    updates: {
      viewBooking: 'View booking',
      unavailable: 'This booking is unavailable or you do not have access.',
      stateChanged: 'Booking {{booking}}: {{state}}',
      ticketsReady: 'Booking {{booking}}: tickets available',
      holdExpiring: 'Booking {{booking}}: expires on {{date}}. Check its status',
    },
    bookingStates: {
      draft: 'Draft',
      quoted: 'Quoted',
      held: 'On hold',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      failed: 'Failed',
      onRequest: 'Awaiting operator',
    },
    routeMap: {
      title: 'Route',
      requestedPointsHint:
        'These are the points that were requested; if the provider resolved a different one, the address on the offer prevails.',
      resolvedPointsHint:
        'This is the route the provider confirmed for this offer, not the one that was requested.',
      alt: 'Route map',
      show: 'View route',
      hide: 'Hide route',
    },
    serviceMoments: {
      start: 'Start',
      end: 'End',
      requested: 'Requested data',
    },
    booking: {
      ticketsExpired: 'The documents expired. Request them again to get valid links.',
      ticketsExpireAt: 'Links expire on {{date}}. After that they must be requested again.',
      ticketsRefresh: 'Request again',
      esimQrCopied: 'Copied!',
      esimCopyFailed: 'Could not copy automatically. Select the link and copy it by hand.',
      esimInstallation: 'Manual eSIM installation',
      esimIccid: 'ICCID',
      esimSmdpAddress: 'SM-DP+ address',
      esimActivationCode: 'Activation code',
      esimQrCodeData: 'QR data',
      esimCopyValue: 'Copy',
      ticketOpen: 'Open document',
      ticketCodeHint: 'This code is activated in the provider app: there is nothing to download.',
      ticketKinds: {
        pdf: 'PDF document',
        checkin: 'Online check-in',
        collection: 'Desk collection',
        file: 'Document',
        selfservice: 'Self-service',
        barcode: 'Boarding barcode',
        code: 'Pass code',
      },
      ticketDirections: {
        outbound: 'Outbound · {{document}}',
        return: 'Return · {{document}}',
      },
      // What the form asks comes from what the offer declares: these labels
      // name FIELDS, never a vertical.
      phoneCountry: 'Country',
      phonePlaceholder: 'Number without the country code',
      phonePickCountry: 'Pick the country code',
      phoneIncomplete: 'Incomplete number, or not valid for that country',
      identityDocument: 'Identity document',
      reselectIdentityDocument: 'The profile document changed. Select it again before booking or saving changes.',
      chooseIdentityDocument: 'Choose a document',
      newIdentityDocument: 'Enter {{type}}',
      paxFields: {
        type: 'Passenger type',
        title: 'Title',
        birthDate: 'Date of birth',
        email: 'Email',
        phone: 'Phone',
        nationality: 'Nationality',
        countryOfResidence: 'Country of residence',
        passportNumber: 'Passport',
        passportExpiry: 'Passport expiry date',
        flightNumber: 'Arrival flight',
        returnFlightNumber: 'Return flight',
        driverNotes: 'Driver comments',
      },
      // Placeholders for the fields that need one (free text): they say WHAT
      // to write, they do not repeat the label.
      paxFieldPlaceholders: {
        driverNotes:
          'Gate, doorbell, exact meeting point, "do not honk"…',
      },
      // The multiline input does not clip (the provider answers 400 instead
      // of trimming): the excess is flagged and the agent shortens it.
      paxFieldTooLong:
        'Maximum {{max}} characters ({{count}} typed). The provider does not trim: shorten it.',
      fieldScope: {
        contact: 'holder',
        all: 'every passenger',
      },
      fieldLevel: {
        hold: 'to book',
        quote: 'to quote',
        confirm: 'to confirm',
        recommended: 'improves the booking',
      },
      requiredForQuote:
        'The selected session requires {{fields}} to obtain its final price.',
      requiredForAll: 'This provider requires {{fields}} for EVERY passenger.',
      requiredForContact: 'It also requires {{fields}} from the holder.',
      recommendedFields:
        'Optional, used by the provider when present: {{fields}}.',
      confirmValidatedNotice:
        'The provider validates {{fields}} at confirmation, but they are sent when booking. Complete them before creating the hold.',
      missingPassengerFields:
        'The provider rejected the booking because these are missing: {{fields}}. Complete them and book again.',
      confirmBlockTitle: 'This held booking cannot be confirmed',
      confirmBlockNotice:
        'The provider required passenger data that was not published before the hold. Passengers cannot be changed through the current integration contract, so confirming again cannot repair this booking.',
      confirmBlockField: '{{field}} — {{scope}}',
      confirmBlockChip: 'Confirmation blocked',
      confirmBlockListTooltip:
        'The provider requested passenger data after the hold; confirming again cannot repair this booking.',
      confirmPendingSection: 'Missing to confirm',
      confirmPendingNotice:
        'Passenger details only reach the provider when booking: confirming carries the answers to the operator and nothing else. To enter them, cancel this booking and book again.',
      passengers: 'Passengers',
      passenger: 'Passenger',
      adult: 'Adult',
      youth: 'Youth',
      child: 'Child',
      infant: 'Infant',
      senior: 'Senior',
      firstName: 'First name',
      lastName: 'Last name',
      addPassenger: 'Add passenger',
      rosterHint: 'Choose a roster traveler for each place, or enter the details manually.',
      manageRoster: 'Manage travelers',
      rosterLoading: 'Loading case travelers…',
      rosterLoadError: 'The roster could not be loaded. Booking input is preserved.',
      rosterTraveler: 'Case traveler',
      rosterTravelerHint: 'Optional: prefills canonical profile data.',
      rosterBirthDateMissing: 'No birth date',
      bookingContact: 'Booking contact',
      saveChangesToProfile: "Save these changes to {{traveler}}'s profile",
      saveTravelerToCase: 'Save {{traveler}} as a traveler in this case',
      saveTravelerToCaseHint: 'Their details can be reused in future bookings.',
      travelersSavedToCase: 'The selected travelers were saved in the case.',
      travelerCreateError: '{{traveler}} could not be saved in the case. The entered details were preserved.',
      profileUpdated: 'Traveler profile updated',
      profileUpdateError: 'The profile could not be updated. Booking input is preserved.',
      selectedCaseSummary: 'Case #{{case}} · Client: {{client}}',
      holderSection: 'Booking holder details',
      holderContactSection: 'Because they hold the booking',
      driverContactSection: 'Because they drive the car',
      contactRow: 'Passenger {{number}} · contact',
      driverRow: 'Driver',
      paxTypeWithAge: '{{type}} · {{age}}',
      partyFromSearch: 'From the search: {{party}}',
      partyFromQuote: 'From the quote: {{party}}',
      partyChangeHint: 'to change it, search again',
      partyGroup_adult_one: '1 adult',
      partyGroup_adult_other: '{{count}} adults',
      partyGroup_passenger_one: '1 passenger',
      partyGroup_passenger_other: '{{count}} passengers',
      partyGroup_youth_one: '1 minor',
      partyGroup_youth_other: '{{count}} minors',
      partyGroup_child_one: '1 child',
      partyGroup_child_other: '{{count}} children',
      partyGroup_infant_one: '1 infant',
      partyGroup_infant_other: '{{count}} infants',
      partyGroup_senior_one: '1 senior',
      partyGroup_senior_other: '{{count}} seniors',
      partyGroupWithAges: '{{group}} (aged {{ages}})',
      shapeContradiction:
        'The provider requires {{fields}}, which this request does not have. That is a contradiction in their integration: there is nowhere to enter it and no point in doing so. If the booking is rejected for it, raise it through their channel.',
      driverSection: 'Driver details',
      refundPending:
        'The provider is processing the refund: the amount is confirmed in their settlement, not on this screen.',
      refundDone: 'Refund confirmed: {{amount}}',
      refundError: 'The provider could not process the refund ({{amount}}): claim it through their channel.',
      nationality: 'Nationality',
      phone: 'Phone',
      birthDate: 'Date of birth',
      providerHold: 'Create the booking with the provider (On Hold)',
      providerHoldTooltip:
        'Unlike every other provider, for cars the hold does NOT create anything on their side by default: it only freezes this quote\'s price here. Turn this on so the rental company creates the "On Hold" booking (no charge, fare held), as happens with the other products; you then confirm or cancel it.',
      offerOptionsUnavailable:
        "The provider's options could not be loaded: the booking goes ahead and the provider assigns what is included.",
      flightsSection: 'Times and flights',
      outbound: 'Outbound',
      return: 'Return',
      paxNumber: 'Passenger {{number}}',
      requoteHint: 'You changed a time: update the price before booking.',
      arrivalFlight: 'Arrival flight',
      returnFlight: 'Return flight',
      requote: 'Update price',
      requoteNotFound:
        'No equivalent offer (same provider and product) was found when re-quoting. The results already show the new search: pick an offer from the list.',
      requoteAmbiguous:
        'Re-quoting returned several offers indistinguishable from this one (same provider and product). To avoid booking a different one, pick the right offer from the list, which already shows the new search.',
      fixedPax:
        'Passengers match the search ({{count}}). To change the count, search again.',
      hold: 'Book (hold)',
      confirm: 'Confirm booking',
      cancelHold: 'Cancel hold',
      cancelBooking: 'Cancel booking',
      sync: 'Sync',
      getTickets: 'View tickets',
      close: 'Close',
      holdRetention: {
        none: 'Local hold: the provider has not retained availability or the fare. Confirm within the published deadline.',
        provider: 'The provider holds a temporary reservation until the published deadline.',
        'provider-expiry-unpublished': 'The provider holds a temporary reservation but has not published its expiry.',
        unknown: 'Pending confirmation. The provider has not specified whether availability is retained.',
      },
      holdRateExpired: 'The confirmation deadline has passed. Obtain a new quote; this fare cannot be confirmed again.',
      holdTooltip:
        'Pending confirmation. Check the retention conditions and confirmation deadline.',
      questionsTitle: 'The operator asks',
      questionsNotice:
        'The answers travel with the confirmation; the mandatory ones block it until answered. None is filled in on its own: a made-up answer would confirm the booking with the wrong data.',
      questionForPassenger: 'Passenger {{number}}',
      questionForPassengerNamed: 'Passenger {{number}} · {{name}}',
      questionYes: 'Yes',
      questionNo: 'No',
      questionMandatory: 'mandatory',
      questionAnswer: 'Answer',
      questionsOmitted_one:
        'The confirmation takes {{max}} answers: 1 of the optional ones you filled in will not travel.',
      questionsOmitted_other:
        'The confirmation takes {{max}} answers: {{count}} of the optional ones you filled in will not travel.',
      questionFormat: 'Format the operator expects: {{format}}',
      confirmTooltip:
        'Confirms the booking with the provider: it creates a payment obligation unless cancelled according to its cancellation policy.',
      cancelHoldTooltip:
        'Releases the hold at no cost. Note: some providers do not allow cancelling it (it expires on its own).',
      flightNumber: 'Flight',
      passport: 'Passport/ID',
      tickets: 'Tickets',
      ticketsNotReady: 'Tickets are not ready yet: retry in a few minutes.',
      ticketsPolling:
        'Tickets are not ready yet. We are checking again automatically.',
      ticketsPollingStopped:
        'Tickets are not ready yet. You can request them again whenever you want.',
      ticketsNotSupported: 'This provider does not expose tickets through the API.',
      pendingVerification:
        'The booking is pending provider verification. Sync can recover it using its Rutero booking ID; do not retry the hold.',
      pendingChip: 'Pending verification',
      manualChip: 'Manual reconciliation',
      manualNotice:
        'The provider may have created a booking. Automatic Sync cannot resolve it because no Rutero booking ID is available. Do not create another booking until this ambiguity is resolved.',
      sameAttemptRetryChip: 'Retry original attempt',
      sameAttemptRetryNotice:
        'An equivalent hold is unresolved. Retry to resume the original attempt and its original idempotency key; do not create a new attempt.',
      existingAttemptNotice:
        'An equivalent hold attempt is already being processed. The original attempt remains authoritative.',
      verifiedBookingId: 'Verified Rutero booking ID',
      attachVerifiedBookingId: 'Attach verified booking ID',
      markNoRemoteBooking: 'No remote booking exists',
      confirmNoRemoteTitle: 'Confirm no remote booking exists',
      confirmNoRemoteNotice:
        'Only continue after externally verifying that the provider did not create a booking. This closes the original uncertain attempt and cannot be undone automatically.',
      keepManual: 'Keep pending',
      confirmNoRemote: 'Confirm no remote booking',
      manualAttachError: 'Could not attach the verified booking ID',
      manualNoRemoteError: 'Could not resolve the booking as not created',
      // When something goes sideways: every hold state gets ITS face and ITS
      // action. The wording is the agent's, not the system's — the technical
      // code sits apart, small, for support.
      recovery: {
        transportAmbiguous: {
          title: '🔌 We do not know whether it arrived',
          body: 'The connection dropped while sending the booking: it may or may not have been created. Retrying is safe — it is resent with the same key and the provider does not duplicate it.',
          cta: 'Retry (safe)',
          noResend:
            'This attempt can no longer be resent from here. Check with the provider whether the booking exists before booking again.',
        },
        unknownStateSync: {
          title: '🔄 Pending reconciliation',
          body: 'We do not know whether the provider completed the operation. Sync to check its status; the result may remain unknown. Do not create another booking or repeat confirmation or cancellation while it is pending.',
          cta: 'Sync now',
        },
        manual: {
          title: '🧑‍💼 Needs a human eye',
          body: 'The provider answered oddly and there is no reference to verify it on our own. Do not retry: check the provider extranet to see whether the booking exists.',
          cta: 'Verified — resolve',
        },
        providerReference: 'Provider booking reference: {{reference}}',
        trackingId: 'Tracking ID: {{id}}',
        adoptionPending: {
          title: '⏳ Booked, closing the numbers',
          body: 'The booking is created; the commercial record (price, markup, commission) is still being frozen. It completes on its own — it cannot be confirmed until it closes.',
          cta: 'Refresh state',
        },
        // Clean rejections: the hub answered, nothing is live on the other
        // side, and the only honest way out is to search again.
        terminal: {
          provenanceExpired: 'This offer is no longer valid',
          selectionChanged: 'The provider changed the selected rate',
          offerExpired: 'The offer expired per the provider',
          railPassengerCompositionMismatch:
            'The passengers do not match the quote',
          railPlacePropertyRequired: 'This fare requires a seat selection',
          railPlacePropertyRequiredBody:
            'Bandido cannot select that seat yet. The provider’s temporary hold expires automatically; search again after it expires before trying another booking.',
          providerTemporaryHoldExpiresAt:
            "The provider's temporary hold expires: {{date}}",
          offerExpiredHint:
            'The provider signs an expiry on every quote. Re-quote the same transfer to get a current offer without losing what you loaded.',
          providerPolicy: 'The operator does not accept this booking',
          cleanFailed: 'This offer could not be booked',
          correctRequest:
            'The offer is still valid: correct what the provider points out — below — and book again. Sending the same request changes nothing.',
          noProviderMessage: 'The provider gave no reason.',
          requote: 'Re-quote this offer',
          searchAgain: 'Search again',
        },
      },
      activeOperation: {
        confirm: {
          in_progress: 'Confirm processing',
          ambiguous: 'Confirm outcome being verified',
          reconciling: 'Confirm reconciling locally',
          stalled:
            'Confirm unresolved: the system stopped retrying. There may be a live booking at the provider — check with them before operating.',
        },
        cancel: {
          in_progress: 'Cancel processing',
          ambiguous: 'Cancel outcome being verified',
          reconciling: 'Cancel reconciling locally',
          stalled:
            'Cancel unresolved: the system stopped retrying. The booking may still be live at the provider — check with them before operating.',
        },
      },
      fareValidFor: 'Fare valid for {{time}}',
      fareExpired: 'Fare expired',
      fareTtlHint: 'The provider holds the price for 15 minutes from the search. If it expires, search again.',
      fareExpiredHint: '15 minutes have passed since the search: search again to get a new quote.',
      // Cuando el corte lo fija el vencimiento FIRMADO por el proveedor en la
      // oferta (no el TTL local): el plazo es de él, no nuestro.
      fareSignedTtlHint: 'The provider signed an expiry for this quote. If it lapses, quote again.',
      fareSignedExpiredHint: 'The quote lapsed per the expiry the provider signed: quote again to get a current offer.',
      perPassengerTimes: '{{price}} × {{count}} passengers',
      // El precio de cada momento: qué es el número de la barra y, si el
      // proveedor lo movió al reservar, con qué desglose lo explica.
      priceEstimated: 'Estimated — the final price arrives when booking',
      providerFrozenPrice: 'Provider price, frozen',
      providerFrozenPriceAmount: 'Provider price, frozen: {{price}}',
      reprice: {
        title: 'The provider adjusted the price when booking',
        breakdown: 'Provider breakdown: {{lines}} · frozen {{total}}',
      },
      includesOptions: 'Includes {{price}} in options',
      includesNamedOptions: 'Includes {{options}}: {{price}}',
      discountsOptions: '{{price}} less for the selected options',
      selectionRejected: {
        UNCOVERED_PASSENGERS_one: 'The operator rejects this selection: {{count}} passenger still needs a place.',
        UNCOVERED_PASSENGERS_other: 'The operator rejects this selection: {{count}} passengers still need a place.',
        UNCOVERED_PASSENGERS_UNKNOWN: 'The operator rejects this selection: it does not cover every passenger.',
        BELOW_MINIMUM_OCCUPANCY: 'The operator rejects this selection: one of the places requires more occupants than the passengers travelling.',
        PROVIDER_RULE: 'The operator rejects this selection.',
      },
      willReserve: 'Would reserve: {{options}}',
      quotingSelection: 'Quoting the selection with the operator…',
      basePriceWithUnquotedOptions:
        'Base price — selected add-ons are not included in this amount. The total is validated when creating the hold, before confirmation.',
      selectionNotQuoted:
        'The selection could not be quoted: the total above is the offer without options; the firm price is computed when booking.',
      provenanceExpired: 'The offer expired (15 min). Search again for a current price.',
      offerExpiredByProvider:
        'The offer expired per the expiry the provider signed. Search again for a current price.',
      searchChanged: 'The search changed. Select a current offer from the latest results.',
      caseLabel: 'Case for this booking',
      caseHint: 'Search by number, subject, or client.',
      caseNumber: 'Case #{{id}}',
      caseNoOptions: 'No cases with a client match this search.',
      caseLoadError: 'Cases could not be loaded. Try again.',
      createCase: 'Create case',
      createCaseTitle: 'Create a case for this booking',
      createCaseSubject: 'Case subject',
      createCaseClient: 'Required client',
      selectClient: 'Choose client',
      holdError: 'Could not create the booking',
      confirmError: 'Could not confirm the booking',
      cancelError: 'Could not cancel the booking',
      syncError: 'Could not sync the booking',
      syncSuccess: 'Booking synced',
      // El momento de la reserva y su barra de decisión: qué se está
      // decidiendo, con qué precio, hasta cuándo y qué falta para decidirlo.
      moments: {
        review: 'Review',
      },
      viaHub: '{{provider}} · via Rutero',
      providerReference: 'Ref: {{reference}}',
      tripSection: 'Trip details',
      // Los adicionales que publica la OFERTA: se eligen acá (cantidad y
      // tramo) y se reservan en el MISMO hold. El total con adicionales lo
      // devuelve el hub; la sección no suma plata.
      optionalItems: {
        section: 'The provider also offers',
        perBooking: 'per booking',
        perPerson: 'per passenger',
        maxAllowed: 'Up to {{max}} accepted',
        unknownRejected:
          'The offer no longer includes: {{names}}. Fix the selection and book again.',
        limitRejected:
          'The operator accepts fewer units of an add-on than requested',
      },
      confirmedOptionalItems: {
        title: 'Add-ons in the original confirmation',
        providerAdded: 'Included by the operator',
        free: 'No charge',
      },
      conditionsSection: 'Conditions',
      cancellationPolicySection: 'Cancellation policy before confirming',
      showConditions_one: 'See {{count}} condition',
      showConditions_other: 'See {{count}} conditions',
      hideConditions: 'Hide conditions',
      documentsSection: 'Travel documents',
      voucher: {
        title: 'Agency voucher',
        kind: {
          voucher: 'Agency voucher',
          receipt: 'Agency receipt',
          confirmation: 'Agency confirmation',
        },
        generate: {
          voucher: 'Generate voucher',
          receipt: 'Generate receipt',
          confirmation: 'Generate confirmation',
        },
        regenerate: 'Regenerate',
        view: 'View',
        download: 'Download',
        generatedAt: 'PDF · generated on {{date}}',
        generating: 'Generating the document… this can take a few seconds.',
        ready: 'The voucher is ready.',
        stillGenerating: 'Generation is taking longer than usual. Reopen the booking in a while.',
        requestFailed: 'The voucher could not be requested.',
        fileUnavailable: 'The file is no longer available. Generate it again.',
        fileFailed: 'The file could not be downloaded. Try again.',
        stateUnavailable: 'The voucher state could not be read.',
        stale: 'The booking changed on {{date}}: the document you have no longer matches.',
        unsupported: 'There is no voucher for {{vertical}} yet.',
        alternative: {
          circuits: 'The itinerary is printed from the product.',
        },
        attachedReferences: 'The operator ticket is delivered separately: {{references}}. Download it from "View tickets".',
        providerDocument: {
          embedded: 'Includes the operator ticket in the following pages.',
          printed: 'The document includes the operator details or instructions. A self-service link is not an embedded ticket.',
          attached: 'The operator ticket is delivered separately, in its original file.',
        },
        send: {
          button: 'Send…',
          email: 'By email to the booking contact',
          link: 'Copy link (expires in 7 days)',
          requestFailed: 'The voucher could not be sent.',
          requestUnanswered:
            'The server did not answer in time. The email may be on its way: do not repeat it yet; the status below says how it ended. If you press "Send" again, that same attempt is checked instead of sending another email.',
          sent: {
            pending: 'The send is still in progress: the status below refreshes on its own. Pressing "Send" again checks this same attempt; it does not send another email.',
            resolved: 'Voucher sent to {{recipient}}.',
            partial: 'Sent to {{recipient}}, but WITHOUT the operator ticket: it travels separately, send it from "View tickets".',
            no_email: 'The booking has no contact email. Add it to the passenger details and send again.',
            failed: 'The email provider rejected it: it did not go out. It was recorded; try again later and, if it persists, contact support.',
            uncertain:
              'Unknown whether it went out: the email to {{recipient}} was handed to the provider but the connection dropped before the reply. Confirm with the contact before resending.',
          },
          copied: 'Link copied. It expires in 7 days.',
          copiedPartial: 'Link copied, expires in 7 days. NOTE: it does not include the operator ticket, which travels separately.',
          copyFailed: 'Could not copy automatically: copy the link from the field below.',
          linkField: 'Link to share',
          linkHint: 'Expires in 7 days and stops working if the voucher is regenerated.',
          last: {
            pending: 'Send in progress since {{date}}: no reply from the email provider yet.',
            resolved: 'Sent to {{recipient}} on {{date}}.',
            partial: 'Sent to {{recipient}} on {{date}}, without the operator ticket (delivered separately).',
            no_email: 'Last attempt on {{date}}: the booking had no contact email.',
            failed: 'Last attempt on {{date}}: the email provider rejected the send to {{recipient}}.',
            uncertain:
              'Last attempt on {{date}}: the email to {{recipient}} was left unconfirmed (it may have gone out).',
            link: 'Share link issued on {{date}}.',
            linkPartial: 'Share link issued on {{date}}, without the operator ticket (delivered separately).',
            linkUnconfirmed:
              'A share link was requested on {{date}} and it was never confirmed as issued. If you need one, copy a new link.',
          },
        },
        row: {
          ready: 'Voucher ready',
          stale: 'Voucher outdated',
          pending: 'Voucher to generate',
        },
        reasons: {
          agency_brand_missing: 'The agency has not uploaded its logo yet. It is set up under Vouchers.',
          provider_reference_missing: 'The booking has no provider reference.',
          service_date_missing: 'The service date is missing.',
          provider_created_at_missing: "The provider's booking date is missing; it arrives with the next sync.",
          service_end_missing: 'The drop-off date the rental document needs is missing.',
          holder_missing: 'The booking has no lead passenger.',
          service_detail_missing: 'The service detail the document prints is missing.',
          booking_not_confirmed: 'The booking is not confirmed yet.',
          booking_outcome_unresolved: "The booking's last change is not settled with the provider. It resolves with recovery or the next sync.",
          provider_tickets_pending: 'The provider has not issued the code or QR yet; it arrives with the next sync.',
        },
      },
      recordSection: 'Record',
      holdExpiresIn: 'Expires in {{time}}',
      holdExpired: 'The hold expired',
      instantConfirmation: 'Instant confirmation',
      onRequestConfirmation: 'The operator must accept the booking',
      freeCancellationUntil: 'Free cancellation until {{date}}',
      // Un CTA deshabilitado SIEMPRE dice por qué, al lado del botón.
      blockedForHold: 'To book: {{reasons}}',
      blockedForConfirm: 'To confirm: {{reasons}}',
      blocked: {
        case: "choose the client's case",
        passengers: 'fill in the passenger details the provider requires',
        passengerFields: 'fill in the required details: {{fields}}',
        incompleteTrip:
          'this fare does not contain the outbound and return legs required by the request',
        fieldTooLong:
          'shorten {{fields}}: it exceeds the maximum length the provider accepts, and it is not trimmed automatically',
        shapeContradiction:
          'the provider requires {{fields}} and this request cannot have them: there is nowhere to enter them, and passenger data only travels when booking, so the booking could never be completed. Raise it through their channel',
        options: 'check the selected options',
        luggageQuote: 'complete luggage details and wait for a valid luggage and price quote',
        requote: 'refresh the price with the new time',
        requoting: 'wait for the new quote to come back',
        loadingOptions: "wait for the provider's options to load",
        questions: "answer the operator's mandatory questions",
        answersOverflow:
          'the operator published more mandatory questions than its confirmation can take answers for: this booking cannot be confirmed from here, it has to be sorted out with the operator',
        confirmPassengers:
          'the provider validates these at confirmation and they are missing: {{fields}}',
        requestRefused:
          'correct what the operator refused (the reason is above): the same request will be refused again',
      },
      expiresAt: 'Hold expires: {{date}}',
      // Closing the modal while the booking is still held: asked once, and
      // with no published expiry no hour is made up.
      closeHeld: {
        title: 'The booking is still held',
        question: 'The booking is held until {{time}}. Close anyway?',
        questionNoExpiry:
          'The booking is still held and not confirmed yet. Close anyway?',
        keep: 'Stay here',
        closeAnyway: 'Close anyway',
      },
    },
    tours: {
      session: 'Session',
      pickup: 'Pickup point',
      ticketQuantities: 'Tickets',
      ticketQuantitiesRequired:
        'Choose the exact quantities published for this session.',
      ticketQuantitiesUnavailable:
        'This session requires ticket quantities, but none are available.',
      noSessions: 'There are no available sessions for this offer.',
      selectionUnavailable: 'The selected session is no longer available. Choose another one.',
      discover: 'View options',
      priceAfterSession: 'Price after selecting a session',
      priceOnRequest: 'Price available on request',
      viewExperience: 'View experience',
      freeCancellation: 'Free cancellation',
      noFreeCancellation: 'No free cancellation',
      instantConfirmation: 'Instant confirmation',
      noInstantConfirmation: 'Confirmation is not immediate',
      mobileVoucher: 'Mobile voucher',
      noMobileVoucher: 'Mobile voucher is not accepted',
      productDetail: 'Experience details',
      detailUnavailable:
        'We could not load the expanded information. You can check availability using the search details.',
      description: 'Description',
      highlights: 'Highlights',
      routes: 'Itinerary',
      modalities: 'Available formats',
      location: 'Location',
      conditions: 'Conditions',
      refundPolicies: 'Refund policy',
      nonRefundable: 'The provider declares this option non-refundable.',
      included: 'Included',
      notIncluded: 'Not included',
      meetingPoint: 'Meeting point',
      remarks: 'Important information',
      availability: 'Availability',
      chooseExperience: 'Choose your experience',
      loadingOptions: 'Loading dates and options…',
      optionsUnavailable: 'We could not load this product’s options.',
      retryOptions: 'Try again',
      continueToQuote: 'Continue and quote',
      selectPickup: 'Choose a pickup point to view the sessions.',
      selectSession: 'Choose a session explicitly to continue.',
      availabilityStatus: {
        schedule: 'Published schedule',
        live: 'Live availability',
      },
      pricingStatus: {
        unpriced: 'Price pending quote',
        indicative: 'Indicative price',
        authoritative: 'Authoritative price',
      },
      quoteRequirements: {
        ages: 'Real ages required to quote',
        ticket_quantities: 'Ticket quantities required',
      },
      checkAvailability: 'Check availability',
      quoteBeforeHold:
        'The session and final price are verified before booking.',
      passengerAgesForQuote: 'Passengers in this quote',
      passengerAgesForQuoteHint:
        'Enter each passenger’s birth date once. We calculate their age on the activity date and reuse it when booking.',
      passengerAgesWithoutDateHint:
        'The provider did not publish this session’s date, so age cannot be calculated from birth date. Enter each passenger’s current whole age to quote.',
      passengerAge: 'Participant {{number}} age',
      passengerAgeInvalid: 'Enter a whole age from 0 to 120.',
      quoteInputRequired:
        'Complete the non-personal party information required to quote this session.',
      quoteMustRefresh:
        'The quote no longer matches the selection or passengers. Check the ages and quote again.',
      quoteValidUntil: 'Price valid until {{date}}',
      continueWithPassengers: 'Continue with passengers',
      blockedForQuote: 'To continue: {{reasons}}',
      quotedSelection: 'Availability and price verified',
      changeAvailability: 'Change availability',
      birthDateQuoteMismatch:
        'A complete birth date does not match the age used for the quote. Refresh the price with the correct age before booking.',
      requoteCorrectedAges: 'Requote ages',
      providerFilter: 'Provider',
      categoryFilter: 'Category',
      languageFilter: 'Language',
      sortRelevance: 'Relevance',
      sortTitle: 'Name',
      sortPrice: 'Price',
      mixedCurrencies:
        'Results use different currencies. Relevance order is preserved instead of comparing unlike amounts.',
      productsShown_one: '{{count}} of {{total}} experience',
      productsShown_other: '{{count}} of {{total}} experiences',
      noFilteredProducts: 'No experiences match these filters.',
      free: 'Free',
      from: 'From',
      to: 'To',
      destination: 'Destination',
      typeToSearch: 'Type at least 3 letters',
      noDestinations: 'No destinations with that name',
      productsFound_one: '{{count}} experience',
      productsFound_other: '{{count}} experiences',
      departuresCount_one: '{{count}} departure',
      departuresCount_other: '{{count}} departures',
      pickDeparture: 'Pick a departure date',
      ageBand: 'Age {{band}}',
      fare: 'Fare',
      perPassenger: 'per passenger',
      perGroup: 'per group or service',
      perPassengerHint: 'Prices are per passenger of each age band, not for the group.',
      adultOnlyBookingUnavailable:
        'Quote only: this fare is outside the current adult-only booking flow.',
      adultOnlyShort: 'Not for this booking',
      showMore: 'Show more',
      showLess: 'Show less',
    },
    transfers: {
      onRequest: 'The operator must accept the booking',
      pickUpAt: 'Pick-up {{time}}',
      pickUpAtDiffers: 'Pick-up {{time}} (you asked for {{requested}})',
      pickUpReconfirm: 'Pickup time pending',
      pickUpReconfirmWithin: 'Check the pickup time {{hours}} h before',
      pickUpLegReconfirmWithin: '{{leg}}: check the pickup time {{hours}} h before',
      pickUpReconfirmLink: 'Check pickup time',
      pickUpLeg: '{{leg}}: pick-up {{time}}',
      pickUpLegDiffers: '{{leg}}: pick-up {{time}} (you asked for {{requested}})',
      pickUpLegReconfirm: '{{leg}}: pickup time pending',
      showConditions_one: 'View {{count}} condition',
      showConditions_other: 'View {{count}} conditions',
      hideConditions: 'Hide conditions',
      meetingPoint: 'Meeting point and instructions',
      showMeetingPoints: 'View meeting point and instructions',
      hideMeetingPoints: 'Hide meeting point and instructions',
      operatingHours: 'Service operating hours',
      allDay: '24 hours',
      days: {
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun',
      },
      providerFilter: 'Provider',
      categoryFilter: 'Vehicle',
      capacityFilter: 'Passengers',
      conditionsFilter: 'Conditions',
      freeCancellationFilter: 'Free cancellation',
      sortCapacity: 'Capacity',
      found_one: '{{count}} of {{total}} transfer',
      found_other: '{{count}} of {{total}} transfers',
      capacity_one: 'Up to {{count}} passenger',
      capacity_other: 'Up to {{count}} passengers',
      luggage_one: '{{count}} bag',
      luggage_other: '{{count}} bags',
      waiting_one: '{{count}} min waiting included',
      waiting_other: '{{count}} min waiting included',
      instant: 'Instant confirmation',
      freeCancellation: 'Free cancellation until {{date}}',
      perPassenger: '{{price}} per passenger',
      requestedRouteHint: 'Requested points: the provider did not confirm both endpoints.',
      resolvedRouteWithoutLabelsHint:
        'The provider confirmed the locations but did not publish their names; requested points are shown.',
      resolvedRoute: 'Address confirmed by the provider: {{from}} → {{to}}',
      resolvedPointHint:
        'The provider resolved the point to the address shown above: confirm it with the passenger before booking.',
      notAvailable: 'Unavailable',
      incompleteRoundTrip: {
        'outbound-only':
          'This fare covers the outbound leg only; it cannot be booked as a round trip.',
        'return-only':
          'This fare covers the return leg only; it cannot be booked as a round trip.',
        missing:
          'The offer does not identify both legs; it cannot be booked as a round trip.',
      },
      incompleteRoundTripModal: {
        'outbound-only':
          'We cannot confirm this option as a round trip: the offer only contains the outbound leg. Choose another fare or search for two separate transfers.',
        'return-only':
          'We cannot confirm this option as a round trip: the offer only contains the return leg. Choose another fare or search for two separate transfers.',
        missing:
          'We cannot confirm this option as a round trip because the offer does not identify both legs. Choose another fare or search for two separate transfers.',
      },
      whenAndWho: 'When and who',
      tripType: 'Trip type',
      swap: 'Swap origin and destination',
      swapWhileResolving:
        'Wait until the address finishes resolving to swap origin and destination.',
      oneWay: 'One way',
      roundTrip: 'Round trip',
      date: 'Date',
      endDate: 'Return date',
      dateInPast: 'That date is in the past: pick today or later.',
      returnBeforePickup: 'The return cannot be earlier than the outbound.',
      summaryFlightTime: 'flight {{time}}',
      summaryServiceTime: 'service {{time}}',
      summaryFlightNumber: 'Flight {{flight}}',
      summaryFrom: 'From',
      summaryTo: 'To',
      providerResolvedRoute: 'Route resolved by the provider',
      luggage: {
        change: 'Change luggage',
        pricedSelection: 'Quoted total includes the selected luggage and extras.',
        recoveryLocked: 'Luggage is locked while the booking attempt is resolved. Retrying preserves the quantities originally submitted.',
        confirmed: 'Confirmed group luggage',
        pieces: 'Capacity used: {{pieces}} pieces',
        capacity: 'Capacity used: {{pieces}} of {{capacity}} pieces',
        verified: 'Luggage and price verified by the provider',
        expires: 'Quote valid until {{date}}',
        freshOptions: 'Extras published with this quote (provider prices). Choose extras in the extras section.',
        quoting: 'Checking luggage, vehicle and price…',
        unavailable: 'Enter luggage and obtain a current quote before booking. If verification failed or expired, retry or search again.',
        rejected: 'The provider rejected this selection. Change luggage or extras, or return to results.',
        retry: 'Quote again',
        label: 'Total luggage for the group',
        later: 'I will specify it later',
        none: 'No luggage',
        specified: 'Specify luggage',
        checked: 'Checked bags',
        carryOn: 'Carry-on bags',
        invalid: 'Enter a whole number starting at 0.',
        increase: 'Increase {{label}}',
        decrease: 'Decrease {{label}}',
        requested: 'Declared luggage for the group',
        summary: '{{checked}} checked · {{carryOn}} carry-on',
      },
      originTime: 'Service time (outbound)',
      destinationTime: 'Service time (return)',
      originTimeFlight: 'Flight time (outbound)',
      destinationTimeFlight: 'Flight time (return)',
      flightTimeHint:
        'With an airport on the route, enter the flight time: arrival if the transfer leaves the airport, departure if it heads there. Without an airport, this is the service time at that end. Either way the provider works out the pickup time and each offer shows it.',
      adults: 'Adults',
      origin: 'Origin',
      destination: 'Destination',
      pointType: 'Point type',
      pointMode: 'Locate by',
      iataPoint: 'IATA point',
      placePoint: 'Place',
      pointSearch: 'Airport, hotel or address',
      pointPlaceholder: 'Type an airport, a hotel or an address…',
      pointNotice: {
        noSources: 'Points cannot be searched right now. Enter the point manually to continue.',
        placesDisabled:
          'No transfer providers configured: only hotels and addresses can be searched.',
        placesError:
          'Airports, stations and ports could not be searched. You can still search a hotel or an address.',
        addressesNotEnabled:
          'Hotel and address search is unavailable right now: only airports, stations and ports are listed.',
        addressesBudgetExceeded:
          "Today's address search limit has been reached: only airports, stations and ports are listed.",
        addressesError:
          'Hotels and addresses could not be searched. Pick an airport, a station or a port, or enter the point manually.',
      },
      coordinates: 'Coordinates',
      airport: 'Airport',
      hotel: 'Hotel',
      station: 'Station',
      port: 'Port',
      name: 'Name',
      address: 'Address',
      town: 'Address city',
      country: 'Address country',
      zip: 'Postal code',
      completeAddress: 'Complete address',
      postalAddressHint: 'Complete the city, country and postal code: some providers require them to book this location.',
      geocode: {
        resolving: 'Resolving address…',
        resolveError: 'Could not resolve that address. Try again.',
        useManualFallback: 'Enter address by hand',
        searchAgain: 'Search by name',
      },
    },
  },
  vouchers: {
    config: {
      tabLabel: 'Vouchers',
      title: 'Vouchers',
      intro:
        'What gets printed on the documents your passengers receive: the agency identity and its texts.',
      loadFailed: 'The voucher settings could not be loaded.',
      anchors: {
        identity: 'Identity',
        texts: 'Texts',
        byVertical: 'By product type',
        byProvider: 'By provider',
      },
      preview: {
        title: 'Preview',
        seeAs: 'See as',
        savedIdentity: 'It draws with the SAVED logo and colour: save the identity to see it here.',
        empty: 'Type something or pick a product type to see the document.',
        open: 'Open PDF',
        retry: 'Try again',
        unsupported: 'There is no voucher for this product type yet.',
        needsLogo: 'Upload the agency logo to see the document header.',
        notReady: 'The sample document cannot be drawn yet.',
        hasIssues: 'Fix the flagged texts to draw it again.',
        failed: 'The preview could not be drawn. Try again.',
      },
      identity: {
        appliesToAll:
          'These details appear on ALL of the agency documents, for every product.',
        legalName: 'Legal name',
        legalNameHint: 'The legal name, as printed in the footer',
        taxId: 'Tax ID',
        taxIdHint: 'Exactly as printed',
        phone: 'Phone',
        phoneHint: 'The commercial one, for enquiries',
        email: 'Email',
        emailHint: 'Where the passenger can write',
        website: 'Website',
        websiteHint: 'Including https://',
        address: 'Address',
        addressHint: 'The one printed in the document footer',
        assistancePhone: 'Assistance phone',
        assistancePhoneHint: 'The one the passenger calls WHILE TRAVELLING, not the commercial one',
        headerColor: 'Header colour',
        headerColorHint: 'The negative logo sits on this colour',
        headerColorEnabled: 'Use a header colour',
        headerColorOn:
          'Header with a band of this colour and the NEGATIVE logo on top',
        headerColorOff: 'Plain header, with the regular logo',
        tooLong: 'At most {{max}} characters',
        emailInvalid: 'It must be a valid email, or empty',
        colorInvalid: 'It must be #RRGGBB, or empty',
      },
      issuesIn: {
        identity: 'The problem holding Save is in Identity.',
        texts: 'The problem holding Save is in Texts.',
        byVertical: 'The problem holding Save is in By product type.',
        byProvider: 'The problem holding Save is in By provider.',
      },
      rejectedIn: {
        texts: 'Nothing was saved: a text in Texts was rejected.',
        byVertical: 'Nothing was saved: a text in By product type was rejected.',
        byProvider: 'Nothing was saved: a text in By provider was rejected.',
      },
      byVertical: {
        appliesTo:
          'This text is ADDED to the agency one on documents for that product type. Those without their own text use only the agency one.',
        hint: 'It prints below the welcome, before the terms.',
        hasOwn: 'Has its own text',
        usesAgency: 'Uses the agency one',
        agencyOnly: 'No text of its own',
        edit: 'Write the text',
        close: 'Close',
      },
      byProvider: {
        appliesTo:
          "A provider's text only appears on that provider's bookings, below the text of its product type or instead of it.",
        hint: 'It prints after the product type text, before the terms.',
        append: 'Added below',
        replace: 'Replaces',
        appendHelp: 'It prints below the product type text.',
        replaceHelp: 'It prints instead of the product type text, which is left out of these bookings.',
        mixed: 'Differs by language',
        mixedHelp: 'Each language prints with a different mode. Pick one and it is saved for both.',
        modeNeedsText: 'Write the text to choose how it prints.',
        noText: 'No text of its own',
        notConfigured: 'Not configured in Rutero',
        notConfiguredHelp: 'Rutero does not have it configured for the agency: the text can only be cleared.',
        rejectedShort: 'Not configured in Rutero',
        rejectedAsUnknown:
          'Rutero no longer has this provider configured for the agency. Clear the text to be able to save.',
        loading: 'Reading the Rutero providers…',
        loadFailed: 'The Rutero provider list could not be read.',
        retry: 'Try again',
        empty: 'Rutero publishes no providers for this agency.',
        hubUnavailable:
          'Nothing was saved: Rutero did not answer while checking the providers. Try again in a few minutes.',
        edit: 'Write the text',
        close: 'Close',
      },
      texts: {
        appliesToAll:
          'These texts open and close ALL of the agency documents, for every product.',
        format:
          'Formatting: **bold**, bullets with "- " and links [text](https://…). Any other markup is printed as typed, so it is not accepted.',
        emptyRemoves: 'Clearing it removes it from the document.',
        counter: '{{count}} of {{max}} characters',
        intro: 'Welcome',
        introHint: 'Opens the document, above the service details.',
        footer: 'Terms and footer',
        footerHint: 'Closes the document, below everything else.',
        rejected: 'Nothing was saved. Check the texts flagged below.',
        otherRejected:
          'The backend rejected a text this screen does not show. Check the texts by product type or by provider.',
        lang: {
          es: 'Español',
          en: 'English',
          enEmpty: 'English · empty, uses Spanish',
        },
        issues: {
          too_long: 'Over the {{max}} character limit',
          unsupported_markup:
            'Contains markup the document cannot draw: only bold, bullets and links',
          unsupported_link: 'Links must be [text](https://…)',
          unbalanced_emphasis: 'There is an unclosed bold mark',
        },
      },
      logo: {
        primary: 'Logo',
        primaryHint: 'Shown in the document header',
        negative: 'Negative logo',
        negativeHint: 'The version for dark backgrounds, on the header colour',
        empty: 'This logo is not uploaded yet',
        upload: 'Upload image',
        done: 'Logo updated',
        rejected: 'That image could not be used.',
        failed: 'The logo could not be uploaded. Try again.',
        tooBig: 'The image is too heavy. Try a lighter one.',
      },
      save: {
        action: 'Save',
        done: 'Settings saved',
        failed: 'The settings could not be saved.',
        needsAdmin: 'Only an agency administrator can edit this.',
        saving: 'Saving…',
        hasIssues: 'Check the highlighted fields before saving.',
        noChanges: 'There are no changes to save.',
        notLoaded:
          'The settings could not be read yet: reload the page before saving.',
      },
      unsaved: {
        title: 'You have unsaved changes',
        body: 'If you leave now the identity and text changes are lost. Logos already uploaded stay as they are.',
        leave: 'Leave without saving',
        stay: 'Keep editing',
      },
    },
  },
  rutero: {
    tabLabel: 'Ground services',
    settingsTitle: 'Ground services (Rutero)',
    pageIntro:
      'Search and booking for tours, transfers, circuits, rail, ferries, cars and eSIMs. Each provider needs its own credentials: without credentials, the provider does not show up in searches for this agency.',
    enabledLabel: 'Enable ground services for this agency',
    voucherAutoEmailLabel:
      'Email the voucher on confirmation (requires ground services enabled)',
    voucherAutoEmailHint:
      'It goes out on its own to the booking contact, with the PDF attached, only once the operator confirms too. The advisor sees it in the detail and can resend it by hand.',
    saveSuccess: 'Settings saved',
    saveError: 'Could not save settings',
    connectors: {
      title: 'Providers',
      intro:
        'Credentials are stored encrypted in the hub and never shown again. Saving replaces the full credential set for the provider.',
      provider: 'Provider',
      connector: 'Connector',
      vertical: 'Product',
      status: 'Status',
      actions: 'Actions',
      configured: 'Configured',
      disabled: 'Disabled',
      notConfigured: 'Not configured',
      configure: 'Configure',
      replace: 'Replace',
      delete: 'Delete',
      cancel: 'Cancel',
      save: 'Save',
      enabledLabel: 'Enabled',
      formTitle: '{{connector}} credentials',
      integrationName: 'Shared account (integration name)',
      integrationEnvironment: 'Account environment',
      integrationNotice:
        'Use the same name for agencies sharing a Transferz account. TEST and PROD require different names. The webhook secret is shared; rotation is managed separately. A published path does not prove event reception.',
      integrationConflict:
        'The name, environment or secret conflicts with the shared integration. Changes were not saved; review the configuration with the administrator.',
      webhookPath: 'Webhook path (relative to the public API origin)',
      webhookSecret: 'HMAC secret (optional; empty preserves the existing value)',
      secretConfigured: 'Secret configured',
      secretPending: 'Secret pending',
      writeOnlyNotice:
        'This provider already has stored credentials. For security they are never shown: fill in every field to replace them.',
      unsupported:
        'The hub offers providers this screen cannot configure yet: {{providers}}. Until they are added to the catalog they do not take part in searches.',
      loadError: 'Could not load providers',
      saveSuccess: 'Credentials saved',
      saveError: 'Could not save credentials',
      deleteConfirmTitle: 'Delete credentials',
      deleteConfirmBody:
        'This removes the {{connector}} credentials for this agency: the provider will stop showing up in searches. Continue?',
      deleteSuccess: 'Credentials deleted',
      deleteError: 'Could not delete credentials',
    },
    verticals: {
      rail: 'Rail',
      buses: 'Buses',
      ferry: 'Ferries',
      tours: 'Tours',
      circuits: 'Circuits',
      transfers: 'Transfers',
      cars: 'Cars',
      esims: 'eSIMs',
      passes: 'Rail passes',
    },
  },
  copilot: {
    panel: {
      title: 'AI Copilot',
      offline: 'offline',
      noSuggestions: 'No suggestions yet',
      updateClientName: 'Update client name',
      updateCustomField: 'Update field',
      suggestReply: 'Suggested reply',
      useReply: 'Use',
      interestScore: 'Interest score',
      salesCoaching: 'Sales coaching',
      apply: 'Apply',
      dismiss: 'Dismiss',
      dismissError: 'Could not dismiss the suggestion',
      applied: 'Suggestion applied',
      applyError: 'Could not apply the suggestion',
      requestSuggestion: 'Request suggestion',
      requestError: 'Could not request the suggestion',
    },
    tabLabel: 'AI Copilot',
    settingsTitle: 'AI Copilot',
    apiKeyConfigured:
      'Copilot API key configured. Fill the field only if you want to change it.',
    credentialsTitle: 'Credentials & agency context',
    pageIntro:
      'Configure the copilot in two layers: agency-wide credentials and context below, and the copilot behaviour per workflow/step further down.',
    configs: {
      title: 'Copilot configurations',
      subtitle: 'Enable copilot capabilities per workflow and step.',
      createCta: 'New configuration',
      empty: 'No configurations yet.',
      allWorkflows: 'All workflows',
      allSteps: 'All steps',
      moreSteps: '+{{count}} more',
      confirmDelete: 'Delete this configuration?',
      deleteSuccess: 'Configuration deleted',
      deleteError: 'Could not delete the configuration',
      saveSuccess: 'Configuration saved',
      saveError: 'Could not save the configuration',
      modal: {
        createTitle: 'New copilot configuration',
        editTitle: 'Edit copilot configuration',
      },
      table: {
        actions: 'Actions',
        workflow: 'Workflow',
        steps: 'Steps',
        capabilities: 'Capabilities',
        enabled: 'Enabled',
      },
      form: {
        enabled: 'Enabled',
        workflow: 'Workflow',
        allWorkflows: 'All workflows',
        steps: 'Steps',
        allSteps: 'All steps',
        capabilities: 'Capabilities',
        detectClientName: 'Detect client name',
        detectCustomFields: 'Detect travel data',
        analyzeInterestScore: 'Interest score',
        suggestReplies: 'Suggest replies',
        salesCoaching: 'Sales coaching',
        useExternalApis: 'Use external APIs',
        stepContext: 'Step context & instructions',
        stepContextPlaceholder: 'What should the agent achieve in this step?',
        stepContextHelper:
          'Supports Markdown. Describe what the agent should achieve in this step, what data to collect, and when to suggest closing.',
        passiveModel: 'Passive model (per message)',
        passiveModelHelper:
          'Runs automatically on every message. Use a fast, cheap model (e.g. gpt-4o-mini). Leave empty to use the agency default.',
        activeModel: 'Active model (on-demand)',
        activeModelHelper:
          'Runs when the agent requests suggestions on-demand. Use a more capable model (e.g. gpt-4o). Leave empty to use the agency default.',
        externalApiFacets: 'Enabled external APIs',
        externalApiFacetsPlaceholder: 'Empty = all',
        externalApiFacetsHelper:
          'Which travel facets are offered (on-demand buttons + automatic) in this workflow/step. Empty = all.',
        targetCustomFields: 'Fields to capture in this step',
        targetCustomFieldsPlaceholder: 'Empty = all',
        targetCustomFieldsHelper:
          'The copilot only suggests capturing these fields and steers the reply toward the next missing one. Empty = detect any field.',
        create: 'Create',
        update: 'Update',
      },
    },
    travelIntelligence: {
      settings: {
        title: 'Travel intelligence (external API)',
        intro:
          'Brings travel context (weather, documentation, packages, risk, etc.) to assist the agent. Choose in which steps it is used from each copilot configuration ("Use external APIs" toggle).',
        enable: 'Enable external API',
        apiKeyConfigured:
          'API key configured. Fill the field only if you want to change it.',
        apiKey: 'API key',
        businessUnit: 'Business unit (e.g. UY)',
        currency: 'Currency (ISO, e.g. USD)',
        originCountry: 'Origin country (ISO, e.g. UY)',
        passportCountry: 'Passport country (ISO, e.g. UY)',
        save: 'Save',
        saveSuccess: 'Travel intelligence saved',
        saveError: 'Could not save',
      },
      facets: {
        weather: 'Weather',
        documentation: 'Documentation',
        money: 'Currency',
        packages: 'Packages',
        highlights: 'What to see',
        events: 'Events',
        risk: 'Alerts',
        socialProof: 'Reviews',
      },
      fields: {
        destination: 'Destination (city, e.g. Cancún)',
        packageDestination: 'Package destination code (e.g. CUN-Cancún)',
        businessUnit: 'Business unit',
        originCountry: 'Origin country (ISO code)',
        passportCountry: 'Passport country (ISO code)',
        travelDate: 'Travel date',
        category: 'Place type',
      },
      categories: {
        attractions: 'Attractions',
        restaurants: 'Restaurants',
        hotels: 'Hotels',
        all: 'All',
      },
      cards: {
        packages: 'Packages',
        weather: 'Weather',
        documentation: 'Documentation',
        money: 'Currency',
        highlights: 'What to see',
        destination_summary: 'Destination',
        social_proof: 'Reviews',
        events: 'Events',
        risk: 'Alerts',
        fallback: 'Travel intelligence',
      },
      riskLevels: {
        none_detected: 'No alerts',
        low: 'Low risk',
        medium: 'Medium risk',
        high: 'High risk',
      },
      bar: {
        noResults: 'No {{facet}} results for this destination/dates',
        throttled: 'Try again in a moment',
        requestError: 'Could not fetch',
        missing: 'For "{{facet}}" we need:',
        consult: 'Fetch',
      },
      card: {
        source: 'Source: {{source}}',
        seeMore: 'See more',
        seeLess: 'See less',
        viewLink: 'View',
        linkSource: 'Source',
      },
      chips: {
        packagesCount: '{{count}} packages',
        packagesFrom: 'from {{currency}} {{price}}',
        stay: 'Stay: {{duration}}',
        money: '1 {{base}} ≈ {{rate}} {{target}}',
        temps: '🌡 {{max}}° / {{min}}°',
        rainyDays: '🌧 {{pct}}% rainy days',
        activeAlerts: '{{count}} active alerts',
        places: '{{count}} places',
        events: '{{count}} events',
      },
    },
  },
  filters: {
    filterBy: 'Filter by',
    clear: 'Clear',
    clearAll: 'Clear all',
    namePlaceholder: 'Write a name',
    nameLabel: 'Filter name',
    isPublic: 'Public (everyone would see it)',
    createFilter: 'Create Filter',
    filterList: 'Filter list',
    editFilter: 'Edit Filter',
    deleteFilter: 'Delete Filter',
    public: 'Public',
    private: 'Private',
    createdSuccess: 'Filter created',
    createdError: 'There was an error while creating your filter',
    deletedSuccess: 'Filter deleted successfully',
    deletedError: 'There was an error while deleting your filter',
    deleteConfirmTitle: 'Delete Filter',
    deleteConfirmMessage: 'Are you sure you want to delete this filter? This action cannot be undone.',
    priceRange: 'Price range',
    searchName: 'Search by name',
    hotelChains: 'Hotel Chains',
    categories: 'Categories',
    amenities: 'Amenities',
    myClients: 'View only my clients',
    myCases: 'View only my cases',
    date: {
      today: 'Today',
      yesterday: 'Yesterday',
      thisWeek: 'This week',
      thisMonth: 'This month',
      lastWeek: 'Last week',
      lastMonth: 'Last month',
      lastNDays: 'Last N days',
      tomorrow: 'Tomorrow',
      nextWeek: 'Next week',
      nextMonth: 'Next month',
      nextNDays: 'Next N days',
      specificDate: 'Specific date',
      dateRange: 'Date range',
      numberOfDays: 'Number of days',
      from: 'From',
      to: 'To',
      selectDateType: 'Select date type',
    },
  },
  whatsapp: {
    chatLocation: {
      location: 'Location',
    },
    title: 'Whatsapp',
    systemSender: 'Agency',
    unknownDirection: 'Unknown direction',
    replyToThisMessage: 'Reply to this message',
    linkedCases: 'Linked cases',
    noMessagesFound: 'No messages found..',
    typeMessage: 'Type your message...',
    startWithTemplate: 'Start with a template',
    templateMessenger: {
      title: 'Send message from template',
      selectTemplate: 'Select a template',
      preview: 'Preview:',
      completeFields: 'Complete the fields:',
      placeholder: 'Placeholder {{number}}',
      sendMessage: 'Send message',
      fileSelected: 'File selected',
      fileRequired: 'A file is required for this template',
      uploadFile: 'Upload file',
      noFileSelected: 'No file selected',
      selectFile: 'Select file',
    },
    results: 'results',
    table: {
      clientName: 'Client name',
      fromNumber: 'Phone number',
      conversation: 'Conversation',
      agency: 'Agency',
      msgType: 'Message type',
      expiration: 'Expiration',
    },
    client: {
      lastCases: 'Last Cases',
      noCases: 'No cases to show',
      conversation: 'Whatsapp Conversation',
      searchPlaceholder: 'Search in conversation',
      writeMessage: 'Write a message',
      sendTemplate: 'Send Template',
      aiEnabled: 'AI Enabled',
      sentTo: 'sent to',
    },
    content: {
      whatsapp: 'Whatsapp',
      email: 'Email',
      phone: 'Phone',
      files: 'Files',
      notes: 'Notes',
      history: 'History',
      all: 'All',
      show: 'Show',
      search: 'Search...',
      viewMore: 'View more',
      now: 'Now',
      minutesAgo: '{{minutes}} minutes ago',
      sessionExpired: 'Session expired',
      timeLeft: 'Time left',
    },
    messageTypes: {
      text: 'Text',
      contacts: 'Contacts',
      template: 'Template',
      sticker: 'Sticker',
      audio: 'Audio',
      video: 'Video',
      image: 'Image',
      document: 'Document',
      location: 'Location',
    },
    emoji: {
      categories: {
        favoritos: 'Favorites',
        smileys_emotion: 'Smileys & Emotion',
        people_body: 'People & Body',
        travel_places: 'Travel & Places',
        food_drink: 'Food & Drink',
        activities: 'Activities',
        objects: 'Objects',
        symbols: 'Symbols',
        flags: 'Flags',
      },
      names: {
        airplane: 'airplane',
        globe: 'globe',
        telephone: 'telephone',
        speech_balloon: 'speech balloon',
        handshake: 'handshake',
        smiling_face: 'smiling face',
        face_with_hearts: 'face with hearts',
        hugging_face: 'hugging face',
        star_struck: 'star-struck',
        folded_hands: 'folded hands',
        thumbs_up: 'thumbs up',
        raising_hands: 'raising hands',
        briefcase: 'briefcase',
        incoming_envelope: 'incoming envelope',
        rocket: 'rocket',
        check_mark: 'check mark',
        alarm_clock: 'alarm clock',
        dollar_sign: 'dollar sign',
        red_heart: 'red heart',
        party_popper: 'party popper',
        grinning_face: 'grinning face',
        beaming_face: 'beaming face',
        face_with_tears: 'face with tears',
        grinning_face_with_sweat: 'grinning face with sweat',
        smiling_face_with_sunglasses: 'smiling face with sunglasses',
        thinking_face: 'thinking face',
        face_with_rolling_eyes: 'face with rolling eyes',
        crying_face: 'crying face',
        smiling_face_with_heart_eyes: 'smiling face with heart-eyes',
        pouting_face: 'pouting face',
        sleeping_face: 'sleeping face',
        partying_face: 'partying face',
        waving_hand: 'waving hand',
        thumbs_down: 'thumbs down',
        open_hands: 'open hands',
        clapping_hands: 'clapping hands',
        call_me_hand: 'call me hand',
        flexed_biceps: 'flexed biceps',
        car: 'car',
        bus: 'bus',
        train: 'train',
        sailboat: 'sailboat',
        passenger_ship: 'passenger ship',
        statue_of_liberty: 'Statue of Liberty',
        world_map: 'world map',
        eiffel_tower: 'Eiffel Tower',
        green_apple: 'green apple',
        hamburger: 'hamburger',
        pizza: 'pizza',
        sushi: 'sushi',
        steaming_bowl: 'steaming bowl',
        shortcake: 'shortcake',
        beer_mug: 'beer mug',
        hot_beverage: 'hot beverage',
        wine_glass: 'wine glass',
        bottle_with_popping_cork: 'bottle with popping cork',
        soccer_ball: 'soccer ball',
        basketball: 'basketball',
        american_football: 'american football',
        tennis: 'tennis',
        volleyball: 'volleyball',
        video_game: 'video game',
        musical_note: 'musical note',
        clapper_board: 'clapper board',
        artist_palette: 'artist palette',
        broken_heart: 'broken heart',
        hundred_points: 'hundred points',
        cross_mark: 'cross mark',
        warning: 'warning',
        information: 'information',
        bell: 'bell',
        ok_button: 'OK button',
        recycling_symbol: 'recycling symbol',
        uruguay_flag: 'flag: Uruguay',
        paraguay_flag: 'flag: Paraguay',
        bolivia_flag: 'flag: Bolivia',
        colombia_flag: 'flag: Colombia',
        usa_flag: 'flag: United States',
        brazil_flag: 'flag: Brazil',
        argentina_flag: 'flag: Argentina',
        mexico_flag: 'flag: Mexico',
        chile_flag: 'flag: Chile',
        spain_flag: 'flag: Spain',
        star: 'star',
        glowing_star: 'glowing star',
        sparkles: 'sparkles',
        dizzy: 'dizzy',
        shooting_star: 'shooting star',
      },
      selectEmoji: 'Choose an emoji',
      searchPlaceholder: 'Type here to search',
    },
    messageStatus: {
      [WhatsappMessageEventStatusType.REQUESTED]: 'requested',
      [WhatsappMessageEventStatusType.ENQUEUED]: 'queued',
      [WhatsappMessageEventStatusType.FAILED]: 'failed',
      [WhatsappMessageEventStatusType.SENT]: 'sent',
      [WhatsappMessageEventStatusType.DELIVERED]: 'delivered',
      [WhatsappMessageEventStatusType.READ]: 'read',
    },
    settings: {
      title: 'Gupshup settings',
      createNewSubscription: 'Create new subscription',
      tag: 'Tag',
      url: 'URL',
      version: 'Version',
      showOnUI: 'Show on UI',
      modes: 'Modes',
      templates: {
        title: 'Templates',
        searchLabel: 'Search templates',
        searchPlaceholder: 'Search templates',
        empty: 'No templates found.',
        table: {
          id: 'Template ID',
          name: 'Template Name',
          enabled: 'Enabled',
          actions: 'Actions',
        },
        actions: {
          edit: 'Edit template placeholders',
        },
        dialog: {
          title: 'Edit template placeholders',
          templateText: 'Original template text',
          placeholderMappings: 'Placeholder mappings',
          placeholderHelp:
            'Each select maps to the matching positional placeholder in the original template text.',
          placeholderLabel: 'Placeholder {{number}}',
          unassigned: 'Unassigned',
          noPlaceholders: 'This template has no positional placeholders.',
        },
        messages: {
          updatedSuccess: 'Template placeholder mappings updated successfully.',
          saveError: 'There was an error saving the template placeholder mappings.',
        },
      },
      quickReplies: {
        title: 'Quick replies',
        searchLabel: 'Search quick replies',
        searchPlaceholder: 'Search by slug, name, or body',
        empty: 'No quick replies created yet.',
        emptySearch: 'No quick replies match your search.',
        table: {
          slug: 'Slug',
          name: 'Name',
          preview: 'Preview',
          enabled: 'Enabled',
          actions: 'Actions',
        },
        actions: {
          create: 'Create',
          edit: 'Edit quick reply',
          delete: 'Delete quick reply',
        },
        form: {
          createTitle: 'Create quick reply',
          editTitle: 'Edit quick reply',
          slug: 'Slug',
          slugPlaceholder: 'booking-update',
          slugHelper: 'Use lowercase kebab-case only. Maximum 64 characters.',
          slugInvalid:
            'Use lowercase kebab-case only, with letters or numbers separated by hyphens.',
          name: 'Name',
          namePlaceholder: 'Booking update',
          body: 'Body',
          bodyPlaceholder: 'Write the saved WhatsApp response here...',
          bodyHelper:
            'Multiline text is supported. Maximum 2000 characters.',
          enabled: 'Enabled',
        },
        deleteDialog: {
          title: 'Delete quick reply',
          description:
            'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        },
        help: {
          title: 'Available template variables',
          intro:
            'These variables can be used in WhatsApp quick replies and are rendered from case context.',
          note:
            'Missing values render as empty text, so keep your messages readable without every variable.',
          exampleLabel: 'Example',
          exampleValue:
            'Hello {{client_first_name}}, welcome to Mevuelo.',
          placeholders: {
            clientName: {
              token: '{{client_name}}',
              description: 'Full client name.',
            },
            clientFirstName: {
              token: '{{client_first_name}}',
              description: 'Client first name.',
            },
            clientLastName: {
              token: '{{client_last_name}}',
              description: 'Client last name.',
            },
            agentName: {
              token: '{{agent_name}}',
              description: 'Full agent name.',
            },
            agentFirstName: {
              token: '{{agent_first_name}}',
              description: 'Agent first name.',
            },
            agentLastName: {
              token: '{{agent_last_name}}',
              description: 'Agent last name.',
            },
          },
        },
        messages: {
          loadError: 'There was an error loading quick replies.',
          createdSuccess: 'Quick reply created successfully.',
          updatedSuccess: 'Quick reply updated successfully.',
          deletedSuccess: 'Quick reply deleted successfully.',
          saveError: 'There was an error saving the quick reply.',
          deleteError: 'There was an error deleting the quick reply.',
          toggleError: 'There was an error updating the quick reply status.',
        },
      },
    },
    slashCommands: {
      emptyCommands: 'No commands match your slash search.',
      commands: {
        quickReplies: {
          label: 'Quick replies',
          description:
            'Insert a saved WhatsApp response into the composer for review before sending.',
          aliases: {
            english: '/quick-reply',
            spanish: '/respuesta-rapida',
            shortEnglish: '/qr',
            shortSpanish: '/rp',
          },
        },
      },
      quickReplies: {
        loading: 'Loading quick replies...',
        empty: 'No enabled quick replies found.',
        requiresCase: 'Open a case to use quick replies here.',
        renderError: 'There was an error inserting the quick reply.',
      },
    },
    iaReasoning: {
      viewReasoning: 'View AI Reasoning',
      hideDetails: 'Hide details',
      viewCompleteAnalysis: 'View complete analysis',
      contextAnalysis: 'Context Analysis',
    },
  },
  userNotificationTriggers: {
    title: 'Notification Management',
    createTrigger: 'Create Trigger',
    editTrigger: 'Edit Trigger',
    deleteTrigger: 'Delete Trigger',
    trigger: 'Trigger',
    active: 'Active',
    noConditions: 'No specific conditions',
    client: 'Client',
    clientOptional: 'Client (optional)',
    agent: 'Agent',
    agentOptional: 'Agent (optional)',
    workflow: 'Workflow',
    step: 'Step',
    steps: 'steps',
    status: 'Status',
    case: 'Case',
    specificCase: 'Specific case (optional)',
    deliveryChannels: 'Delivery channels',
    assigneeScope: {
      label: 'Case assignment',
      any: 'Any assignment',
      mine: 'My cases',
      team: 'My teams’ cases',
      unassigned: 'Unassigned cases',
    },
    invalidStages: 'This rule has no valid stages and cannot be enabled.',
    quietHours: {
      label: 'Quiet hours',
      start: 'From',
      end: 'Until',
      timezone: 'Time zone',
      help: 'Leave both times empty to receive alerts all day. Overnight ranges are supported.',
      disabled: 'No quiet hours',
    },
    devices: {
      title: 'Notification devices',
      help: 'Each device receives alerts for cases in all of your Inbox-enabled agencies.',
      unnamed: 'Unnamed device',
      current: 'This device',
      coverage: 'Agencies: {{agencies}}',
      noAgencies: 'no enabled agencies',
      lastRenewed: 'Renewed: {{date}}',
      suppressWhenActive: 'Suppress this device while I am using it',
      rename: 'Rename device',
      label: 'Device name',
      save: 'Save name',
      cancel: 'Cancel editing',
      test: 'Test',
      testResult: {
        delivered: 'The server sent the test to this device.',
        expired: 'The subscription had expired and was revoked. Enable notifications again from this device.',
        failed: 'The test could not be delivered. Check the server configuration or renew the subscription.',
      },
      revoke: 'Revoke device',
    },
    deliveryChannelsHelp:
      'In-app and email are enabled by default. Google Chat DM stays opt-in and only works when the agency has configured it.',
    noDeliveryChannels: 'No delivery channels',
    addWorkflow: 'Add workflow',
    addWorkflowHint: 'Select a workflow and at least one step to add another row.',
    removeWorkflow: 'Remove workflow',
    selectClient: 'Select client',
    selectAgent: 'Select agent',
    selectWorkflow: 'Select workflow(s)',
    selectStep: 'Select step',
    selectStatus: 'Select status',
    selectWorkflowFirst: 'First select a workflow',
    caseIdPlaceholder: 'Case ID',
    allClients: 'All clients',
    allAgents: 'All agents',
    anyStep: 'Any step',
    allWorkflows: 'All workflows',
    anyStatus: 'Any status',
    anyCase: 'Any case',
    formDescription:
      'Configure the conditions that will trigger a notification. At least one condition must be specified.',
    noTriggers: 'No triggers configured',
    noTriggersDescription: 'Create your first trigger to receive automatic notifications.',
    createFirst: 'Create first trigger',
    createdBy: 'Created by',
    confirmDelete: {
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this trigger? This action cannot be undone.',
      triggerDetails: 'Trigger Details',
      warning: 'This action will permanently delete the trigger and cannot be reverted.',
    },
    fields: {
      client: 'Client',
      agent: 'Agent',
      workflow: 'Workflow',
      step: 'Step',
      status: 'Status',
    },
    channels: {
      inApp: 'In-app',
      email: 'Email',
      googleChat: 'Google Chat DM',
    },
    channelAvailability: {
      emailUnavailable:
        'Email is unavailable for this agency. Configure it in Agency -> Communications.',
      googleChatUnavailable:
        'Google Chat DM is unavailable for this agency. Configure it in Agency -> Communications.',
    },
    createSuccess: 'Trigger created successfully',
    updateSuccess: 'Trigger updated successfully',
    deleteSuccess: 'Trigger deleted successfully',
    saveError: 'Error saving trigger',
    deleteError: 'Error deleting trigger',
    activatedSuccess: 'Trigger activated',
    deactivatedSuccess: 'Trigger deactivated',
    toggleError: 'Error toggling trigger status',
    validation: {
      workflowRequired: 'Workflow is required',
      stepsRequired: 'Select at least one step',
      statusRequired: 'Status is required',
    },
  },
  agencyAccess: {
    title: 'Agency Access',
    saveError: 'An error ocurrer while updating user agency access permissions',
  },
  updateClientAgency: {
    updateSuccess: 'Agency updated!',
    updateError: 'An error occurred while updating the agency',
    title: 'Reponsible Agency',
    inputLabel: 'Select Agency',
    confirm: 'Confirm Changes',
    dialog1: 'Changing the user agency will trigger several changes',
    dialog2: 'User will not be part of the previous agency teams he might have been part of',
    dialog3: 'All cases beloging to this user will change the ownership to unassigned',
    dialog4: 'Are you sure you want to proceed?',
  },
  agentAvailability: {
    title: 'Available agents',
    status: 'Status',
    agent: 'Agent',
    since: 'Since',
  },
  agentStatus: {
    [AgentStatus.AFTER_CALL]: 'After Call',
    [AgentStatus.AVAILABLE]: 'Available',
    [AgentStatus.BREAK]: 'On Break',
    [AgentStatus.IN_CALL]: 'In Call',
    [AgentStatus.LUNCH]: 'At lunch',
    [AgentStatus.NOT_AVAILABLE]: 'Do not disturb',
    [AgentStatus.NOT_APPLICABLE]: 'Not Applicable',
    [AgentStatus.WORKING_IN_CASE]: 'Working in a case',
    [AgentStatus.DISCONNECTED]: 'Disconnected',
  },
  roles: {
    title: 'Roles',
    [UserRoles.ADMINS]: 'Administrator',
    [UserRoles.AGENTS]: 'Agent',
    [UserRoles.OPERATORS]: 'Operator',
    [UserRoles.REGISTERED]: 'Registered',
    [UserRoles.HOTEL_CONTENT_MODERATOR]: 'Hotel content moderator',
    [UserRoles.EXTERNAL_INTEGRATION]: 'External integration',
    [UserRoles.TESTER]: 'Tester',
    [UserRoles.HOTEL_BOOKER]: 'Hotel Booker',
  },
  calls: {
    subtitle: 'System calls list',
    table: {
      title: 'Calls',
      type: 'Type',
      from: 'From',
      to: 'To',
      agent: 'Agent',
      status: 'Status',
      duration: 'Duration',
      inbound: 'Inbound',
      outbound: 'Outbound',
      notAssigned: 'Not assigned',
    },
    status: {
      [CallStatus.COMPLETED]: 'Completed',
      [CallStatus.IN_PROGRESS]: 'In progress',
      [CallStatus.DECLINED]: 'Declined',
    },
    incoming: {
      title: 'Incoming call',
      callFrom: 'Call from {{phoneNumber}}',
      unknown: 'Unknown',
      contentSubtitle: 'Is calling you',
      answer: 'Answer',
      phone: 'Phone',
      decline: 'Decline',
      transfer: {
        title: 'Transfer call',
        searchPlaceholder: 'Search user',
        freqUsers: 'Frequent users',
        calling: 'Calling...',
        joined: 'In progress',
      },
    },
    interaction: {
      title: 'Enable call reception',
      description: 'To receive calls and play sounds automatically, you need to click anywhere on the screen.',
      enable: 'Enable calls',
      enable_audio: 'Enable sound',
      banner_message: 'Click here to enable incoming call sounds. You can also click anywhere on the page.',
    },
    assignment: {
      title: 'Call details',
      search: 'Search case',
      newCase: 'New case',
      caseId: 'Case ID',
      success: 'Call assigned successfully',
      error: 'Could not link this call to the selected case. Please try again.',
      missingCallId:
        "We couldn't find the call information needed to link this call. Please reopen the call or refresh the page.",
      flow: 'Flow',
      client: 'Client',
      clientUnassigned: 'Unassigned',
      step: 'Step',
      status: 'Status',
      team: 'Team',
      assignedTo: 'Assigned agent',
      link: 'Link to this case',
      create: {
        title: 'Create new case',
        cancel: 'Cancel',
        accept: 'Create',
        subject: 'Case subject',
        unassigned: 'Unassigned',
        warning: 'The current call will be associated with this new case',
        missingCallId:
          "We couldn't find the call information needed to create the case. Please reopen the call or refresh the page.",
        error: 'Could not create the case for this call. Please try again.',
      },
      filters: {
        title: 'Filter by',
        team: 'Team',
        client: 'Client',
        agent: 'Agent',
        workflow: 'Workflow',
        step: 'Step',
      },
    },
    inProgress: { title: 'Call in progress', caseNumber: 'Case #{{caseId}}' },
  },
  supportForms: {
    list: {
      title: 'Support forms',
      subtitle: 'Support forms list',
      results: 'results',
      error: 'There was an error trying to get the support forms',
      delete: 'Confirm to delete: {{name}}',
      deleteTitle: 'Delete support form',
      deleted: 'Support form deleted',
      deleteError: 'There was an error trying to deletethe support form',
    },
    fields: {
      titleField: 'Title',
      workflowField: 'Workflow',
      descriptionField: 'Description',
      customLogoUrl: 'Custom logo URL',
      customLogoUrlHelper: "If not completed, default agency's logo will be used",
      finalUrl: 'Final url',
      name: 'Name',
      lastName: 'Last name',
      email: 'Email Address',
      emailSubject: '{{title}} {{code}} 🚀',
    },
    view: {
      titleCreate: 'Create Support Form',
      titleUpdate: 'Update Support Form',
      preview: 'Form preview',
      noWorkflows: 'No workflows found',
      saved: 'Support form saved correctly!',
      error: 'There was an error trying to save the support form',
    },
    generator: {
      title: 'Add custom fields',
      fieldType: 'Field type',
      optionsLabel: 'Options (coma separated)',
      label: 'Label',
      key: 'field name in form',
      isRequired: 'Is required',
      addField: 'Add field',
    },
    send: {
      success: 'Request successfuly sent',
      successMessage: 'We will be contacting you soon!',
      error: 'There was an error trying to send the request',
    },
    email: {
      title: 'A new case was open for: {{email}} with the following processed data:',
    },
  },
  auth: {
    login: {
      title: 'Log in to Me Vuelo',
      email: 'Email',
      password: 'Passowrd',
      forgotPassword: 'Forgot Password?',
      createAccountText: "Don't you have an account?",
      createAccountLink: 'Register',
      loginMesssage: 'Log in',
      logoutMesssage: 'Logout',
      loginGoogleMesssage: 'Login with Google',
      fieldErrorMessage: 'This field is required',
      error: 'Invalid Email or password',
    },
    forgotPassword: {
      title: 'Forgot password?',
      subtitle: "No worries, we'll send you a reset code",
      email: 'Enter your email',
      reset: 'Reset password',
      back: 'Go back',
      error: 'there was an error',
      sentEmail: 'Email sent',
    },
    resetPasswordCode: {
      title: 'Password reset',
      subtitle: 'We sent a code to',
      continue: 'Continue',
      back: 'Go back',
      error: 'There was an error',
    },
    resetPassword: {
      title: 'Set a new password',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm new password',
      save: 'Reset password',
      error: 'There was an error',
      success: 'Password reset success',
    },
    register: {
      title: 'Register to Me Vuelo',
      email: 'Email',
      password: 'Passowrd',
      confirm: 'Confirm passowrd',
      registerMesssage: 'Register',
      logInText: 'Already have an account?',
      logInLink: 'Log in',
      fieldErrorMessage: 'This field is required',
      userRegistered: 'User created: now log in',
      error: 'Email already exists in our database',
      errorMessage: 'Invalid Email or password!',
    },
    changePassword: {
      title: 'Change password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmNewPassword: 'Confirm password',
      passwordsDontMatch: "Passwords don't match",
      changePasswordError: 'There was an error while trying change the password',
      success: 'Password successfuly changed',
      invalidPassword: 'Invalid password',
    },
    editUser: {
      title: 'Edit personal data',
      subtitle: 'Please insert data as it is shown in your identification document',
      failMessage: 'There was an error while trying to apply changes',
      uploadPicture: 'Upload image',
      pictureFieldDescription: 'Add a photo. Maximum file size 3MB',

      firstName: {
        placeHolder: 'Names',
        errorMessage: 'Required field',
        helperText: 'As it shows in you indentification document',
      },
      lastName: {
        placeHolder: 'Lastnames',
        errorMessage: 'Required field',
        helperText: 'As it shows in you indentification document',
      },
      phoneCountryCode: {
        placeHolder: 'Area code',
        errorMessage: 'Required field',
      },
      phoneNumber: {
        placeHolder: 'Phone number',
        errorMessage: 'Required field',
      },
      phoneExtension: {
        placeHolder: 'Extension number',
        errorMessage: 'This failed',
        helperText: '(optional)',
      },
      position: {
        placeHolder: 'Position',
        errorMessage: 'Required field',
      },
      address1: {
        placeHolder: 'Address',
        errorMessage: 'Required field',
        helperText: 'Ingrese su direccion',
      },
      address2: {
        placeHolder: '',
        errorMessage: 'Required field',
        helperText: '2nda linea de direccion (opcional)',
      },
      country: {
        placeHolder: 'Country',
        errorMessage: 'Required field',
      },
      city: {
        placeHolder: 'City',
        errorMessage: 'Required field',
      },
      postalCode: {
        placeHolder: 'Postal Code',
        errorMessage: 'Required field',
      },
      documentType: {
        placeHolder: 'Document type',
        errorMessage: 'Required field',
        helperText: 'Tipo de documento',
      },
      documentNumber: {
        placeHolder: 'Document number',
        errorMessage: 'Required field',
        helperText: 'As it shows in you indentification document',
      },
      profilePicture: {
        errorMessage: 'There was an error uploading your photo.',
        fileTooLarge: 'File is too large. Maximum allowed size is 3 MB.',
        invalidFileFormat: 'File format not supported.',
      },
      applyChangesBtn: 'Apply',
      patchSuccess: 'User updated successfully!',
    },
  },
  noteEditor: {
    subject: 'Subject',
    save: 'Save',
    send: 'Send',
    to: 'To',
    cc: 'CC',
    bcc: 'BCC',
    from: 'From',
    emailTitle: 'New E-mail',
    noteTitle: 'New note',
    placeholder: 'Write something',
    noOptions: 'Results not found',
    controls: {
      minimize: 'Minimize',
      maximize: 'Full screen',
      close: 'Close',
      remove: 'Remove',
      bold: 'Bold',
      italic: 'Italic',
      underline: 'Underline',
      bulletList: 'Bullet list',
      attachments: 'Attachments',
      link: 'Link',
      image: 'Image',
      undo: 'Undo',
      redo: 'Redo',
    },
    errors: {
      subject: 'Subject is required',
      content: 'Content is required',
      email: 'Recipient is required',
      fileTooLarge: 'File is too large. Maximum allowed size is 3 MB.',
      fileNotAvailable: 'No file available',
    },
  },
  customFields: {
    title: 'Custom Fields',
    subtitle: 'Custom Fields list',
    fieldBlockCreate: {
      title: 'Create Field Block',
      name: 'Name',
      createdSuccess: 'Field block created',
      createdError: 'There was an error while creating the Field block',
    },
    fieldBlockEdit: {
      title: 'Edit Field Block',
      name: 'Name',
      editSuccess: 'Field block updated',
      editError: 'There was an error while updateing the Field block',
    },
  },
  pwa: {
    update: {
      available: 'A new version is available.',
      action: 'Update',
    },
  },
  common: {
    all: 'All',
    hours: 'hours',
    view: 'View',
    create: 'Create',
    delete: 'Delete',
    deleting: 'Deleting...',
    active: 'Active',
    inactive: 'Inactive',
    mode: 'Mode',
    count: 'Count',
    name: 'Name',
    noData: 'No data available',
    comingSoon: 'Coming soon',
    mandatoryField: 'This field is mandatory',
    invalidDate: 'Invalid date',
    add: 'Add',
    close: 'Close',
    loading: 'Saving…',
    retry: 'Retry',
    update: 'Update',
    continue: 'Continue',
    cheapest: 'Cheapest',
    nonEconomic: 'Non enconomic',
    recomended: 'recommended',
    orderBy: 'Order by:',
    results: 'Results',
    refresh: 'Refresh',
    refreshData: 'Refresh data',
    status: 'Status',
    step: 'Step',
    workflow: 'Workflow',
    channel: 'Channel',
    agent: 'Agent',
    updated: 'Updated',
    list: 'List',
    accept: 'Accept',
    confirmAction: 'Confirm Action',
    goBack: 'Go Back',
    warning: 'Warning',
    save: 'Save',
    today: 'Today',
    confirm: 'Confirm',
    cancel: 'Cancel',
    unassigned: 'Unassigned',
    yes: 'Yes',
    no: 'No',
    welcomeMessage: 'Hello world! this is an english welcome message for: ',
    filters: 'Filters',
    general: 'General',
    notValueFound: '-',
    na: 'N/A',
    new: 'New',
    search: 'Search',
    takeALook: 'Take a look',
    selectFile: 'Select file',
    tbd: 'TBD',
    maxSelectionsReached: 'Maximum selections reached',
    itemsSelected: 'items selected',
    readMore: 'Read more',
    showLess: 'Show less',
    currencies: {
      usd: 'USD',
    },
    actions: {
      add: 'Add',
      close: 'Close',
      cancel: 'Cancel',
      create: 'Create',
      save: 'Save',
      gptGeneration: 'Generate with GPT',
      gptAllGeneration: 'Generate all with GPT',
      open: 'Open',
      remove: 'Remove',
      send: 'Send',
      download: 'Download',
      edit: 'Edit',
    },
    form: {
      onlyLetters: 'Only letters',
      onlyNumbers: 'Only numbers',
      onlyLettersAndNumbers: 'Only letters and numbers',
      pressEnterToAdd: 'Press enter to add',
      invalidMail: 'Invalid email',
      duplicateMail: 'Email already added',
      apply: 'Apply',
      clear: 'Clear',
      required: 'This field is required',
      save: 'Save',
      discard: 'Discard',
      select: 'Select',
      delete: 'Delete',
      url: 'This filed is an URL',
    },
    nav: {
      searchNavigation: 'Search services',
      moreSearches: 'More search services',
      serviceUnavailable: 'This service is not available for the agency.',
      flights: 'Flights',
      hotels: 'Hotels',
      transfers: 'Transfers',
      experiences: 'Experiences',
      esims: 'eSIMs',
      rail: 'Rail',
      buses: 'Buses',
      ferry: 'Ferries',
      circuits: 'Circuits',
      passes: 'Passes',
      travelBookings: 'Bookings',
      cars: 'Cars',
      explorer: 'Explorer',
      dashboard: 'Dashboard',
      cases: 'Cases',
      inbox: 'Inbox',
      clients: 'Clients',
      saleOrders: 'Sale Orders',
      agencies: 'Agencies',
      teams: 'Teams',
      workflows: 'Workflows',
      whatsapp: 'Whatsapp',
      calls: 'Calls',
      users: 'Users',
      flightIntegrations: 'Flights',
      hotelIntegrations: 'Hotels',
      hotelContentManagement: 'Hotel Content',
      userMenu: {
        userIdentifier: 'Guest',
        myFavorites: 'My favorites',
        myNotifications: 'Notifications',
        myBookings: 'My Bookings',
        admin: 'Admin',
        routingRules: 'Routing Rules',
        markupRules: 'Markup Rules',
        userList: 'User list',
        contentManager: 'Content Manager',
        accountMessage: 'Account',
        currentCurrency: 'Currency',
        currentLanguage: 'Language',
        help: 'Help',
        selectedLanguage: 'English',
      },
    },
    footer: {
      companyType: 'Registered travel agency',
      identity: 'Real Consulting S.A. Resolution Registur N° 1263/2019',
      contactMessage: 'Contact Us',
      company: {
        title: 'Company',
        about: 'About Us',
        terms: 'Terms and Conditions',
        privacy: 'Privacy Policy',
        join: 'Join The team',
      },
      account: {
        title: 'Account',
        data: 'My Data',
        favorites: 'My Favorites',
        alerts: 'My Alerts',
        bookings: 'My Bookings',
        close: 'Log Out',
      },
      platform: {
        title: 'Platform',
        currency: 'Currency',
        language: 'Language',
        rates: 'Rates Tracker',
        rules: 'Markup Rules',
        travelers: 'My Travelers',
        airlines: 'ABM Airlines',
        cabines: 'ABM Cabins',
        alliances: 'ABM Alliances',
      },
      payments: {
        certification: {
          title: 'Safety certification',
          description: 'Mevuelo.com is certified by the council of security norms for your means of payment.',
        },
        intelligentTravel: {
          title: 'Travel smart and spend less',
          description:
            'Discover unbeatable prices on flights, hotels and cars, making every trip an economic adventure.',
        },
        explore: {
          title: 'Explore More, Worry Less',
          description:
            'With Mevuelo, traveling is easier than ever. Customize your trip as you wish, pack your dreams, and we take care of the rest.',
        },
        paymentMethods: {
          title: 'Pay with the method you like the most',
          credit: 'Credit Cards',
          bankTransfers: 'Bank Transfers',
          cash: 'Cash at any of our branches',
        },
        altImages: {
          visa: 'Visa card logo',
          oca: 'OCA card logo',
          mastercard: 'Mastercard card logo',
          american: 'American card logo',
          securityEncryption: 'Safety box image',
          wallet: 'Wallet image',
          backpack: 'Backpack image',
          card: 'Card icon',
          bank: 'Bank icon',
          cash: 'Money icon',
        },
      },
      help: {
        title: 'Help',
        faq: "FAQ's",
      },
    },
    priceTrend: {
      message: 'Price trend',
    },
    tableList: {
      numberOfResults: '{{count}} Results',
      rowsPerPage: 'Rows per page:',
      empty: 'Opss.. No results were found',
    },
    fields: {
      country: 'Country',
      provider: 'Provider',
      status: 'Status',
      actions: 'Actions',
      uploadFile: 'Upload file',
    },
    language: {
      english: 'English',
      portuguese: 'Portuguese',
      spanish: 'Spanish',
    },
    error: {
      server: 'Ops! Something went wrong! :(',
      client: 'Ops! The requested action couldn`t be sent',
      unathorized: 'Unable to verify your credentials',
      documentNotSupported: 'The file type is not supported',
    },
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'General description',
    casesByChannel: 'Cases by channel',
    casesByDay: 'Cases by day',
    casesByHourAndChannel: 'Cases by hour and channel',
    casesNeedAgentByTime: 'Cases need agent by time',
    missingAgent: 'Needs agent',
    todayCases: 'Today cases',
    connectedAgents: 'Connected agents',
    sections: {
      importantNumbers: 'Important Numbers',
      needsAgent: 'Needs Agent',
      needAgentByTeam: 'Cases that need agent by team',
      needAgentByChannel: 'Cases that need agent by channel',
      noDataAvailable: 'No data available',
      quickComparisons: 'Quick Comparisons',
      agentsStatus: 'Agents Status',
      activityTrends: 'Activity Trends',
    },
    kpis: {
      availableAgents: 'Available agents',
      needAgentCases: 'Need agent cases',
      callsOnHold: 'Calls on hold',
      avgEmailResponse: 'Avg Email response',
      avgWhatsappResponse: 'Avg WhatsApp response',
      noData: '-',
    },
    charts: {
      unassignedCasesByTeam: 'Unassigned cases by team',
      avgResponseByChannel: 'Average response by channel (minutes)',
      casesByHourPattern: 'Average cases opened by hour on {{day}}',
      casesByWeekday: 'Average cases opened by day of week',
    },
    comparatives: {
      casesResolved: 'Cases Resolved',
      casesCreated: 'Cases Created',
      whatsappConversations: 'WhatsApp Conversations',
      incomingCalls: 'Incoming Calls',
      todayVsYesterday: 'Today vs Yesterday',
      thisWeekVsPrevious: 'This week vs previous',
      vs: 'vs',
      casesNeedingAgent: 'Cases needing agent by time',
      timeRanges: {
        '0-1': '1 hour or less',
        '1-3': '1-3 hours',
        '3-8': '3-8 hours',
        '8-24': '8-24 hours',
        '24-48': '24-48 hours',
        '+48': 'More than 48 hours',
      },
    },
    agentsStatus: {
      agentStates: 'Agent States',
      agentsList: 'Agents List',
      totalAgents: 'Total Agents',
      updatedAgo: 'Updated {{time}} ago',
      noAgentsConnected: 'No agents connected',
    },
    trends: {
      weekday: 'Day of the week',
      startDate: 'Start date',
      endDate: 'End date',
      weekdays: {
        monday: 'Monday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday',
      },
      weekdaysShort: {
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat',
        sun: 'Sun',
      },
    },
    channels: {
      whatsapp: 'Whatsapp',
      call: 'Call',
      email: 'Email',
      externalIntegration: 'External integration',
      manual: 'Manual',
      uploaded: 'Uploaded',
      supportForm: 'Support form',
    },
    filters: {
      agencies: 'Agencies',
      teams: 'Teams',
      channels: 'Channels',
      agency: 'Select agency',
      agencyPlaceholder: 'All agencies',
      startDate: 'Start date',
      endDate: 'End date',
      withoutTeam: 'Without team',
    },
  },
  flights: {
    searcher: {
      ariaLabelSearcher: 'Flights engine',
      oneWay: 'One way',
      roundTrip: 'Round trip',
      multidestination: 'Multidestino',
      origin: 'Origin',
      departureDate: 'Departure date',
      arrivalDate: 'Arrival date',
      destination: 'Destination',
      go: 'Go',
      return: 'Return',
      unknownDates: 'I have not yet decided on the dates',
      economic: 'Economic',
      guest: 'Guests',
      search: 'Search',
      addSection: 'Add segment',
      section: 'Section {{amount}}',
    },
    availabilityLayout: {
      editSearchTitle: 'Edit Search',
      avatarAriaLabel: 'Hotel bed avatar icon',
      viewMapAriaLabel: 'Open View map button',
      tuneFiltersAriaLabel: 'Open tune filters button',
      result_one: 'result',
      result_other: 'results',
      viewMap: 'View map',
      loadingFlightsLegend: 'We are looking for the best flight deals for you.',
      loadingMoreFlights: 'Loading more flights...',
      flightDetail: 'Flight detail',
      isReturn: 'return',
      isStart: 'takeoff',
      totalDuration: 'Total duration',
      noFareDataAvailable: 'No fare data available',
      noFares: 'No fares found matching your search criteria',
    },
    cardPackage: {
      direct: 'Direct',
      doubleBaseText: 'Value per person on a double basis',
      fromText: 'From',
      reserveButtonText: 'Book',
      night_one: 'night',
      night_other: 'nights',
      stop_one: 'Stop',
      stop_other: 'Stops',
    },
    availabilityCard: {
      recommendedLabel: 'Recommended',
    },
    booking: {
      purchaseDetails: 'Purchase details',
      total: 'total',
      continueBooking: 'continue',
    },
    payment: {
      summary: 'Payment summary',
      total: 'Total payment',
      success: 'Successful payment!',
      failed: 'Unsusscessful payment',
      method: 'Payment method',
      yours: 'Your payment',
      serviceFare: 'Service fare',
      taxes: 'Taxes',
      date: 'Payment date',
    },
    voucher: {
      index: 'Voucher',
      card: 'Embarkment card',
      localizer: 'Booking localizer',
      agency: 'Agency',
      sits: 'Sits',
      gohome: 'back to home',
      gate: 'Gate',
      terminal: 'Terminal',
      arrival: 'Arrival',
      departure: 'Departure',
    },
  },
  bookings: {
    view: {
      title: 'Booking',
      subtitle: 'Booking information',
      bookingNumber: 'Reservation #',
      cancelBooking: 'Cancel',
      issueBooking: 'Issue Booking',
      caseNumber: 'Case# #',
      quotationNumber: 'Quotation #',
      salesOrderNumber: 'Salesorder #',
      created: 'Creation date',
      passengers: 'Passengers',
      name: 'FIRST NAMES',
      lastname: 'LAST NAMES',
      document: 'DOCUMENT',
      paxType: 'PAX TYPES',
      nightsIn: 'nights in',
      finalPrice: 'Final price',
    },
    title: 'Bookings',
    subtitle: 'Booking list',
    table: {
      holder: 'Holder',
      paxQty: 'Passengers',
      destination: 'Destination',
      fareProvider: 'Fare provider',
      serviceProvider: 'Service Provider',
      status: 'Status',
      reservationCode: 'Reservation',
    },
    status: {
      open: 'open',
      pending: 'pending',
      failed: 'failed',
      confirmed: 'confirmed',
      completed: 'completed',
      cancelled: 'cancelled',
      not_found: 'not found',
      expired: 'expired',
      unknown: 'unknown',
    },
  },
  hotels: {
    fareBreakDown: {
      title: 'Fare Breakdown',
      netAmount: 'Net Amount',
      grossAmount: 'Gross Amount',
      included: 'included',
      excluded: 'to be paid at hotel',
      markup: 'Markup',
      fixed: 'fixed',
      total: 'Total',
    },
    rating: {
      opinion_one: '({{formattedCount}} opinion)',
      opinion_other: '({{formattedCount}} opinions)',
      error: 'An error happened retrieving the rating of hotel. Contact site administrator',
    },
    cancelPolicyModal: {
      title: 'Cancellation Policy',
      refundableFare: 'Refundable Fare',
      penaltyNights: 'penalty night(s)',
      fromTotal: '% of the total',
      fixedAmount: 'Fixed amount',
      unknownPenalty: 'Unknown penalty',
      limitDate: 'Apply from',
      penaltyTitle: 'Cancellation Penalty',
      additionalDetails: 'Additional details',
      dateCaluclatedByProvider: 'Date origin calculated by provider',
      dateCaluclatedBySystem: 'Date origin calculated by system',
    },
    availability: {
      fareCount: 'Up to {{count}} results',
      noMarkup: 'Raw price',
      loading: 'Loading results..',
      empty: 'Oops, no results were found. Please try again in a few minutes.',
      error: 'Oops, something went worong. Please try again in a few minutes.',
    },
    fares: {
      filters: {
        searchByName: 'Search by name',
        board: 'Board',
        provider: 'Provider',
        onlyRefundable: 'Only refundable',
      },
      amenitites: 'Amenities',
      showAllAmenitiesTrue: 'Show all amenities',
      showAllAmenitiesFalse: 'Hide all amenities',
      showCompleteDescriptionTrue: 'See more',
      showCompleteDescriptionFalse: 'Hide description',
      about: 'About this hotel',
      rooms: 'Select your option',
      summary: 'Summary',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      checkMinAge: 'Min age for check-in/check-out',
      checkInstructions: 'Instructions for check-in/check-out',
      remarks: 'Remarks',
      paymentType: {
        MERCHANT: 'Payment is managed by the supplier.',
        DIRECT:
          'Payment is made directly to the actual payee (e.g., the hotel), without involving an intermediary or third party. The payment will be completed at check-in.',
        CARD_BOOKING: 'Payment is managed by the supplier. The payment is effectuated at the time of booking',
        CARD_CHECK_IN: 'Payment is managed by the supplier and is made at the hotel during check-in',
      },
      paxes: {
        title: 'Room for:',
        adult: 'One adult ({{age}} yrs)',
        children: 'One children ({{age}} yrs)',
      },
      room: {
        title: 'Room: ',
      },
      detail: {
        price: 'Price: ',
        refundable: 'Refundable: ',
        paymentType: 'Payment type: ',
      },
      hotelPaxData: {
        pax: {
          addInfo: 'Add passengers info',
          useExisting: 'Use existing passenger',
        },
        name: {
          title: 'First Name',
          required: 'First name is required.',
        },
        surname: {
          title: 'Last Name',
          required: 'Last name is required.',
        },
        lastName: {
          title: 'Last Name',
          required: 'Last name is required.',
        },
        birthDate: {
          title: 'Date of Birth',
          required: 'Date of birth is required.',
          notValid: 'Please enter a valid date of birth.',
          notPast: 'Date of birth cannot be in the future.',
        },
        email: {
          title: 'Email',
          notValid: 'Please enter a valid email address.',
        },
        phone: {
          title: 'Phone',
        },
        form: {
          age_one: '{{count}} year',
          age_other: '{{count}} years',
          index: 'Passenger: {{count}}',
        },
      },
      cancelPolicy: {
        title: 'Cancel policies',
        details: 'See details',
        deadline: 'Free cancelation until',
        refundable: 'Refundable',
        nonRefundable: 'Not refundable',
      },
      rateRules: {
        title: 'Rate rules:',
        values: {
          NORMAL: 'Normal',
          PACKAGE: 'Package',
          OLDER55: 'Seniors 55+',
          OLDER60: 'Seniors 60+',
          OLDER65: 'Seniors 65+',
          CANARY_RESIDENT: 'Canary resident',
          BALEARIC_RESIDENT: 'Balearic resident',
          LARGE_FAMILY: 'Large family',
          HONEYMOON: 'Honeymoon',
          PUBLIC_SERVANT: 'Public servant',
          UNEMPLOYED: 'Unemployed',
          NON_REFUNDABLE: 'Non-refundable',
          MOBILE: 'Mobile',
          WARRANTY: 'Warranty',
          ESSENTIAL_WORKER: 'Essential worker',
          NEGOTIATED: 'Negotiated',
        },
      },
    },
    searcher: {
      title: 'Hotel search',
      destination: 'Destination',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      unknownDates: 'I have not yet decided on the dates',
      room: 'Rooms',
      guest: 'Guests',
      search: 'Search',
      apply: 'apply',
      hotels: 'Hotels',
      locations: 'Locations',
    },
    roomMenu: {
      roomCount: '{{count}} room',
      roomCount_other: '{{count}} rooms',
      adultCount: '{{count}} adult',
      adultCount_other: '{{count}} adults',
      childCount: '{{count}} child',
      childCount_other: '{{count}} children',

      title: 'Rooms and travelers',
      distribute: 'Assign travelers to each room.',
      summary: '{{rooms}} · {{adults}} · {{children}}',
      ageAtCheckIn: 'Ages at check-in',
      addRoom: '+ Add room',
      removeRoom: 'Remove room',
      cancel: 'Cancel',
      adultAges: 'Adult ages',
      adultRange: '18 years and older',
      childRange: 'Ages 0–17',
      infantAge: 'Under 1 year: enter 0',
      invalidRooms: 'Check ages: each room requires an adult and supports up to 4 travelers.',

      roomAmount: 'Amount',
      room: 'Room',
      year: 'year',
      years: 'years',
      adults: 'Adults',
      minors: 'Minors',
      apply: 'Apply',
      selected: '{{count}} room selected',
      selected_other: '{{count}} rooms selected',
      minorAge: 'Minor {{index}} age',
      adultAge: 'Adult {{index}} age',
      yearsOld: '{{count}} year old',
      yearsOld_other: '{{count}} years old',
      lessThanOneYear: 'Less than one year',
      error: 'Please review that all the fields are correctly completed',
    },
    cardsDestination: {
      seeMore: 'See more',
      from: 'From',
    },
    cardPackage: {
      doubleBaseText: 'Value per person on a double basis',
      fromText: 'From',
      reserveButtonText: 'Book',
      hotelRating: 'Hotel {{stars}} stars',
      nightsOfDuration: '{{amount}} nights',
    },
    filter: {
      filters: 'Filters',
      orderByAriaLabel: 'Order by sort group',
      orderBy: 'Order by',
      recommended: 'Recommended',
      bestPrice: 'Best Price',
      moreReviews: 'More Reviews',
      score: 'Score',
      name: 'Name',
      searchDistanceAriaLabel: 'Search distance slider',
      searchDistance: 'Search distance',
      radiusMessage: 'In a radius of',
      popularFiltersAriaLabel: 'Popular filters group',
      popularFilters: 'Popular filters',
      allInclusive: 'All inclusive',
      oceanView: 'Ocean view',
      hotelResort: 'Hotel resort',
      hotel: 'Hotel',
      freeCancel: 'Free cancel',
      noPaymentInAdvance: 'No payment in advance',
      priceAriaLabel: 'Prices ranges slider',
      price: 'Price',
      travelersExperienceAriaLabel: 'Travelers experience filter group',
      travelersExperience: 'Travelers experience',
      lgbtq: 'LGBTQ',
      businessFriendly: 'Business friendly',
      reducedMobility: 'Reduced Mobility',
      onlyAdults: 'Only adults',
    },
    availabilityLayout: {
      editSearchTitle: 'Edit Search',
      avatarAriaLabel: 'Hotel bed avatar icon',
      viewMapAriaLabel: 'Open View map button',
      tuneFiltersAriaLabel: 'Open tune filters button',
      results: 'results',
      viewMap: 'View map',
    },
    availabilityCard: {
      opinions: '{{numberOfOpinions}} opinions',
      endValueText: 'Total final value from',
      hotelRating: '{{rating}} star hotel',
      allInclusiveLabel: 'All inclusive',
      recommendedLabel: 'Recommended',
    },
    markupRules: {
      title: 'Markup Rules',
      optionFlights: 'Flights',
      optionHotels: 'Hotels',
      optionExplorer: 'Explorer',
      buttonNewRule: '+ NEW RULE',
      buttonBackHome: 'Back home',
      shownElements: 'Elements shown',
      listTitle: 'List of rules',
      search: 'Search engine',
    },
    routingRules: {
      title: 'Routing Rules',
      optionFlights: 'Flights',
      optionHotels: 'Hotels',
      optionExplorer: 'Explorer',
      buttonNewRule: '+ NEW RULE',
      buttonBackHome: 'Back home',
      shownElements: 'Elements shown',
      listTitle: 'List of rules',
      search: 'Search engine',
    },
    propertyMapDrawer: {
      hotelDetails: 'Hotel details',
      value: 'Final value from',
      reserveButton: 'Reserve',
    },
    propertyMapCard: {
      details: 'Details',
      opinions: 'Opinions',
      reserveButton: 'Reserve',
      popularAmenities: 'Popular amenities',
    },
    contentManager: {
      title: 'Hotels',
      subtitle: 'Hotel list',
      search: 'Search hotels..',
      error: 'An error happened retrieving hotels. Contact site administrator',
      status: {
        managed: 'Reviewed',
        outdated: 'Outdated',
        toReview: 'To Review',
      },
      fields: {
        id: 'Hotel ID',
        idSub: 'Internal ID: {{id}}',
        code: 'TGX ID',
        accessCode: 'Provider',
        name: 'Name',
        country: 'Country',
        city: 'City',
        address: 'Address',
        impressions: '# Impressions',
        status: 'Status',
        linked: 'Linked',
        modParent: 'Parent ID',
        modParentName: 'Parent Name',
        priority: 'Priority',
      },
      filters: {
        status: {
          managed: 'Managed',
          outdated: 'Outdated',
          toReview: 'To Review',
        },
        name: {
          title: 'Name',
        },
        address: {
          title: 'Address',
        },
        city: {
          title: 'City',
        },
        code: {
          title: 'TGX ID',
        },
        hotelId: {
          title: 'Hotel ID',
        },
        modParentId: {
          title: 'Internal ID',
        },
      },
      configuration: {
        title: 'Configuration',
        tabs: {
          providers: {
            title: 'Providers',
          },
          imageTags: {
            title: 'Image tags',
          },
          updates: {
            title: 'Updates',
          },
          amenities: {
            title: 'Amenities',
            subTitle: 'List of available icons for amenities',
            translatedTo: 'Translated to current locale',
          },
          categories: {
            title: 'Categories',
            subTitle: 'List of available category names',
            translatedTo: 'Translated to current locale',
          },
          amenitiesMapping: {
            title: 'Amenities mapping',
            subTitle: 'Select a provider and map the amenitites',
            notMapped: 'No amenities mapped yet',
            mapped: 'Amenity mapped succesfully',
          },
          categoriesMapping: {
            title: 'Categories mapping',
            subTitle: 'Select a provider and map the categories',
            notMapped: 'No categories mapped yet',
            mapped: 'Category mapped succesfully',
          },
        },
        defaultValues: {
          description: 'Description default',
          location: 'Location default',
          rooms: 'Rooms default',
          images: 'Images default',
          amenities: 'Amenities default',
          updateSuccess: 'Default value updated successfuly',
          updateError: 'An error happened updating default values',
          getError: 'An error happened retrieving default values',
        },
      },
      detail: {
        title: 'Hotel overview',
        error: {
          detail: 'An error happened retrieving the selected hotel. Contact site administrator',
          detailInfo: 'An error happened retrieving the selected hotel information. Contact site administrator',
        },
        description: {
          tab: 'Hotel',
          title: 'My description',
          editor: {
            name: {
              required: 'Hotel name is required',
            },
            rating: {
              required: 'Star rating is required',
            },
            descriptions: {
              required: 'Description is required',
              card: 'Card description',
              detail: 'Detail Description',
            },
            generateByAI: 'Do you want to generate descriptions by AI?',
          },
          update: {
            success: 'Descriptions updated successfully',
            error: 'An error happened updating descriptions. Contact site administrator',
          },
        },
        checkOptions: {
          tab: 'Check In / Out',
          title: 'Check In / Check Out',
          editor: {
            addOptions: 'Add',
            type: {
              title: 'Type',
              checkIn: 'Check In',
              checkOut: 'Check Out',
            },
            minAge: {
              title: 'Minimum age',
              required: 'Minimum age is required',
              error: 'Invalid age.',
              value: '{{value}} yrs old',
            },
            startTime: {
              title: 'Start time',
              required: 'Start time is required',
              error: 'Invalid start time.',
              value: '{{value}} hs',
            },
            endTime: {
              title: 'End time',
              required: 'End time is required',
              value: '{{value}} hs',
            },
            instructions: {
              title: 'Instructions',
              isSpecial: 'Is special?',
              text: 'Text',
              language: 'Language',
              edit: 'Edit instruction',
            },
          },
          update: {
            success: 'Check In / Out updated successfuly',
            error: 'An error happened updating Check In / Out options. Contact site administrator',
          },
        },
        location: {
          tab: 'Location',
          title: 'My location',
          showInMap: 'Use location',
          editor: {
            latLong: {
              required: 'Latitude and longitude are required',
              error: 'Invalid latitude, longitude format',
            },
          },
          update: {
            success: 'Location updated successfuly',
            error: 'An error happened updating location. Contact site administrator',
          },
        },
        rooms: {
          tab: 'Rooms',
          title: 'My rooms',
          subTitle: 'Room {{code}}',
        },
        images: {
          tab: 'Images',
          title: 'Hotel Images',
          tags: 'Room Tags',
          update: {
            success: 'Images updated successfully',
            error: 'An error happened updating imgaes. Contact site administrator',
          },
        },
        amenities: {
          tab: 'Amenitites',
          title: 'Hotel Amenitites',
          update: {
            success: 'Amenitites updated successfully',
            error: 'An error happened updating amenitites. Contact site administrator',
          },
        },
        category: {
          tab: 'Categories',
          mapped: 'Category mapped succesfully',
        },
        moderation: {
          link: {
            title: 'Linked hotels',
            manually: 'Link manually',
            button: 'Link',
            success: 'Hotel linked successfuly',
            error: 'An error happened linking hotels. Contact site administrator',
          },
          unlink: {
            button: 'Unlink',
            success: 'Hotel unlinked successfuly',
            error: 'An error happened unlinking hotels. Contact site administrator',
          },
          moderate: {
            button: "Mark as '{{status}}'",
            success: "Hotel marked as '{{status}}' successfuly",
            error: "An error happened marking hotel as '{{status}}'. Contact site administrator",
          },
          linked: {
            title: 'Linked',
          },
        },
        priority: {
          label: 'Priority',
          success: 'Priority updated to {{priority}}',
          error: 'An error occurred updating the priority',
        },
      },
    },
    amenitiesTitle: 'Amenities',
    amenities: {
      LGBTQ: 'LGBTQ',
      ac: 'Air Conditioning',
      allInclusive: 'All-Inclusive',
      aquaGym: 'Aqua Gym',
      arte: 'Art',
      azafata: 'Flight Attendant',
      babyFriendly: 'Baby-Friendly',
      badminton: 'Badminton',
      bakery: 'Bakery',
      barDeTragos: 'Cocktail Bar',
      basketball: 'Basketball',
      beach: 'Beach',
      beerPaint: 'Brewery',
      bicycle: 'Bicycles',
      billar: 'Billiards Table',
      botSail: 'Sailing',
      breakfast: 'Breakfast Included',
      brillos: 'Fireworks',
      brunch: 'Brunch',
      carRental: 'Car Rental',
      cardFill: 'Credit Card',
      casino: 'Casino',
      chefCocina: 'Chef',
      childCare: 'Childcare',
      cleaningService: 'Cleaning Service',
      closet: 'Closet',
      concierge: 'Concierge',
      conferenceRoom: 'Conference Room',
      currencyExchange: 'Currency Exchange',
      deck: 'Deck',
      desfibrilator: 'Defibrillator',
      doctor: 'Doctor',
      dryCleaning: 'Dry Cleaning',
      dryer: 'Dryer',
      eGames: 'eGames',
      ecoFriendly: 'Eco-Friendly',
      edificio: 'Building',
      elevator: 'Elevator',
      entertainment: 'Entertainment',
      family: 'Family-Friendly',
      fireplace: 'Fireplace',
      girlGym: "Women's Gym",
      golf: 'Golf',
      grillBbc: 'Barbecue',
      gym: 'Gym',
      hairSaloon: 'Hair Salon',
      jacuzzi: 'Jacuzzi',
      laundry: 'Laundry',
      library: 'Library',
      luggageCart: 'Luggage Cart',
      meetingRoom: 'Meeting Room',
      mobileOk: 'Mobile Signal',
      moneyOff: 'Cash Payment',
      multipleCard: 'Multiple Cards Accepted',
      nightClub: 'Nightclub',
      nightLife: 'Nightlife',
      parking: 'Parking',
      petfriendly: 'Pet-Friendly',
      phone: 'Phone',
      photocamara: 'Photography',
      pingPong: 'Ping Pong',
      pool: 'Swimming Pool',
      poolTable: 'Pool Table',
      proyector: 'Projector',
      restaurant: 'Restaurant Service',
      roomService: 'Room Service',
      safeBox: 'Safe Box',
      sauna: 'Sauna',
      scubadiving: 'Scuba Diving',
      securityCam: 'Security Cameras',
      seguridad: 'Security',
      shop: 'Shop',
      smokeDetector: 'Smoke Detector',
      smokeFree: 'Smoke-Free',
      smoking: 'Smoking Area',
      solarium: 'Solarium',
      spa: 'Spa',
      sportsDeportes: 'Sports Facilities',
      starFilled: 'Star Rating',
      surf: 'Surfing',
      tennis: 'Tennis',
      transfer: 'Shuttle Service',
      tv: 'Television',
      valetParking: 'Valet Parking',
      veggie: 'Vegetarian Options',
      volley: 'Volleyball',
      volunteerActivism: 'Volunteer Activities',
      waterSki: 'Water Skiing',
      wheelChair: 'Wheelchair Accessible',
      wifi: 'WiFi',
      notMappedYet: 'Amenity not mapped yet.',
    },
    categories: {
      [HotelCategories.AGROTOURISM]: 'Agrotourism',
      [HotelCategories.ONE_STAR]: 'One Star',
      [HotelCategories.ONE_KEY]: 'One Key',
      [HotelCategories.TWO_STARS]: 'Two Stars',
      [HotelCategories.TWO_KEYS]: 'Two Keys',
      [HotelCategories.THREE_STARS]: 'Three Stars',
      [HotelCategories.THREE_KEYS]: 'Three Keys',
      [HotelCategories.FOUR_STARS]: 'Four Stars',
      [HotelCategories.FOUR_KEYS]: 'Four Keys',
      [HotelCategories.FOUR_STARS_LUXURY]: 'Four Luxury Stars',
      [HotelCategories.FIVE_STARS]: 'Five Stars',
      [HotelCategories.FIVE_KEYS]: 'Five Keys',
      [HotelCategories.FIVE_STARS_LUXURY]: 'Five Luxury Stars',
      [HotelCategories.ONE_STAR_APARTHOTEL]: 'One Star Apart Hotel',
      [HotelCategories.TWO_STARS_APARTHOTEL]: 'Two Star Apart Hotel',
      [HotelCategories.THREE_STARS_APARTHOTEL]: 'Three Star Apart Hotel',
      [HotelCategories.FOUR_STARS_APARTHOTEL]: 'Four Star Apart Hotel',
      [HotelCategories.FIVE_STARS_APARTHOTEL]: 'Five Star Apart Hotel',
      [HotelCategories.FIRST_CATEGORY_APARTMENT]: '1st Category Apartment',
      [HotelCategories.SECOND_CATEGORY_APARTMENT]: '2nd Category Apartment',
      [HotelCategories.THIRD_CATEGORY_APARTMENT]: '3rd Category Apartment',
      [HotelCategories.BED_AND_BREAKFAST]: 'Bed and Breakfast',
      [HotelCategories.THREE_STARS_BED_AND_BREAKFAST]: 'Three Stars Bed And Break Fast',
      [HotelCategories.FOUR_STARS_BED_AND_BREAKFAST]: 'Four Stars Bed And Break Fast',
      [HotelCategories.FIVE_STARS_BED_AND_BREAKFAST]: 'Five Stars Bed And Break Fast',
      [HotelCategories.BOUTIQUE]: 'Boutique',
      [HotelCategories.FIRST_CATEGORY_CAMPING]: 'First Category Camping',
      [HotelCategories.SECOND_CATEGORY_CAMPING]: 'Second Category Camping',
      [HotelCategories.GUESTHOUSE]: 'Guest House',
      [HotelCategories.ONE_AND_HALF_STARS]: 'One And Half Stars',
      [HotelCategories.SUPERIOR_TWO_STARS]: 'Superior Two Stars',
      [HotelCategories.TWO_AND_HALF_STARS]: 'Two And Half Stars',
      [HotelCategories.SUPERIOR_THREE_STARS]: 'Superior Three Stars',
      [HotelCategories.THREE_AND_HALF_STARS]: 'Three And Half Stars',
      [HotelCategories.FOUR_AND_HALF_STARS]: 'Four And Half Stars',
      [HotelCategories.FIVE_AND_HALF_STARS]: 'Five And Half Stars',
      [HotelCategories.LUXURY_HISTORIC_HOTEL]: 'Luxury Historic Hotel',
      [HotelCategories.SUPERIOR_CATEGORY_RURAL_HOTEL]: 'Superior Category Rural Hotel',
      [HotelCategories.ONE_STAR_HOSTEL]: 'One Star Hostel',
      [HotelCategories.TWO_STARS_HOSTEL]: 'Two Stars Hostel',
      [HotelCategories.THREE_STARS_HOSTEL]: 'Three Stars Hostel',
      [HotelCategories.FOUR_STARS_HOSTEL]: 'Four Stars Hostel',
      [HotelCategories.FIVE_STARS_HOSTEL]: 'Five Stars Hostel',
      [HotelCategories.ONE_STAR_RURAL_HOSTEL]: 'One Star Rural Hostel',
      [HotelCategories.TWO_STARS_RURAL_HOSTEL]: 'Two Stars Rural Hostel',
      [HotelCategories.LODGE]: 'Lodge',
      [HotelCategories.MINI_HOTEL]: 'Mini Hotel',
      [HotelCategories.PENDING_CATEGORY]: 'Pending Category',
      [HotelCategories.PENSION]: 'Guesthouse o GuestRoom',
      [HotelCategories.POUSADA]: 'Inn',
      [HotelCategories.RESIDENCE]: 'Residence',
      [HotelCategories.RESORT]: 'Resort',
      [HotelCategories.WITHOUT_OFFICIAL_CATEGORY]: 'Without Official Category',
      [HotelCategories.STANDARD]: 'Standard',
      [HotelCategories.FOUR_SUPERIOR_STARS]: 'Four Superior Stars',
      [HotelCategories.VILLA]: 'Villa',
      [HotelCategories.VACATION_TOURIST_HOMES]: 'Vacation Tourist Homes',
    },
    hotelImageTag: {
      title: 'Tags',
      subtitle: 'Available Tags',
      results: 'Results',
      search: 'Search',
      table: {
        name: 'Name',
        color: 'Color',
      },
      create: {
        createdSuccess: 'Tag created',
        createdError: 'An error ocurred while creating the tag',
        title: 'Create Tag',
        name: 'Name',
        color: 'Color',
      },
      edit: {
        createdSuccess: 'Tag updated',
        createdError: 'An error ocurred while editing the tag',
        title: 'Edit Image Tag',
        name: 'Name',
        color: 'Color',
      },
      delete: {
        success: 'Tag deleted',
        error: 'An error ocurred while deleting the tag',
        title: 'Delete tag',
        message: 'Are you sure about deleting: {{ name }}?',
      },
    },
    paymentPolicies: {
      nonAnticipate: 'No advance payment',
    },
    cancelationPolicies: {
      free: 'Free cancelation',
    },
  },
  users: {
    results: 'results',
    search: 'User search',
    create: {
      title: 'Create user',
      createdError: 'An error ocurred while creating the user',
      warning: 'This is the only time this password will be shown, make sure you save it!',
      user: 'User:',
      password: 'Password:',
      finish: 'Finish',
    },
    selector: {
      title: 'User selector',
    },
    list: {
      title: 'Users',
      subtitle: 'User list',
    },
    table: {
      name: 'Fullname',
      email: 'Email',
      agency: 'Agency',
      roles: 'Roles',
      client: 'See as client',
    },
    view: {
      teams: 'teams',
    },
  },
  clients: {
    selector: {
      searchHint: 'Search by first and last name, phone or email. Select a row to add the client.',
      searchPlaceholder: 'Name, phone or email',
      loading: 'Searching clients…',
      retry: 'Retry',
      error: 'Could not load clients. Try again.',
      empty: 'No clients found. Try another name, phone or email, or create a client.',

      title: 'Client Selector',
    },
    title: 'Clients',
    subtitle: 'Client list',
    results: 'Results',
    search: 'Search',
    table: {
      name: 'Name',
      email: 'Email',
      phone: 'Phone #',
      agency: 'Agency',
      whatsapp: 'WhatsApp #',
      client: 'Client',
      contact: 'Contact',
      preferredAgent: 'Preferred Agent',
      organization: 'Organization',
      location: 'Location',
      created: 'Created',
    },
    filters: {
      clientName: 'Client Name',
      countries: 'Countries',
      organizations: 'Organizations',
      agencies: 'Agencies',
      preferredAgent: 'Preferred Agent',
      documentTypes: 'Document Types',
      searchClientNamePlaceholder: 'Search by first or last name',
      myClients: 'View only my clients',
    },
    view: {
      title: 'Client Information',
      agencyInfo: 'Agency Information',
      basicInfo: 'Basic Information',
      contactInfo: 'Contact Information',
      addressInfo: 'Address Information',
      documentInfo: 'Document Information',
      agency: 'Agency',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phoneCountryCode: 'Country Code',
      phoneNumber: 'Phone Number',
      whatsappNumber: 'WhatsApp Number',
      country: 'Country',
      city: 'City',
      postalCode: 'Postal Code',
      address1: 'Address',
      address2: 'Address (line 2)',
      documentType: 'Document Type',
      documentNumber: 'Document Number',
      clientSince: 'Client since',
      basicInfoSuccess: 'Basic information updated',
      contactInfoSuccess: 'Contact information updated',
      addressInfoSuccess: 'Address information updated',
      documentInfoSuccess: 'Document information updated',
      createdError: 'There was an error updating the information',
      preferredAgent: 'Preferred Agent',
      selectPreferredAgent: 'Select preferred agent',
    },
    errors: {
      DUPLICATED_PHONE_ERROR: 'Phone number is already registered for the selected Agency',
      DUPLICATED_EMAIL_ERROR: 'Email is already registered for the selected Agency',
      DUPLICATED_WHATSAPP_ERROR: 'WhatsApp number is already registered for the selected Agency',
      MISSING_FIELDS_ERROR: 'You must provide either an email, phone or WhatsApp number',
    },
    create: {
      createdSuccess: 'Client successfully created',
      createdError: 'There was an error creating the client',
      createdErrorPhone: 'Phone number is already registered for the selected Agency',
      createdErrorEmail: 'Email is already registered for the selected Agency',
      createdErrorWhatsapp: 'WhatsApp number is already registered for the selected Agency',
      createdErrorMissingFields: 'You must provide either an email, phone or WhatsApp number',
      title: 'Create Client',
      firstName: 'First Name',
      agency: 'Agency',
      lastName: 'Last Name',
      email: 'Email',
      phoneNumber: 'Phone Number',
      phoneCountryCode: 'Country Code',
      country: 'Country',
      city: 'City',
      postalCode: 'Postal Code',
      documentType: 'Document Type',
      documentNumber: 'Document Number',
      address1: 'Address',
      address2: 'Address (line 2)',
      whatsappNumber: 'WhatsApp',
    },
    edit: {
      whatsappNumber: 'WhatsApp',
      createdSuccess: 'Client successfully updated',
      createdError: 'There was an error updating the client',
      title: 'Update Client',
      firstName: 'First Name',
      agency: 'Agency',
      lastName: 'Last Name',
      email: 'Email',
      phoneNumber: 'Phone Number',
      phoneCountryCode: 'Country Code',
      country: 'Country',
      city: 'City',
      postalCode: 'Postal Code',
      documentType: 'Document Type',
      documentNumber: 'Document Number',
      address1: 'Address',
      address2: 'Address (line 2)',
    },
  },
  workflows: {
    title: 'Workflows',
    subtitle: 'List of available workflows',
    results: 'Results',
    search: 'Search',
    form: {
      name: 'Name',
      color: 'Color',
      domains: 'Domains',
      domainsHint: 'You can add emails or just domains, e.g: *@acme.com or john.doe@acme.com',
      error: {
        invalidEmailDomain: 'The domain {{ emailDomainValue }} is not valid',
      },
    },
    selectedStep: {
      title: 'Step details',
      label: 'Step name',
      info: 'Step information',
      delete: 'Delete step',
      teams: 'Teams',
      mailTemplate: 'Mail template',
      isCancelStepLabel: 'Cancel step?',
      cancelStepExists: 'A cancel step already exists',
    },
    instructions: {
      title: '{{step}} instructions',
      save: 'Save',
      close: 'Close',
      tabs: {
        edit: 'Edit',
        preview: 'Preview',
      },
    },
    edit: {
      title: 'Edit Workflow',
      error: 'There was an error while editing the workflow',
      success: 'Workflow successfully updated',
      unsavedChanges: 'There are unsaved changes. Do you want to save before leaving?',
    },
    create: {
      title: 'Create Workflow',
      error: 'There was an error while creating the workflow',
      success: 'Workflow successfully created',
      step: {
        title: 'Create Step',
        color: 'Color',
        name: 'Name',
        teams: 'Teams',
      },
    },
    table: {
      name: 'Name',
      color: 'Color',
      emailDomains: 'Email Domains',
    },
    view: {
      addInitialStep: 'Add initial step',
      addMiddleStep: 'Add middle step',
      addFinalStep: 'Add final step',
      agency: 'Agency',
      color: 'Color',
      created: 'Created',
      updatedSuccess: 'Workflow successfully updated',
    },
    actions: {
      title: 'Actions',
      filters: 'Filters',
      settings: 'Settings',
      add: 'Add',
    },
  },
  agencies: {
    title: 'Agencies',
    subtitle: 'List of available agencies',
    results: 'Results',
    search: 'Search',
    view: {
      dashboard: 'Dashboard',
      generalSettings: 'General Config',
      caseHandlers: 'Case Handlers',
      supportForms: 'Support Forms',
      twilio: 'Twilio',
      teams: 'Teams',
      communications: 'Communications',
      callsMenu: 'Calls Menu',
      iaConfig: 'AI Configuration',
      whatsappSettings: 'Whatsapp Settings',
      caseTriggers: 'Case triggers',
      uploads: 'Data uploads',
      organizations: 'Organizations',
      phone: 'Phone',
      email: 'Mailing',
      googleChat: 'Google Chat',
      workflows: 'Workflows',
      general: 'General',
      settings: 'Settings',
      flights: 'Flights',
      hotels: 'Hotels',
      markupRules: 'Markup Rules',
      routingRules: 'Routing Rules',
      invalidationRules: 'Invalidation Rules',
      integrations: 'Integrations',
      customFields: 'Custom Fields',
      flightIntegrations: 'Flight Integrations',
      webhooks: 'Webhooks',
      metaAds: 'Meta Ads',
    },
    table: {
      name: 'Name',
      currency: 'Currency',
      country: 'Country',
      isPrivate: 'Is a private site',
    },
    settings: {
      twilio: 'Twilio',
      saveSuccess: 'Settings saved',
      saveError: 'An error ocurred while saving the settings...',
      general: 'General',
      auth: 'Authentication',
      communicationChannels: 'Communication Channels',
      maps: 'Maps',
      callsMenu: 'Calls menu',
      whitelabeling: 'White labeling',
      aws: 'AWS',
      sendgrid: 'Sendgrid',
      gmail: 'Gmail SMTP OAuth2',
      googleMaps: 'Google Maps',
      googleSignIn: 'Google Sign-in',
      registration: 'Registration',
      whatsapp: 'Whatsapp (Gupshup)',
      googleChat: 'Google Chat',
      emailSignature: 'Email Signature',
      openai: 'OpenAI',
      ai: 'Artificial Intelligence',
      whatsappSubscriptionError: 'Error al crear subscripcion',
      hotels: 'Hotels',
      placeholders: {
        selectAIUser: 'Select an AI user',
        noUsersFound: 'No users found',
        contextMessages: '10',
      },
      envMap: {
        AI_CASE_HANDLERS_ENABLED: 'Enable AI for case handlers',
        IA_USER_ID: 'IA User ID',
        TWILIO_ACCOUNT_SID: 'Account SID',
        TWILIO_AUTH_TOKEN: 'Authorization Token',
        TWILIO_API_KEY: 'API KEY',
        TWILIO_API_KEY_SECRET: 'Api KEY secret',
        TWILIO_TWIML_APP_SID: 'Twiml app sid',
        TWILIO_PHONE_NUMBER: 'Phone number',
        TWILIO_TRUNK_SID: 'Twilio trunk SID',
        JWT_SECRET: 'JWT secret',
        REFRESH_JWT_SECRET: 'JWT refresh token secret',
        DEFAULT_WORKFLOW_ID: 'Default workflow Id',
        WHATSAPP_DEFAULT_WORKFLOW_ID: 'Whatsapp Default workflow',
        WHATSAPP_PREFERRED_AGENT_WORKFLOW_ID: 'Whatsapp preferred agent workflow',
        JWT_RESET_PASSWORD: 'JWT reset pwd token secret',
        WHITELABEL_CODE: 'Whitelabel code',
        AWS_REGION: 'Region',
        AWS_ACCESS_KEY_ID: 'Access Key Id',
        AWS_SECRET_ACCESS_KEY: 'Secret Access Key',
        AWS_S3_ENDPOINT: 'S3 endpoint',
        AWS_S3_BUCKET_EMAIL_ATTACHMENTS: 'S3 bucket mail attachments',
        GOOGLE_CLIENT_ID: 'Client ID',
        GOOGLE_CLIENT_SECRET: 'Client Secret',
        GOOGLE_CALLBACK_URL: 'Callback URL',
        GMAIL_OAUTH_CLIENT_ID: 'Gmail OAuth Client ID',
        GMAIL_OAUTH_CLIENT_SECRET: 'Gmail OAuth Client Secret',
        GMAIL_OAUTH_REFRESH_TOKEN: 'Gmail OAuth Refresh Token',
        VITE_GOOGLE_KEY: 'Google Key',
        CLIENT_URL: 'Client URL',
        SENDGRID_API_KEY: 'API Key',
        SENDGRID_SENDER_EMAIL: 'Sender email',
        SENDGRID_INBOUND_MAILS: 'Managed mails',
        VITE_MAP_ID: 'Map Id',
        MAIL_CREDENTIAL: 'Mail credential',
        GUPSHUP_AUTH_SECRET: 'Auth Secret',
        GUPSHUP_AUTH_USER: 'login mail',
        GUPSHUP_APP_ID: 'Application ID',
        GUPSHUP_PARTNER_API_URL: 'Partner Api URL',
        GUPSHUP_APP_NAME: 'Application Name',
        GUPSHUP_PHONE_NUMBER: 'Phone Number',
        GUPSHUP_SENDER_NAME: 'Sender Name',
        GUPSHUP_API_KEY: 'API Key',
        GOOGLE_CHAT_CLIENT_EMAIL: 'Client Email',
        GOOGLE_CHAT_PRIVATE_KEY: 'Private Key',
        ALLOWED_EMAIL_DOMAIN: 'Allowed email domain to access',
        VITE_MANUAL_REGISTRATION_ALLOWED: 'Manual registration allowed? (true/false)',
        WHATSAPP_OPENAI_API_KEY: 'OpenAI API Key',
        WHATSAPP_OPENAI_AMOUNT_OF_CONTEXT_MESSAGES: 'Amount of context messages for AI',
        COPILOT_AGENCY_CONTEXT: 'Agency context (business, tone, market)',
        COPILOT_PROVIDER: 'AI provider',
        COPILOT_PASSIVE_MODEL: 'Passive model (per-message analysis)',
        COPILOT_ACTIVE_MODEL: 'Active model (reply suggestions)',
        COPILOT_API_KEY: 'Copilot API key',
        TRAVEL_INTELLIGENCE_BUSINESS_UNIT:
          'Travel intelligence — Business unit (e.g. UY)',
        TRAVEL_INTELLIGENCE_ORIGIN_COUNTRY:
          'Travel intelligence — Origin country (ISO, e.g. UY)',
        TRAVEL_INTELLIGENCE_PASSPORT_COUNTRY:
          'Travel intelligence — Passport country (ISO, e.g. UY)',
        TRAVEL_INTELLIGENCE_CURRENCY:
          'Travel intelligence — Currency (ISO, e.g. USD)',
        HOTEL_BOOK_AGENCY_REFERENCE: 'Hotels booking reference prefix',
        HOTEL_BOOK_MINOR_AGE_DELIMITER: 'Determine minors age',
      },
    },
    create: {
      createdSuccess: 'Agency successfully created',
      createdError: 'There was an error while creating the agency',
      title: 'Create Agency',
      name: 'Name',
      country: 'Country',
      currency: 'Currency',
      isPrivate: 'Private site',
    },
    edit: {
      createdSuccess: 'Agency successfully updated',
      createdError: 'There was an error while updating the agency',
      title: 'Update Agency',
      name: 'Name',
      country: 'Country',
      currency: 'Currency',
      isPrivate: 'Private site',
    },
  },
  caseHandlers: {
    title: 'Case Handlers',
    subtitle: 'List of available case handlers',
    results: 'Results',
    search: 'Search',
    table: {
      name: 'Name',
      description: 'Description',
      enabled: 'Enabled',
      scanWorkflow: 'Scan Workflow',
      scanStep: 'Scan Step',
      scanStatus: 'Scan Status',
      nextWorkflow: 'Next Workflow',
      nextStep: 'Next Step',
      nextStatus: 'Next Status',
      channel: 'Channel',
    },
    create: {
      title: 'Create Case Handler',
      success: 'Handler created successfully',
      error: 'There was an error creating the handler',
    },
    edit: {
      title: 'Edit Case Handler',
      success: 'Handler updated successfully',
      error: 'There was an error updating the handler',
    },
    form: {
      name: 'Name',
      namePlaceholder: 'Enter handler name',
      description: 'Description',
      descriptionPlaceholder: 'Enter optional description',
      messageContextHistoryCount: 'Context count',
      enabled: 'Enabled',
      channel: 'Channel',
      channelPlaceholder: 'Enter channel (optional)',
      scanStatus: 'Scan Status',
      scanStatusPlaceholder: 'Enter scan status (optional)',
      nextStatus: 'Next Status',
      nextStatusPlaceholder: 'Enter next status (optional)',
      scanWorkflow: 'Scan Workflow',
      scanWorkflowPlaceholder: 'Select scan workflow',
      scanStep: 'Scan Step',
      scanStepPlaceholder: 'Select scan step',
      nextWorkflow: 'Next Workflow',
      nextWorkflowPlaceholder: 'Select next workflow',
      nextStep: 'Next Step',
      nextStepPlaceholder: 'Select next step',
      prompt: 'Prompt',
      promptPlaceholder: 'Enter handler prompt',
      listeningTo: 'Listening to',
      onSuccess: 'On Success',
      onBailout: 'On Bailout',
      onCancel: 'On Cancel',
      promptSection: 'Prompt',
      // New translations for AI Handler fields
      iaHandlerConfiguration: 'AI Handler Configuration',
      handlerPurpose: 'Handler Purpose',
      handlerPurposePlaceholder: 'Describe the specific purpose of this handler...',
      behaviorRules: 'Behavior Rules',
      behaviorRulesPlaceholder: 'Define specific behavior rules...',
      stepsDescription: 'Steps Description',
      stepsDescriptionPlaceholder: 'Describe the steps to follow...',
      examples: 'Examples',
      handlerExample: 'Result Example',
      handlerExamplePlaceholder: 'Provide an example of the expected result...',
      conversationExample: 'Conversation Example',
      conversationExamplePlaceholder: 'Show a conversation example...',
      validationsAndNotes: 'Validations and Notes',
      validations: 'Validations',
      validationsPlaceholder: 'Define required validations...',
      allowedTools: 'Allowed Tools',
      allowedToolsPlaceholder: 'Select the tools this handler can use',
      additionalNotes: 'Additional Notes',
      additionalNotesPlaceholder: 'Add any important additional notes...',
      // Tabs labels
      tabs: {
        listening: 'Listening',
        handlePurpose: 'Handle Purpose',
        behaviourRules: 'Behaviour Rules',
        steps: 'Steps',
        resultExamples: 'Result Examples',
        conversationExamples: 'Conversation Examples',
        validations: 'Validations',
        additionalNotes: 'Additional Notes',
      },
    },
  },
  teams: {
    title: 'Teams',
    subtitle: 'List of available teams',
    results: 'Results',
    search: 'Search',
    table: {
      name: 'Name',
      email: 'Email',
      agency: 'Agency',
      color: 'Color',
      isAvailableInPhone: 'is in queue',
    },
    create: {
      createdSuccess: 'Team successfully created',
      createdError: 'There was an error while creating the team',
      title: 'Create Team',
      name: 'Name',
      agency: 'Agency',
      color: 'Color',
    },
    edit: {
      createdSuccess: 'Agency successfully updated',
      createdError: 'There was an error while updating the agency',
      title: 'Update Team',
      name: 'Name',
      agency: 'Agency',
      color: 'Color',
    },
    members: 'members',
    linkUserToTeam: {
      success: 'User successfuly linked to team',
      failed: 'There was a problem while linking the user',
      removed: 'User removed from team',
      removedFailed: 'There was a problem while removing the user',
      removeToolip: 'Remove from team',
    },
  },
  organizations: {
    title: 'Organizations',
    subtitle: 'List of available organizations',
    results: 'Results',
    search: 'Search',
    table: {
      name: 'Name',
      domain: 'Domain',
      agency: 'Agency',
      memberCount: 'Member count',
    },
    create: {
      createdSuccess: 'Organization successfully created',
      createdError: 'There was an error while creating the organization',
      title: 'Create Organization',
      name: 'Name',
      domain: 'Domain',
    },
    edit: {
      createdSuccess: 'Organization successfully updated',
      createdError: 'There was an error while updating the organization',
      title: 'Update Organization',
      name: 'Name',
      domain: 'Domain',
    },
    domainError: 'Enter a valid domain (e.g. @yourdomain.com)',
    members: 'members',
    linkUserToOrganization: {
      success: 'User successfuly linked to organization',
      failed: 'There was a problem while linking the user',
      removed: 'User removed from organization',
      removedFailed: 'There was a problem while removing the user',
      removeToolip: 'Remove from organization',
    },
    selector: {
      add: 'agregar',
      title: 'Organization selector',
    },
  },
  error404: {
    message: "The page you're looking for doesn't exist.",
    btnLabel: 'back home',
  },
  error401: {
    message: 'Unauthorized access.',
    btnLabel: 'Back home',
  },
  linkCaseModal: {
    newCase: 'New case',
    linkedTo: 'Link to',
    duplicates: 'Dupes',
    linkSuccess: 'Case link successful',
    linkError: 'Unable to link case',
    selectCaseError: 'You need to select a case',
    searchTextLabel: 'Search for a case',
  },
  travelers: {
    title: 'Case travelers',
    subtitle: 'Prepare traveler details once and reuse them when booking.',
    search: 'Search by name or document',
    birthDateValue: 'Birth date: {{date}}',
    noBirthDate: 'Birth date not provided',
    summary: {
      title: 'Travelers',
      count: '{{count}} traveler',
      count_other: '{{count}} travelers',
      more: 'and more',
    },
    sections: {
      roster: 'In this case ({{count}})',
      rosterHint: 'These people are available for this trip bookings.',
      known: 'Known by the customer ({{count}})',
      knownHint: 'Add them only when they are joining this trip.',
    },
    actions: {
      manage: 'Manage travelers',
      create: 'Create traveler',
      addToCase: 'Add to case',
      remove: 'Remove from case',
    },
    fields: {
      title: 'Title',
      firstName: 'First name',
      lastName: 'Last name',
      birthDate: 'Birth date',
      email: 'Email',
      phone: 'Phone',
      nationality: 'Nationality',
      countryOfResidence: 'Country of residence',
    },
    documents: {
      title: 'Documents',
      add: 'Add document',
      remove: 'Remove document',
      type: 'Type',
      country: 'Issuing country',
      number: 'Number',
      expiresOn: 'Expiration date',
      types: {
        passport: 'Passport',
        ci: 'Identity card',
        dni: 'National ID',
      },
      privateHint: 'The full number is only shown inside this form.',
      maskedHint: 'Number masked for security',
    },
    form: {
      createTitle: 'New traveler',
      editTitle: 'Edit traveler',
    },
    validation: {
      required: 'This field is required',
      email: 'Enter a valid email',
      phone: 'Enter a valid international phone number',
      country: 'Choose a country from the list',
    },
    states: {
      unavailable: 'The traveler summary could not be loaded.',
      loadError: 'Travelers could not be loaded. Please retry.',
      suggestionsError: 'Known travelers could not be loaded.',
      noPermission: 'You do not have permission to manage travelers for this case.',
      noClient: 'Assign a customer to create travelers or reuse known people.',
      emptyRoster: 'There are no travelers in this case yet.',
      emptySuggestions: 'There are no other known travelers to suggest.',
      noResults: 'No travelers matched this search.',
    },
    feedback: {
      saved: 'Traveler details saved',
      saveError: 'Traveler details could not be saved.',
      added: '{{name}} was added to the case',
      addError: 'The traveler could not be added to the case.',
      removed: 'The traveler was removed from the case',
      removeError: 'The traveler could not be removed from the case.',
    },
    documentMatches: {
      sectionTitle: 'In the agency',
      sectionHint: 'People with this document who traveled with another client.',
      none: 'Nobody in the agency has this document.',
      createWithDocument: 'Create traveler with document {{number}}',
      noticeTitle: 'A traveler with this document already exists',
      use: 'Use this traveler',
      useInThisCase: 'Use that traveler in this case',
      birthYear: 'Born: {{year}}',
      relation: {
        in_case: 'Already in this case',
        known_by_client: 'Known by the client',
        other_client: 'From another client',
      },
      lookupError: 'The document could not be looked up.',
      linkError: 'That traveler could not be used.',
      swapped: 'The case now uses {{name}}',
    },
    remove: {
      title: 'Remove traveler from case',
      description: 'Remove {{name}} from this case?',
      keepProfile: 'Their details remain linked to the customer and can be suggested for future trips.',
    },
  },
  cases: {
    status: {
      [CaseStatus.open]: 'open',
      [CaseStatus.inProgress]: 'in progress',
      [CaseStatus.needsClientAction]: 'waiting for response',
      [CaseStatus.needsAgentAction]: 'needs agent',
      [CaseStatus.done]: 'completed',
      [CaseStatus.canceled]: 'canceled',
      any: 'Any status (default: open)',
    },
    originatedByChannel: {
      [OriginatedByChannel.WHATSAPP]: 'WhatsApp',
      [OriginatedByChannel.CALL]: 'Call',
      [OriginatedByChannel.EMAIL]: 'Email',
      [OriginatedByChannel.MANUAL]: 'Manual',
      [OriginatedByChannel.EXTERNAL_INTEGRATION]: 'External integration',
      [OriginatedByChannel.UPLOADED]: 'Uploaded',
      [OriginatedByChannel.SUPPORT_FORM]: 'Support form',
    },
    stats: {
      byDay: 'Cases by day',
      count: 'Number of cases',
      date: 'Date',
      byTeam: 'Cases by team',
      byStatus: 'Cases by status',
      byWorkflow: 'Cases by workflow',
      noTeam: 'No team',
      noWorkflow: 'Unassigned',
    },
    statistics: {
      title: 'Case Statistics',
      byAgent: 'Cases by Agent',
      byTeam: 'Cases by Team',
      byAgency: 'Cases by Agency',
      byChannel: 'Cases by Channel',
      byStatus: 'Cases by Status',
      byWorkflow: 'Cases by Workflow',
      byStep: 'Cases by Step',
    },
    export: {
      csvButton: 'Generate CSV report (you will receive an email when ready)',
      processingMessage: 'Case report is being processed. You will receive an email when ready.',
      errorMessage: 'There was an error generating the report',
    },
    create: {
      createdSuccess: 'Case successfully created',
      createdError: 'There was an error while creating the case',
      placeholder: 'Enter new case name...',
      agency: 'Agency',
      client: 'Client',
    },
    update: {
      success: 'Case successfully updated',
      error: 'There was an error while modifying your case',
    },
    list: {
      refreshing: 'Updating cases…',
      columns: {
        title: "Custom columns",
        chooseWorkflow: "Select exactly one workflow to add columns",
        search: "Search fields",
        empty: "No available fields",
        selected: "{{count}} of {{max}} columns selected",
        cancel: "Cancel",
        apply: "Apply",
        loadError: "Could not load custom fields",
        retry: "Retry",
        storageError: "Columns applied, but could not be saved on this device.",
        yes: "Yes",
        no: "No",
      },
      bulk: {
        title: 'Bulk edit',
        selectedCount: '{{count}} selected',
        clear: 'Clear',
        apply: 'Apply',
        leaveUnchanged: 'Leave unchanged',
        unassigned: 'Unassigned',
        workflow: 'Workflow',
        step: 'Step',
        status: 'Status',
        agent: 'Agent',
        updatedSuccess: 'Updated {{count}} cases',
        updatedError: 'Failed to update cases',
        workflowRequired: 'Select a workflow before choosing a step',
        noChanges: 'Select at least one field to update',
      },
      filters: {
        status: 'Status',
        title: 'Filters',
        name: 'Filter name',
        placeHolderNew: 'New Filter name',
        placeHolderExisting: 'Editar filter name',
        owner: 'Author',
        myFilters: 'My Filters',
        publicFilters: 'Public Filters',
        checkbox: 'mark as public',
        clear: 'Clear',
        save: 'Save',
        createNew: 'Add new',
        createError: 'Error while creating filter',
      },
      filterByStatus: 'Filter by status',
      filterByTeam: 'Filter by team',
      filterByWorkflow: 'Filter by workflow',
      filterByStep: 'Filter by step',
      filterByAgent: 'Filter by agent',
      filterByClient: 'Filter by client',
      filterByOrganization: 'Filter by organization',
      filterBySubject: 'Filter by subject',
      filterByUtmSource: 'Filter by UTM source',
      filterByUtmCampaign: 'Filter by UTM campaign',
      filterByAdId: 'Filter by ad ID',
      filterByOriginatedByChannel: 'Filter by origin channel',
      filterByAgency: 'Filter by agency',
      filterByCreated: 'Filter by created date',
      filterByUpdated: 'Filter by updated date',
      filterByCreatedAfter: 'Created after',
      filterByCreatedBefore: 'Created before',
      title: 'Cases',
      subtitle: 'Case list',
      search: 'Search...',
      tabs: {
        all: 'All',
        myGroups: 'My Teams',
        myCases: 'My Cases',
      },
      results: 'Results',
      table: {
        organization: 'Organization',
        client: 'Client',
        subject: 'Subject',
        agency: 'Agency',
        team: 'Team',
        workflow: 'Workflow',
        step: 'Step',
        status: 'Status',
        channel: 'Channel',
        agent: 'Agent',
        created: 'Created',
        lastUpdate: 'Last update',
      },
    },
    view: {
      assignToMe: 'Assign to me',
      case: 'Case',
      copyCaseNumber: 'Copy case number',
      copySuccess: 'Case number copied to clipboard',
      titleStatus: 'Case status',
      titleClient: 'Customer information',
      bookingCount_zero: 'No bookings yet',
      bookingCount_one: '1 booking in this case',
      bookingCount_other: '{{count}} bookings in this case',
      travelBookings: 'View case bookings',
      titleOtherDetails: 'Other details',
      relationRemovedSuccess: 'Case successfuly unlinked',
      relationRemovedError: 'An error occurred while unlinking the case',
      addRelationShip: 'Link to another case',
      instructions: 'Instructions',
      assignments: 'Assignments',
      workflow: 'Workflow',
      step: 'Step',
      status: 'Status',
      team: 'Team',
      organization: 'Organization',
      client: 'Client',
      agent: 'Assigned Agent',
      saleorderLegacy: 'Sales Order (Legacy)',
      saleorder: 'Sale Order',
      agency: 'Agency',
      created: 'Created',
      iaEnabled: 'AI Enabled',
      log: {
        UPDATE: 'updated',
        CREATE: 'created',
        LINKED: 'linked',
        DUPED: 'duped',
        WORKFLOW: 'Workflow',
        ORGANIZATION: 'Organization',
        AGENCY: 'Agency',
        IA_ENABLED: 'AI Enabled',
        STEP: 'Step',
        TEAM: 'Team',
        STATUS: 'Status',
        CLIENT: 'Client',
        AGENT: 'Agent',
        CASE: 'Case',
        DELETE: 'deleted',
        SALEORDER: 'Sales order',
        from: 'from',
        to: 'to',
        saleorder: {
          DELETE: 'Sale order removed',
          CREATE: 'Sale order added',
        },
      },
      content: {
        activity: 'Activity',
        show: 'Show',
        all: 'All',
        history: 'History',
        search: 'Search...',
        whatsapp: 'Whatsapp',
        voicemail: 'Voice messages',
        note: 'Notes',
        phone: 'Calls',
        email: 'Emails',
        notes: 'Notes',
        endsWithin: 'Your session ends within:',
        subject: 'Subject',
        date: 'Date',
        viewMore: 'See more',
        noteText: 'added a note',
        edit: 'Edited',
        caseEdit: 'edited the case',
        caseCreate: 'created the case',
        emailText: 'sent an email',
        phoneCallText: 'called',
        reply: 'Reply',
        replyToAll: 'Reply to all',
        from: 'From',
        to: 'To',
        cc: 'CC',
        bcc: 'BCC',
        newEmail: 'Send email',
        draft: 'Draft',
        newWhatsapp: 'Send whatsapp',
        noWhatsappNumber: 'This client has no WhatsApp number assigned',
        newNotes: 'Create note',
        imagePreview: 'Image preview',
        call: 'Call',
        incomingCall: 'Incoming call',
        outgoingCall: 'Outgoing call',
        missingContactData: 'No contact data available',
        callTransfers: {
          transferredTo: 'Transferred to',
        },
        loading: 'Loading...',
        hideThread: 'Hide thread',
        showThread: 'Show previous thread',
        showThreadWithCount: 'Show previous thread ({{count}} messages)',
        previousMessages: 'Previous messages • {{count}} messages',
        noDate: 'No date',
        replySubject: 'Re',
        legacyEmailTooltip: 'Email prior to thread optimization',
        cleanedEmailTooltip: 'Quoted content detected and cleaned',
        showFullEmail: 'Show full email',
        showCleanedEmail: 'Show without thread',
      },
      moreInfo: 'More information',
      lessInfo: 'Less information',
      tabInfo: 'Info',
      tabHistory: 'History',
      attribution: {
        title: 'Attribution',
        utmSource: 'UTM Source',
        utmCampaign: 'UTM Campaign',
        adId: 'Ad ID',
        adSourceType: 'Source type',
        ctwaClid: 'CTWA Click ID',
        fbclid: 'Facebook Click ID',
      },
    },
  },
  callsMenu: {
    [WELCOME_MESSAGE]: 'Welcome message',
    saveSuccess: 'IVR saved successfully',
    saveError: 'An error occurred while saving the IVR',
    selectedStage: {
      dialpad: 'Numbers to connect',
      timeout: 'Timeout (sec.)',
      timeoutAction: {
        label: 'If no digit is pressed',
        action: {
          [TimeoutAction.REPEAT]: 'Repeat',
          [TimeoutAction.EXIT]: 'Exit',
        },
      },
      audioRecorder: {
        title: 'Record',
        delete: 'Delete recording',
      },
      text: {
        title: 'Text',
        textPlaceholder: 'Enter the text you want to convert to voice',
      },
      file: {
        title: 'File',
        uploading: 'Uploading file...',
        delete: 'Delete file',
        upload: 'Upload file',
        uploaded: 'Audio uploaded',
        uploadSuccess: 'File uploaded successfully',
        uploadError: 'An error occurred while uploading the file',
      },
      audioPlayer: {
        play: 'Play',
        pause: 'Pause',
        delete: 'Delete audio',
      },
      sendTo: 'Send to',
      actionType: {
        [IVRActionType.ENTER_EXTENSION]: 'Extension',
        [IVRActionType.SEND_TO_QUEUE]: 'Team',
      },
      communicationType: {
        title: 'Communication type',
        types: {
          [CommunicationType.TEXT_TO_SPEECH]: 'Text to speech',
          [CommunicationType.AUDIO_RECORD]: 'Audio',
          [CommunicationType.AUDIO_FILE]: 'File',
        },
      },
      menu: {
        label: 'Menu name',
        delete: 'Delete menu',
      },
      action: {
        label: 'Action name',
        delete: 'Delete action',
      },
    },
    stage: {
      team: 'Team',
    },
  },
  notifications: {
    title: 'Notifications',
    close: 'Close notifications',
    clean: 'Mark as read',
    recents: 'Recents',
    tabs: {
      label: 'Notification types',
      conversations: 'Conversations',
      activity: 'All activity',
    },
    conversations: {
      loading: 'Loading conversations',
      error: 'We could not load pending conversations.',
      emptyTitle: 'You have no unread conversations',
      emptyDescription: 'New client messages will appear here.',
      listLabel: 'Unread conversations',
      caseNumber: 'Case #{{caseId}}',
      viewAll: 'View all in Inbox',
    },
    push: {
      enable: 'Enable',
      explanation: 'Enable notifications to learn about Inbox activity even when this tab is not open.',
    },
    [NotificationType.CASE_ASSIGNED_TO_ME]: {
      title: 'Caso Assigned',
      message: 'User <b>{{username}}</b> assigned you to case <b>{{caseId}}</b>',
    },
    [NotificationType.CASE_UNASSIGNED_ME]: {
      title: 'Caso Unassigned',
      message: 'User <b>{{username}}</b> unassigned you from case <b>{{caseId}}</b>',
    },
    [NotificationType.CASE_STATUS_MODIFIED]: {
      title: 'Case Modified',
      message: 'User <b>{{username}}</b> change the status of <b>{{caseId}}</b> to <b>{{status}}</b>',
    },
    [NotificationType.CASE_EMAIL_ADDED]: {
      title: 'Mail sent',
      message: 'User <b>{{username}}</b> sent an email in case <b>{{caseId}}</b>',
    },
    [NotificationType.CASE_NOTE_ADDED]: {
      title: 'Note Added',
      message: 'User <b>{{username}}</b> added a note to case <b>{{caseId}}</b>',
    },
    [NotificationType.CASE_CLIENT_REPLIED]: {
      title: 'Client Replied',
      message: 'Client <b>{{clientName}}</b> replied to case <b>{{caseId}}</b>',
    },
    [NotificationType.USER_NOTIFICATION_TRIGGER]: {
      title: 'Trigger Match',
      message: 'Case <b>{{caseId}}</b> matched your notification trigger (status <b>{{status}}</b>)',
    },
  },
  markupRules: {
    newGroup: '+ NEW GROUP',
    buttonBackList: 'Back list',
    isActive: 'Active',
    deleteGroup: 'Delete group',
    addCondition: '+ Add condition',
    addAction: '+ Add action',
    relationshipGroups: 'Relationship between groups',
    and: 'AND',
    or: 'OR',
    name: 'Name',
    order: 'Order',
    orderPlaceholder: 'Empty = last',
    orderHelper:
      'Evaluation position (1 = first). The rule is inserted there and the rest shift.',
    orderAssigned: 'Order {{order}} was assigned (the requested one was past the end of the list)',
    markup: 'Margen',
    percentage: 'percentage',
    fee: 'fee',
    buttonDiscard: 'Discard',
    buttonSave: 'Save',
    conditionalGroup: 'Conditional group',
    createSuccess: 'Markup rule created',
    createError: 'An Error ocurred while creating the rule',
    viewEdit: {
      titleNew: 'Create markup rule',
      titleEdit: 'Edit markup rule',
      subtitleNew: 'Create markup rule',
      subtitleEdit: 'Edit markup rule',
    },
    yup: {
      nameRequired: 'The rule name is required',
      nameCharLength: 'The rule name must be 50 characters or less',
      operatorRequired: 'Operator is required',
      operatorInvalid: 'Invalid operator selected',
      fieldRequired: 'This field is required',
      fieldInvalid: 'Invalid field selected',
      valueRequired: 'Value is required',
      valueCharLength: 'Value must be 100 characters or less',
      minConditions: 'At least one condition is required',
      markupRequired: 'Markup is required',
      valueNotNegative: 'Value cannot be negative',
    },
    hotelMarkupFieldOptions: {
      [MarkupFieldOptionHotels.PAX_QTY]: 'Passenger quantity',
      [MarkupFieldOptionHotels.ROOM_QTY]: 'Room quantity',
      [MarkupFieldOptionHotels.HOTEL_VENDOR]: 'Hotel vendor',
      [MarkupFieldOptionHotels.REQUEST_DATE]: 'Request date',
      [MarkupFieldOptionHotels.TRIP_START_DATE]: 'Trip start date',
      [MarkupFieldOptionHotels.PROPERTY_NAME]: 'Property name',
      [MarkupFieldOptionHotels.DESTINATION]: 'Destination',
      [MarkupFieldOptionHotels.HAS_INFANT]: 'Has infants',
      [MarkupFieldOptionHotels.HAS_CHILD]: 'Has child',
      [MarkupFieldOptionHotels.RATE_CURRENCY]: 'Currency',
      [MarkupFieldOptionHotels.RATE_REFUNDABLE]: 'Is Refundable',
      [MarkupFieldOptionHotels.IS_CORPO]: 'Is Corpo',
      [MarkupFieldOptionHotels.CONNECTIVITY_VENDOR]: 'Connectivity Vendor',
    },
    ruteroMarkupFieldOptions: {
      PROVIDER: 'Provider',
      VERTICAL: 'Product type',
      DESTINATION: 'Destination',
      CURRENCY: 'Currency',
      PAX_QTY: 'Passenger quantity',
    },
    serviceType: 'Service type',
    serviceTypes: {
      hotel: 'Hotels',
      rail: 'Rail',
      buses: 'Buses',
      ferry: 'Ferries',
      tours: 'Tours',
      circuits: 'Circuits',
      transfers: 'Transfers',
      cars: 'Cars',
      esims: 'eSIMs',
    },
    operators: {
      [MarkupRuleOperator.EQUALS]: '=',
      [MarkupRuleOperator.NOT_EQUALS]: '!=',
      [MarkupRuleOperator.GREATER_THAN]: '>',
      [MarkupRuleOperator.GREATER_THAN_OR_EQUAL]: '>=',
      [MarkupRuleOperator.LESS_THAN]: '<',
      [MarkupRuleOperator.LESS_THAN_OR_EQUAL]: '=<',
      [MarkupRuleOperator.CONTAINS]: 'contains',
      [MarkupRuleOperator.NOT_CONTAINS]: 'does not contain',
      [MarkupRuleOperator.STARTS_WITH]: 'start with',
      [MarkupRuleOperator.ENDS_WITH]: 'ends width',
    },
  },
  cart: {
    title: 'Cart',
    legacy: {
      directAccess:
        'The cart is kept only for the legacy hotel flow. Start from hotel search to begin a purchase.',
      goToHotels: 'Go to hotels',
    },
    summary: 'Summary',
    total: 'Total',
    checkout: 'Convert to Sale Order',
    keepBuying: 'Keep Shopping',
    cartAddItemError: 'There was an error while trying to add you item to the cart',
    hotels: 'Hotels',
    cars: 'Cars',
    flights: 'Flights',
    services: 'Services',
    ruteroAuthority: {
      rowVerifying: 'Checking booking state…',
      rowUnavailable: 'Could not verify the booking state',
      checkoutVerifying:
        'Some travel services are still being verified.',
      checkoutUnavailable:
        'Could not verify the state of one travel service.',
    },
    convert: {
      success: 'Sale order created',
      error: 'There was an error doing converting the cart to a sale order. Please try again later',
      successMessage: 'The tracking number for your order is:',
    },
  },
  saleOrder: {
    status: {
      [SALE_ORDER_STATUS.Sold]: 'Sold',
      [SALE_ORDER_STATUS.Active]: 'Active',
      [SALE_ORDER_STATUS.Canceled]: 'Cancelled',
      [SALE_ORDER_STATUS.Lead]: 'Lead',
    },
    list: {
      title: 'Sale Orders',
      subtitle: 'List of sale orders',
      results: 'results',
      table: {
        id: '#',
        status: 'Status',
        createdBy: 'Author',
        convertedAt: 'Converted at',
        updatedAt: 'Updated at',
        case: 'Case',
        amount: {
          title: 'Amount',
          value: 'USD {{value}}',
        },
      },
    },
    view: {
      title: 'Sale Order',
      subtitle: '# {{record}}',
      sideBarTitle: 'Sale order side menu',
      passengers: {
        title: 'Passengers',
        subtitle: 'List of passengers',
        newPassenger: 'New Passenger',
        passengerData: 'Passenger Data',
        successUpdatingPax: 'Passenger information updated successfully',
        errorUpdatingPax: 'Error updating passenger information',
        table: {
          name: 'Name',
          lastname: 'Surname',
          email: 'Email',
          age: 'Age (actual)',
          dob: 'Date of Birth',
          actions: 'Actions',
        },
        form: {
          name: 'Name',
          lastName: 'Last Name',
          birthDate: 'Birth Date',
          email: 'Email',
          phone: 'Phone',
        },
      },
      hotels: {
        title: 'Hotel: {{name}}',
        detail: {
          title: 'Details',
          nights: 'Nights',
          markup: 'Markup %',
          subPrice: 'Sub price',
          totalPrice: 'Total price',
          checkIn: 'Check In',
          checkOut: 'Check Out',
          bookingId: 'Booking ID',
          supplierReference: 'Reference - {{supplierName}}',
          board: 'Board',
          cancelPolicies: 'Cancel Policies',
          remarks: 'Remarks',
          rateRules: 'Rate rules',
        },
        cancel: 'Cancel',
        cancelled: 'Cancelled',
        cancelSuccess: 'Booking canceled successfuly',
        emit: 'Emit',
        emitSuccess: 'Booking emitted successfuly',
        emitError: 'There was an error while emitting the booking',
        holder: {
          title: 'Holder data',
          name: 'Name',
          lastname: 'Surname',
          email: 'Email',
        },
      },
      rooms: {
        title: 'Rooms ({{rooms}})',
        paxName: 'Name',
        paxAge: 'Age (at time of check-out)',
      },
    },
    link: {
      action: {
        add: 'Link sale order',
      },
      modal: {
        action: 'Link',
        title: 'Link to Case',
        searchPlaceholder: 'Search by order number...',
        loadError: 'Error loading sale orders',
      },
      list: {
        title: 'Sale Orders',
        linkButton: 'Link Order',
        createdBy: 'Created by',
        amount: 'Amount',
        name: 'Name',
        unlinkConfirm: {
          title: 'Confirm Unlink',
          message: 'Are you sure you want to unlink this order?',
          cancel: 'Cancel',
          confirm: 'Unlink',
        },
      },
      notifications: {
        linkSuccess: 'Sale order linked successfully',
        linkError: 'Error linking sale order',
        unlinkSuccess: 'Sale order unlinked successfully',
        unlinkError: 'Error unlinking sale order',
        loadError: 'Error loading sale orders',
      },
    },
    unlink: {
      modal: {
        title: 'Confirm Unlink',
        message: 'Are you sure you want to unlink this order?',
        cancel: 'Cancel',
        confirm: 'Unlink',
      },
      notifications: {
        unlinkSuccess: 'Sale order unlinked successfully',
        unlinkError: 'Error unlinking sale order',
      },
    },
    bookingDetails: {
      title: 'Provider details',
      hotelName: 'Provider hotel name',
      provider: 'Provider name',
      reference: 'Provider booking reference',
    },
  },
  invalidationRules: {
    name: 'Name',
    expireAfter: 'Expire after',
    isActive: 'Active',
    title: 'Invalidation Rules',
    subtitle: 'List of Invalidation Rules',
    results: 'Results',
    search: 'Search',
    minutes: 'Minutes',
    serviceType: {
      hotels: 'Hotels',
    },
    table: {
      order: 'Order',
      isDefault: 'Default',
      serviceType: 'Service type',
      yes: 'Yes',
      no: 'No',
      lastUpdate: 'Last update',
      created: 'Created',
      updatedBy: 'Updated by',
      createdBy: 'Created by',
    },
    validations: {
      nameCharLength: 'The rule name must be 255 characters or less',
      fieldRequired: 'This field is required',
      expireAfterMin: 'Expire after must be greater than or equal to 0',
      expireAfterMustBeNumber: 'Expire after must be a number',
      operatorInvalid: 'Invalid operator selected',
      fieldInvalid: 'Invalid field selected',
      valueRequired: 'Value is required',
      valueCharLength: 'Value must be 255 characters or less',
      minConditions: 'At least one condition is required',
      valueInvalidDate: 'Invalid date, value must be in format YYYY-MM-DD',
    },
    viewEdit: {
      titleNew: 'Create invalidation rule',
      titleEdit: 'Edit invalidation rule',
      subtitleNew: 'Create invalidation rule',
      subtitleEdit: 'Edit invalidation rule',
      deleteGroup: 'Delete group',
      addCondition: '+ Add condition',
      addAction: '+ Add action',
      createSuccess: 'Invalidation rule created',
      createError: 'An Error occurred while creating the invalidation rule',
      updateSuccess: 'Invalidation rule updated',
      updateError: 'An Error occurred while updating the invalidation rule',
      buttonDiscard: 'Discard',
      conditions: 'Conditions',
      field: 'Field',
      operator: 'Operator',
      value: 'Value',
    },
    fieldOptions: {
      [InvalidationRuleFieldOptionHotels.SUPPLIER_ACCESS_CODE]: 'Supplier access code',
      [InvalidationRuleFieldOptionHotels.CHECK_IN_DATE]: 'Checkin date',
      [InvalidationRuleFieldOptionHotels.CHECK_OUT_DATE]: 'Checkout date',
    },
    operatorOptions: {
      [InvalidationRuleOperator.EQUALS]: '=',
      [InvalidationRuleOperator.NOT_EQUALS]: '!=',
      [InvalidationRuleOperator.GREATER_THAN]: '>',
      [InvalidationRuleOperator.GREATER_THAN_OR_EQUAL]: '>=',
      [InvalidationRuleOperator.LESS_THAN]: '<',
      [InvalidationRuleOperator.LESS_THAN_OR_EQUAL]: '=<',
    },
  },
  dataUploads: {
    title: 'Data Uploads',
    subtitle: 'Bulk data uploads',
    tabs: {
      clients: 'Clients',
      cases: 'Cases',
    },
    clients: {
      title: 'Client Uploads',
      subtitle: 'Bulk client uploads',
      results: 'results',
      search: 'Search',
      table: {
        id: 'ID',
        agencyId: 'Agency ID',
        status: 'Status',
        createdAt: 'Created At',
        createdBy: 'Created By',
        filename: 'Filename',
        actions: 'Download',
      },
      status: {
        created: 'Created',
        pending: 'Pending',
        processing: 'Processing',
        completed: 'Completed',
        failed: 'Failed',
      },
      create: {
        title: 'Upload client file',
        description: 'Select a CSV file with client data',
        createdSuccess: 'File uploaded successfully',
        createdError: 'There was an error uploading the file',
      },
    },
    cases: {
      title: 'Case Uploads',
      subtitle: 'Bulk case uploads',
      results: 'results',
      search: 'Search',
      table: {
        id: 'ID',
        agencyId: 'Agency ID',
        status: 'Status',
        createdAt: 'Created At',
        createdBy: 'Created By',
        filename: 'Filename',
        workflow: 'Workflow',
        actions: 'Download',
      },
      status: {
        created: 'Created',
        pending: 'Pending',
        processing: 'Processing',
        completed: 'Completed',
        failed: 'Failed',
      },
      create: {
        title: 'Upload case file',
        description: 'Select a CSV file with case data',
        createdSuccess: 'File uploaded successfully',
        createdError: 'There was an error uploading the file',
      },
    },
  },
  flightIntegrations: {
    title: 'Flight Integrations',
    subtitle: 'List of flight integrations',
    results: 'Results',
    search: 'Search',
    table: {
      provider: 'Provider',
      ownerClientName: 'Owner client',
      isShared: 'Is shared',
      createdAt: 'Created At',
    },
    create: {
      title: 'Create Flight Integration',
      createdSuccess: 'Flight Integration created successfully',
      createdError: 'Flight Integration created failed',
    },
    edit: {
      loadItemError: 'Error load flight integration',
      title: 'Edit Flight Integration',
      updatedSuccess: 'Flight Updated successfully',
      updatedError: 'Flight Updated failed',
    },
    form: {
      provider: {
        label: 'Provider',
      },
      allowSeeByOthers: {
        label: 'Allow see by other clients',
      },
      username: {
        label: 'Username',
      },
      password: {
        label: 'Password',
      },
    },
  },
  hotelIntegrations: {
    title: 'Hotel Integrations',
    subtitle: 'List of hotel integrations',
    results: 'Results',
    search: 'Search',
    table: {
      name: 'Name',
      provider: 'Provider',
      ownerClientName: 'Owner client',
      isShared: 'Is shared',
      createdAt: 'Created At',
    },
    create: {
      title: 'Create Hotel Integration',
      success: 'Hotel Integration created successfully',
      error: 'Hotel Integration creation failed',
    },
    edit: {
      loadItemError: 'Error loading hotel integration',
      title: 'Edit Hotel Integration',
      success: 'Hotel Integration updated successfully',
      error: 'Hotel Integration update failed',
    },
    update: {
      success: 'Hotel Integration updated successfully',
      error: 'Hotel Integration update failed',
    },
    form: {
      provider: {
        label: 'Provider',
      },
      allowSeeByOthers: {
        label: 'Allow see by other clients',
      },
      apiKey: {
        label: 'API Key',
      },
      clientCode: {
        label: 'Client Code',
      },
      clientName: {
        label: 'Client Name',
      },
      name: {
        label: 'Integration Name',
      },
      suppliers: {
        label: 'Suppliers',
        helperText: 'Select suppliers for this integration',
      },
    },
  },
  agencyFlightIntegrations: {
    title: 'Agency Flight Integrations',
    subtitle: 'List of agency flight integrations',
    results: 'Results',
    search: 'Search',
    table: {
      provider: 'Provider',
      ownerClientName: 'Owner client',
      isShared: 'Is shared',
      createdAt: 'Created At',
      isEnabledForAgency: 'Integration Enabled',
      itemEnabled: 'The integration is enabled',
      itemDisabled: 'This integration is disabled',
    },
    enabled: {
      success: 'The flight integration has been successfully enabled.',
      error: 'An error occurred while enabling the flight integration. Please try again.',
    },
    disabled: {
      success: 'The flight integration has been successfully disabled.',
      error: 'An error occurred while disabling the flight integration. Please try again.',
    },
    confirmDisableModal: {
      title: 'Disable Flight Integration',
      message:
        'If you disable this flight integration, the agency will no longer be able to search for flights using it. Do you still want to proceed?',
      accept: 'Accept',
      cancel: 'Cancel',
    },
  },
  hotelContent: {
    management: {
      title: 'Hotel Content Management',
      general: 'General',
      activeJobs: 'Active Jobs',
      viewJobs: 'View jobs',
      tabs: {
        suppliers: 'Suppliers',
        defaultSuppliers: 'Default Suppliers',
        boards: 'Boards',
        categories: 'Categories',
        destinations: 'Destinations',
        vendorHotels: 'Vendor Hotels',
        nimbuxHotels: 'Nimbux Hotels',
      },
      defaultSuppliers: {
        title: 'Default Suppliers',
        subtitle: 'Configure the priority order of suppliers for each type of content',
      },
      suppliers: {
        title: 'Hotel Content Suppliers',
        subtitle: 'Manage and view hotel content suppliers',
        updateMetadata: 'Update Metadata',
        updateMetadataMessages: {
          success: 'Metadata updated successfully',
          error: 'Error updating metadata',
        },
        refreshMessages: {
          success: 'A job has been queued to update the suppliers',
          error: 'Error updating suppliers',
        },
        table: {
          supplier: 'Supplier',
          provider: 'Provider',
          supplierCode: 'Supplier Code',
          providerCode: 'Provider Code',
          supplierName: 'Supplier Name',
          supplierContext: 'Supplier Context',
          providerName: 'Provider Name',
          isProviderActive: 'Provider Active',
          isSupplierActive: 'Supplier Active',
        },
        errors: {
          getSuppliers: 'Error loading suppliers',
        },
      },
      vendorHotels: {
        title: 'Vendor Hotels',
        subtitle: 'Manage and view vendor hotel listings',
        moderate: {
          title: 'Moderate',
          success: 'Hotels moderated successfully',
          error: 'Error moderating hotels',
        },
        createExtraction: 'Create Extraction',
        table: {
          name: 'Hotel Name',
          code: 'Hotel Code',
          location: 'Location',
          address: 'Address',
          supplier: 'Supplier',
          parent: 'Parent Hotel',
        },
        clearFilters: 'Clear Filters',
        linkModal: {
          currentNimbuxHotel: 'Current Nimbux Hotel',
          linkToNimbuxHotel: 'Link to Nimbux Hotel',
          searchPlaceholder: 'Search Nimbux Hotels...',
          availableNimbuxHotels: 'Available Nimbux Hotels',
          noNimbuxHotelsFound: 'No Nimbux Hotels found',
          noLinkedNimbuxHotel: 'This hotel is not linked to any Nimbux Hotel',
          link: 'Link',
          unlink: 'Unlink',
          nimbuxHotelLabel: 'Nimbux Hotel:',
          noNimbuxHotel: 'None',
        },
        errors: {
          getVendorHotels: 'Error loading vendor hotels',
        },
        modal: {
          title: 'Create New Extraction',
          supplier: 'Supplier',
          destination: 'Destination',
          startExtraction: 'Start Extraction',
        },
      },
      boards: {
        title: 'Boards',
        subtitle: 'Manage and view boards',
        table: {
          code: 'Code',
          supplierName: 'Supplier Name',
          texts: 'Texts',
        },
        errors: {
          getBoards: 'Error loading boards',
        },
      },
      categories: {
        title: 'Categories',
        subtitle: 'Manage and view categories',
        table: {
          code: 'Code',
          supplierName: 'Supplier Name',
          texts: 'Texts',
        },
        errors: {
          getCategories: 'Error loading categories',
        },
      },
      amenties: {
        title: 'Amenities',
        subtitle: 'Manage and view amenities',
        table: {
          code: 'Code',
          supplierName: 'Supplier Name',
          texts: 'Texts',
        },
        errors: {
          getAmenities: 'Error loading amenities',
        },
      },
      destinations: {
        title: 'Destinations',
        subtitle: 'Manage and view destinations',
        table: {
          code: 'Code',
          supplierName: 'Supplier Name',
          texts: 'Texts',
          type: 'Type',
        },
        errors: {
          getDestinations: 'Error loading destinations',
        },
      },
    },
    extraction: {
      supplier: 'Proveedor',
      destinations: 'Destinos',
      success: 'Extraction started successfully',
      error: 'Error starting the extraction',
      newExtraction: 'Create New Extraction',
      startExtraction: 'Start Extraction',
    },
    jobs: {
      errors: {
        getJobs: 'Error loading content extractor jobs',
      },
      modal: {
        title: 'Extraction Queues',
        resultsFound: 'jobs found',
        noJobsFound: 'No jobs found',
        parameters: 'Parameters',
        errors: 'Errors',
        created: 'Created',
        started: 'Started',
        finished: 'Finished',
        status: {
          pending: 'Pending',
          processing: 'Processing',
          completed: 'Completed',
          failed: 'Failed',
        },
      },
    },
    suppliers: 'Suppliers',
    boards: 'Boards',
    categories: 'Categories',
    destinations: 'Destinations',
    vendorHotels: 'Vendor Hotels',
    nimbuxHotels: {
      title: 'Nimbux Hotels',
      empty: 'Hotel list will be displayed here',
      emptyDescription: 'This section will contain the management interface for Nimbux hotels.',
      card: {
        information: 'Area information',
        lastModeration: 'Last moderation was: {{date}}',
        editButton: 'Edit',
        link: 'Link',
        opinions: '({{count}} opinions)',
        popularAmenities: 'Popular amenities',
        showAllAmenities: 'Show all amenities',
        showLessAmenities: 'Show less amenities',
        status: {
          markedForModeration: 'Marked for moderation',
          moderated: 'Moderated',
          toReview: 'To review',
          reviewed: 'Reviewed',
        },
      },
      linkModal: {
        title: 'Link Hotels - {{hotelName}}',
        searchPlaceholder: 'Search hotels...',
        linkedHotels: 'Linked Hotels',
        availableHotels: 'Available Hotels',
        noLinkedHotels: 'No hotels linked yet',
        noAvailableHotels: 'No hotels found',
        link: 'Link',
        unlink: 'Unlink',
        country: 'Country',
        city: 'City',
        parentHotel: 'Nimbux Hotel',
        noParentHotel: 'No nimbux hotel',
        noSupplier: 'No supplier',
        close: 'Close',
      },
      errors: {
        getNimbuxHotels: 'Error loading nimbux hotels',
        loadingError: 'Error loading hotels. Please try again.',
      },
      loadingMore: 'Loading more hotels...',
      noMoreHotels: 'No more hotels to show',
    },
    filters: {
      orderBy: 'Order by',
      name: 'Name',
      country: 'Country',
      suppliers: 'Suppliers',
      searchByName: 'Search by Name',
      searchPlaceholder: 'Enter hotel name',
      filterByCountry: 'Filter By Country',
      selectCountry: 'Select country',
      allCountries: 'All countries',
      filterByAddress: 'Filter by Address',
      address: 'Address',
      addressPlaceholder: 'Enter an address',
      distance: 'Distance',
    },
    view: {
      tabs: {
        general: 'General',
        checkinCheckout: 'Check-in / Check-out',
        location: 'Location',
        images: 'Images',
        amenities: 'Amenities',
        content: 'Content',
      },
      images: {
        published: 'Published ({{count}})',
        available: 'Available ({{count}})',
        count: '{{count}} images',
        mainBadge: 'Main',
        moveToAvailable: 'Move to Available',
        moveToPublished: 'Move to Published',
        messages: {
          getVendorImagesError: 'Error loading images',
          noAvailableImages: 'No available images',
          updateSuccess: 'Images updated successfully',
          updateError: 'Error updating images',
        },
      },
      location: {
        title: 'Location',
        address: 'Address',
        latitude: 'Latitude',
        longitude: 'Longitude',
        map: 'Map',
        dragInstructions: 'Drag the pin on the map to manually adjust the location',
        loadingMap: 'Loading map...',
        messages: {
          updateSuccess: 'Location updated successfully',
          updateError: 'Error updating location',
        },
      },
      amenities: {
        title: 'Amenities',
        messages: {
          updateSuccess: 'Amenities updated successfully',
          updateError: 'Error updating amenities',
        },
        selectedAmenities: 'Selected Amenities ({{count}})',
      },
      checkinCheckout: {
        title: 'Check-in / Check-out',
        checkIn: 'Check-in',
        checkOut: 'Check-out',
        from: 'From',
        to: 'To',
        minimumAge: 'Minimum age',
        minimumAgeForCheckin: 'Minimum age for checkin',
        minimumAgeForCheckout: 'Minimum age for checkout',
        selectTime: 'Select time',
        selectAge: 'Select age',
        years: 'years',
        instructionsForCheckin: 'Instructions for checkin',
        instructionsForCheckout: 'Instructions for checkout',
        generateWithAI: 'Complete with AI',
        messages: {
          updateSuccess: 'Check-in / Check-out updated successfully',
          updateError: 'Error updating check-in / check-out',
          generateTranslationsSuccess: 'Translations generated successfully',
          generateTranslationsError: 'Error generating translations',
        },
      },
      areaInformation: 'Area Information',
    },
    contentEditor: {
      title: 'Nimbux Content',
      editorTitle: 'Content Editor',
      placeholder: 'Edit hotel content here',
      suppliersPreview: 'Suppliers Description (Preview)',
      generateWithAI: 'Generate with AI',
      ckeditor: {
        paragraph: 'Paragraph',
        heading1: 'Heading 1',
        heading2: 'Heading 2',
        heading3: 'Heading 3',
        heading4: 'Heading 4',
      },
      expandContent: 'Expand content',
      collapseContent: 'Collapse content',
      noDescriptionsAvailable: 'No descriptions available for this supplier',
      loadingDescriptions: 'Loading supplier descriptions...',
      noDescriptionsFromSuppliers: 'No supplier descriptions available',
    },
  },
  suppliers: {
    title: 'Hotel Content Suppliers',
    subtitle: 'Manage and view hotel content suppliers',
    updateMetadata: 'Update Metadata',
    updateMetadataMessages: {
      success: 'Metadata updated successfully',
      error: 'Error updating metadata',
    },
    refreshMessages: {
      success: 'A job has been queued to update the suppliers',
      error: 'Error updating suppliers',
    },
    table: {
      supplier: 'Supplier',
      provider: 'Provider',
      supplierCode: 'Supplier Code',
      providerCode: 'Provider Code',
      supplierName: 'Supplier Name',
      supplierContext: 'Supplier Context',
      providerName: 'Provider Name',
      isProviderActive: 'Provider Active',
      isSupplierActive: 'Supplier Active',
    },
  },
  caseTriggers: {
    form: {
      sendWhatsapp: 'Send WhatsApp message',
      statusPlaceholderAny: 'Any status (leave empty to apply to all)',
      statusHelperAny: 'If you don’t select any status, this trigger will apply regardless of the case status.',
      nextStatus: {
        keepCurrent: 'Keep current status',
      },
    },
    template: {
      placeholdersHint:
        'You can use dynamic placeholders such as {{client_name}}, {{client_first_name}}, {{client_last_name}}, {{agent_name}}, {{agent_first_name}}, {{agent_last_name}}; they will be replaced automatically for each case.',
      supportedPlaceholders:
        'Supported placeholders: {{client_name}}, {{client_first_name}}, {{client_last_name}}, {{agent_name}}, {{agent_first_name}}, {{agent_last_name}}',
    },
  },
  webhooks: {
    title: 'Webhooks',
    subtitle: 'Push CRM events to external systems',
    results: 'results',
    searchPlaceholder: 'Search (not implemented)',
    allWorkflows: 'All workflows',
    empty: 'No webhooks configured yet.',
    createCta: 'Create webhook',
    confirmDelete: 'Are you sure you want to delete this webhook?',
    deleteSuccess: 'Webhook deleted',
    deleteError: 'Error deleting webhook',
    table: {
      actions: 'Actions',
      url: 'URL',
      events: 'Events',
      workflows: 'Workflows',
      active: 'Active',
    },
    events: {
      'client.created': 'Client created',
      'client.updated': 'Client updated',
      'case.created': 'Case created',
      'case.step_changed': 'Case step changed',
      'case.status_changed': 'Case status changed',
      'case.agent_assigned': 'Agent assigned to case',
      'case.custom_field_updated': 'Custom field updated',
    },
    modal: {
      createTitle: 'Create webhook',
      editTitle: 'Edit webhook',
    },
    form: {
      url: 'Destination URL',
      secret: 'Secret',
      secretHelper: 'Optional. Used to sign the payload (HMAC-SHA256).',
      secretEditHelper: 'Leave blank to keep the current secret.',
      events: 'Events',
      eventsPlaceholder: 'Select events',
      eventsRequired: 'Select at least one event',
      workflows: 'Workflows',
      workflowsPlaceholder: 'All workflows',
      workflowsHelper: 'Leave empty to apply to all workflows of the agency.',
      active: 'Active',
      create: 'Create',
      update: 'Update',
      saveSuccess: 'Webhook saved',
      saveError: 'Error saving webhook',
    },
  },
  metaCapi: {
    title: 'Meta Ads — Attribution & Conversions',
    subtitle:
      'Configure the Meta Conversions API to attribute conversations and bookings to your ads (WhatsApp CTWA and Web)',
    form: {
      enabled: 'Enable sending conversions to Meta',
      credentialsSection: 'Credentials',
      accessToken: 'Access Token',
      accessTokenHelper: 'Meta access token with event management permissions.',
      accessTokenEditHelper:
        'A token is already stored. Leave blank to keep it.',
      datasetId: 'Dataset ID',
      whatsappBusinessAccountId: 'WhatsApp Business Account ID (WABA)',
      graphApiVersion: 'Graph API version',
      graphApiVersionHelper: 'Leave blank to use the default version.',
      defaultCurrency: 'Default currency',
      testEventCode: 'Test Event Code',
      testEventCodeHelper:
        'Optional. To test events in Meta Events Manager.',
      purchaseTriggerSection: 'Purchase event trigger',
      purchaseTriggerType: 'Trigger type',
      purchaseTriggerTypeHelper: 'Define when the purchase event is sent to Meta.',
      purchaseTriggerNone: 'No trigger (Lead only)',
      purchaseTriggerStep: 'When the case reaches a step',
      purchaseTriggerStepPlaceholder: 'Select a step',
      purchaseTriggerCustomField: 'When a custom field changes',
      purchaseTriggerFieldName: 'Field name',
      purchaseTriggerFieldValue: 'Expected value',
      purchaseTriggerFieldValueHelper:
        'Optional. If set, the event only fires when the field takes this value.',
      save: 'Save',
      saveSuccess: 'Configuration saved',
      saveError: 'Error saving configuration',
    },
  },
};
